'use strict';

window.LogicCraft = window.LogicCraft || {};

window.LogicCraft.simulation = (function() {
    const state = window.LogicCraft.state;
    const core = window.LogicCraft.core;
    
    const WIRE_LENGTH = 0.50; 
    const WIRE_WIDTH = 0.12;   
    const WIRE_HEIGHT = 0.05;  
    const WIRE_Y = -0.47;      

    let scene, camera, renderer, board, gridMesh, fluidHosesGroup;
    let moveF = false, moveB = false, moveL = false, moveR = false, moveUp = false, moveDown = false;
    let yaw = 0, pitch = -0.42;
    const clock = new THREE.Clock();
    const raycaster = new THREE.Raycaster();
    const mouseVector = new THREE.Vector2();
    
    let isInitialized = false;

    const _fwdVector = new THREE.Vector3();
    const _rightVector = new THREE.Vector3();
    const _tempEuler = new THREE.Euler(0, 0, 0, 'YXZ');
    const _upAxis = new THREE.Vector3(0, 1, 0);
    const _zeroVector = new THREE.Vector2(0, 0);

    const COLOR_BG = 0x07152F;
    const COLOR_BOARD = 0x0B2048;
    const COLOR_LOW = 0x30415D;
    const COLOR_HIGH = 0x1677F2;

    const COLOR_PNEU_PRESSURIZED = 0x00E5FF;
    const COLOR_PNEU_EXHAUST     = 0x1E293B;
    const COLOR_HYDRA_PRESSURIZED = 0xF59E0B;
    const COLOR_HYDRA_EXHAUST     = 0x1E293B;

    const AIR_SWITCH_OFF = 0xB45309;
    const AIR_SWITCH_ON  = 0xFACC15;
    const AIR_GATE_OFF   = 0x0369A1;
    const AIR_GATE_ON    = 0x38BDF8;

    const wireMaterialLow = new THREE.MeshStandardMaterial({ color: COLOR_LOW, roughness: 0.8, metalness: 0.2 });
    const matJumperBase = new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.9 });
    
    // Hitbox invisível expandida para facilitar clique e conexão nos pinos
    const pinHitboxGeo = new THREE.CylinderGeometry(0.35, 0.35, 0.30, 16);
    const pinHitboxMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false });

    const materialCache = new Map();
    function getWireMaterial(colorHex, isHigh) {
        if (!isHigh) return wireMaterialLow;
        if (materialCache.has(colorHex)) return materialCache.get(colorHex);
        const mat = new THREE.MeshStandardMaterial({ 
            color: colorHex, 
            emissive: colorHex, 
            emissiveIntensity: 0.95, 
            roughness: 0.3, 
            metalness: 0.2 
        });
        materialCache.set(colorHex, mat);
        return mat;
    }

    function isCachedWireMaterial(mat) {
        if (mat === wireMaterialLow || mat === matJumperBase || mat === pinHitboxMat) return true;
        for (const cached of materialCache.values()) {
            if (cached === mat) return true;
        }
        return false;
    }

    function disposeMeshTree(obj) {
        if (!obj) return;
        obj.traverse(child => {
            if (child.geometry && child.geometry !== pinHitboxGeo) child.geometry.dispose();
            if (child.material) {
                const mats = Array.isArray(child.material) ? child.material : [child.material];
                mats.forEach(m => {
                    if (!isCachedWireMaterial(m)) {
                        if (m.map && m.map.dispose) m.map.dispose();
                        m.dispose();
                    }
                });
            }
        });
    }

    state.airWires = state.airWires || [];
    state.hoses = state.hoses || [];
    let airWireStart = null;

    window.isBatchLoading = false;

    function notify(type, msg) {
        if (window.isBatchLoading || (window.LogicCraftGuard && window.LogicCraftGuard.isBatchLoading)) return;
        if (window.LogicCraft.ui && window.LogicCraft.ui.showToast) {
            window.LogicCraft.ui.showToast(type, msg);
        }
    }

    function updateUndoRedoButtons() {
        const btnUndo = document.getElementById('btn-undo');
        if (btnUndo) btnUndo.disabled = (state.history.length === 0);
        const btnRedo = document.getElementById('btn-redo');
        if (btnRedo) btnRedo.disabled = (!state.redoStack || state.redoStack.length === 0);
    }

    
    let audioCtx = null;
    let audioUnlocked = false;

    function getAudioContext() {
        if (!audioUnlocked) return null;
        if (!audioCtx) {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (AudioContextClass) {
                audioCtx = new AudioContextClass();
            }
        }
        if (audioCtx && audioCtx.state === 'suspended') {
            audioCtx.resume().catch(() => {});
        }
        return audioCtx;
    }

    // Desbloqueia a API de áudio no primeiro gesto intencional do usuário (clique, toque ou tecla)
    const unlockAudio = () => {
        audioUnlocked = true;
        getAudioContext();
    };
    window.addEventListener('pointerdown', unlockAudio, { once: true, passive: true });
    window.addEventListener('keydown', unlockAudio, { once: true, passive: true });
    window.addEventListener('touchstart', unlockAudio, { once: true, passive: true });

    const AudioEngine = {
        get ctx() {
            return getAudioContext();
        },
        playExhaust() {
            const ctx = getAudioContext();
            if (!ctx || ctx.state !== 'running') return;
            try {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'square';
                osc.frequency.setValueAtTime(150, ctx.currentTime);
                osc.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + 0.15);
                gain.gain.setValueAtTime(0.1, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start();
                osc.stop(ctx.currentTime + 0.15);
            } catch (_) {}
        },
        stopAll() { }
    };
    window.AudioEngine = AudioEngine;

    function init() {
        if (isInitialized) return;
        isInitialized = true;

        scene = new THREE.Scene();
        window.LogicCraft.scene = scene;
        scene.background = new THREE.Color(COLOR_BG);
        scene.fog = new THREE.Fog(COLOR_BG, 18, 55);

        camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 1000);
        camera.position.set(0, 4.2, 6.2);
        _tempEuler.set(pitch, yaw, 0, 'YXZ');
        camera.quaternion.setFromEuler(_tempEuler);

        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
        renderer.domElement.id = 'game-canvas';
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        document.body.appendChild(renderer.domElement);

        scene.add(new THREE.AmbientLight(0xffffff, 0.65));
        const dirLight = new THREE.DirectionalLight(0xffffff, 0.75);
        dirLight.position.set(12, 28, 14);
        dirLight.castShadow = true;
        dirLight.shadow.mapSize.set(2048, 2048);
        dirLight.shadow.bias = -0.001;
        dirLight.shadow.normalBias = 0.02;
        scene.add(dirLight);

        board = new THREE.Mesh(
            new THREE.PlaneGeometry(60, 60), 
            new THREE.MeshStandardMaterial({ color: COLOR_BOARD, roughness: 0.9, metalness: 0.1 })
        );
        board.rotation.x = -Math.PI / 2;
        board.position.y = -0.5;
        board.receiveShadow = true;
        scene.add(board);

        gridMesh = new THREE.GridHelper(60, 120, 0x30415D, 0x07152F);
        gridMesh.position.y = -0.495;
        gridMesh.material.opacity = 0.35;
        gridMesh.material.transparent = true;
        scene.add(gridMesh);

        fluidHosesGroup = new THREE.Group();
        fluidHosesGroup.name = 'fluidHosesGroup';
        scene.add(fluidHosesGroup);

        setupEvents();
        animate();
    }

    const GATE_74XX_INFO = {
        [core.COMP.AND]: { code: 'SN74LS08N', name: '7408 AND' },
        [core.COMP.OR]: { code: 'SN74LS32N', name: '7432 OR' },
        [core.COMP.NOT]: { code: 'SN74LS04N', name: '7404 NOT' },
        [core.COMP.NAND]: { code: 'SN74LS00N', name: '7400 NAND' },
        [core.COMP.NOR]: { code: 'SN74LS02N', name: '7402 NOR' },
        [core.COMP.XOR]: { code: 'SN74LS86N', name: '7486 XOR' },
        [core.COMP.XNOR]: { code: 'SN74LS266N', name: '74266 XNOR' },
        [core.COMP.BUFFER]: { code: 'SN74LS07N', name: '7407 BUFF' }
    };

    function drawGateSymbol(ctx, type) {
        ctx.fillStyle = '#F8FAFC'; 
        ctx.strokeStyle = '#F8FAFC'; 
        ctx.lineWidth = 7; 
        ctx.beginPath();
        const isTri = (type === core.COMP.NOT || type === core.COMP.BUFFER);
        const isAnd = (type === core.COMP.AND || type === core.COMP.NAND);
        const isOr  = (type === core.COMP.OR || type === core.COMP.NOR || type === core.COMP.XOR || type === core.COMP.XNOR);
        const hasBubble = (type === core.COMP.NOT || type === core.COMP.NAND || type === core.COMP.NOR || type === core.COMP.XNOR);

        if (isTri) { 
            ctx.moveTo(65, 55); ctx.lineTo(65, 200); ctx.lineTo(175, 128); ctx.closePath(); ctx.stroke(); 
        } else if (isAnd) { 
            ctx.moveTo(60, 50); ctx.lineTo(125, 50); ctx.arc(125, 128, 78, -Math.PI / 2, Math.PI / 2); ctx.lineTo(60, 206); ctx.closePath(); ctx.stroke(); 
        } else if (isOr) {
            if (type === core.COMP.XOR || type === core.COMP.XNOR) { 
                ctx.moveTo(40, 50); ctx.quadraticCurveTo(70, 128, 40, 206); ctx.stroke(); ctx.beginPath(); 
            }
            ctx.moveTo(55, 50); ctx.quadraticCurveTo(115, 128, 55, 206); ctx.quadraticCurveTo(145, 206, 200, 128); ctx.quadraticCurveTo(145, 50, 55, 50); ctx.stroke();
        }
        if (hasBubble) { 
            ctx.beginPath(); 
            const bx = isTri ? 194 : (isAnd ? 222 : 216); 
            ctx.arc(bx, 128, 11, 0, Math.PI * 2); 
            ctx.stroke(); 
        }
    }

    function createGateTopTexture(type, inputCount) {
        const isLong = inputCount > 3;
        const width = 512;
        const height = isLong ? 812 : 512;
        const canvas = document.createElement('canvas'); 
        canvas.width = width; 
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        // Carcaça DIP industrial fosco escuro
        ctx.fillStyle = '#111827'; ctx.fillRect(0, 0, width, height);
        ctx.strokeStyle = '#1F2937'; ctx.lineWidth = 14; ctx.strokeRect(7, 7, width - 14, height - 14);
        
        // Chanfro / notch lateral semicircular indicador de orientação (Pino 1 à esquerda)
        ctx.fillStyle = '#030712';
        ctx.beginPath();
        ctx.arc(28, height / 2, 28, -Math.PI / 2, Math.PI / 2);
        ctx.fill();
        ctx.strokeStyle = '#374151'; ctx.lineWidth = 3; ctx.stroke();

        // Ponto chanfrado indicador do Pino 1
        ctx.fillStyle = '#94A3B8';
        ctx.beginPath(); ctx.arc(48, 48, 10, 0, Math.PI * 2); ctx.fill();

        const info = GATE_74XX_INFO[type] || { code: 'SN74LSxx', name: type };
        
        // Serigrafia ANSI em branco fosco (#F8FAFC) de alto contraste
        ctx.fillStyle = '#F8FAFC';
        ctx.font = 'bold 32px "JetBrains Mono", monospace';
        ctx.textAlign = 'center';
        ctx.fillText(info.code, width / 2, 60);

        ctx.save();
        ctx.scale(2, 2);
        if (isLong) ctx.translate(0, (height / 2 - 256) / 2);
        drawGateSymbol(ctx, type);
        ctx.restore();
        
        ctx.font = 'bold 36px "JetBrains Mono", monospace';
        ctx.fillStyle = '#F8FAFC';
        ctx.textAlign = 'center'; 
        ctx.fillText(`${info.name} [${inputCount} IN]`, width / 2, height - 42);

        // Indicador serigráfico de saída S em destaque âmbar/dourado
        ctx.fillStyle = '#F59E0B';
        ctx.font = 'bold 26px "JetBrains Mono", monospace';
        ctx.fillText('▲ S (SAÍDA)', width / 2, 105);

        return new THREE.CanvasTexture(canvas);
    }

    function createGenericTopTexture(title, subtitle) {
        const canvas = document.createElement('canvas'); canvas.width = 512; canvas.height = 512;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#0F172A'; ctx.fillRect(0, 0, 512, 512);
        ctx.strokeStyle = '#334155'; ctx.lineWidth = 10; ctx.strokeRect(8, 8, 496, 496);
        ctx.fillStyle = '#38BDF8';
        ctx.beginPath(); ctx.arc(48, 48, 12, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#CBD5E1'; ctx.font = 'bold 44px monospace'; ctx.textAlign = 'center'; ctx.fillText(title, 256, 210);
        ctx.font = 'bold 28px monospace'; ctx.fillStyle = '#94A3B8'; ctx.fillText(subtitle, 256, 290);
        return new THREE.CanvasTexture(canvas);
    }

    function drawValvePlug(ctx, x, y, isTop) {
        ctx.beginPath();
        const dy = isTop ? 10 : -10;
        ctx.moveTo(x, y);
        ctx.lineTo(x, y + dy);
        ctx.moveTo(x - 6, y + dy);
        ctx.lineTo(x + 6, y + dy);
        ctx.stroke();
    }

    function drawValveSpring(ctx, startX, startY, endX, endY) {
        ctx.save();
        ctx.strokeStyle = '#94A3B8';
        ctx.lineWidth = 2.5;
        const totalW = endX - startX;
        const coils = 4;
        const step = totalW / (coils * 2);
        const amp = 10;
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        for (let i = 0; i < coils * 2; i++) {
            const px = startX + (i + 0.5) * step;
            const py = startY + ((i % 2 === 0) ? -amp : amp);
            ctx.lineTo(px, py);
        }
        ctx.lineTo(endX, endY);
        ctx.stroke();
        ctx.restore();
    }

    function drawValveSolenoidSymbol(ctx, x, y, w, h, label) {
        ctx.save();
        ctx.strokeStyle = '#94A3B8';
        ctx.lineWidth = 2;
        ctx.strokeRect(x, y, w, h);
        // Linha diagonal e triângulo de pilotagem ISO 1219
        ctx.beginPath();
        ctx.moveTo(x, y + h);
        ctx.lineTo(x + w, y);
        ctx.stroke();
        // Triângulo de acionamento
        ctx.fillStyle = '#38BDF8';
        ctx.beginPath();
        ctx.moveTo(x + w, y + h / 2 - 6);
        ctx.lineTo(x + w + 8, y + h / 2);
        ctx.lineTo(x + w, y + h / 2 + 6);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = '#F8FAFC';
        ctx.font = 'bold 10px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(label || 'Y1', x + w / 2, y + h + 12);
        ctx.restore();
    }

    function createValveTopTexture(title, isHydra, isMono) {
        const canvas = document.createElement('canvas'); canvas.width = 512; canvas.height = 512;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#0F172A'; ctx.fillRect(0, 0, 512, 512); ctx.scale(2, 2);
        
        // Borda industrial com parafusos nos quatro cantos
        ctx.strokeStyle = '#334155'; ctx.lineWidth = 4; ctx.strokeRect(4, 4, 248, 248);
        const drawScrew = (sx, sy) => {
            ctx.fillStyle = '#64748B'; ctx.beginPath(); ctx.arc(sx, sy, 4, 0, Math.PI * 2); ctx.fill();
            ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(sx - 3, sy); ctx.lineTo(sx + 3, sy); ctx.stroke();
        };
        drawScrew(12, 12); drawScrew(244, 12); drawScrew(12, 244); drawScrew(244, 244);
        
        const is32 = title.includes('3/2');
        ctx.fillStyle = isHydra ? '#F59E0B' : '#38BDF8';
        ctx.font = 'bold 13px Inter, sans-serif'; ctx.textAlign = 'center'; 
        ctx.fillText(title + ' (ISO 1219)', 128, 44);
        
        // Envelopes das posições (cada envelope com largura 90, altura 100)
        // Envelope Esquerdo (Posição 'a' / Acionada): X de 38 a 128
        // Envelope Direito  (Posição '0' / Repouso):   X de 128 a 218
        const yTop = 68;
        const yBottom = 168;
        const hBox = yBottom - yTop;

        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 2.5;
        ctx.strokeRect(38, yTop, 90, hBox);
        ctx.strokeRect(128, yTop, 90, hBox);

        // Acionamentos externos nas pontas dos envelopes
        drawValveSolenoidSymbol(ctx, 16, 95, 22, 46, isHydra ? 'YH1' : 'Y1');
        if (isMono) {
            drawValveSpring(ctx, 218, 118, 242, 118);
        } else {
            drawValveSolenoidSymbol(ctx, 218, 95, 22, 46, isHydra ? 'YH2' : 'Y2');
        }

        // Rótulos de pórticos padronizados segundo a norma ISO 1219:
        // Tipografia nítida Inter 14px de alto contraste
        ctx.font = 'bold 14px Inter, sans-serif'; ctx.textAlign = 'center';

        const arrowColor = isHydra ? '#F59E0B' : '#38BDF8';
        ctx.strokeStyle = arrowColor; ctx.lineWidth = 2.5;

        if (is32) {
            // 3/2 VIAS:
            // Face voltada ao Atuador: 2(A) em x=128, y=22
            ctx.fillStyle = '#FFFFFF'; ctx.fillText('2(A)', 128, 22);
            // Face voltada à Alimentação & Escape: 1(P) em x=70, 3(R) em x=185, y=238
            ctx.fillStyle = '#FFFFFF'; ctx.fillText('1(P)', 70, 238);
            ctx.fillStyle = '#94A3B8'; ctx.fillText('3(R)', 185, 238);

            // ENVELOPE ESQUERDO (Acionado): 1(P) -> 2(A)
            drawArrow(ctx, 70, yBottom, 128, yTop);

            // ENVELOPE DIREITO (Repouso): 1(P) bloqueado em T, 2(A) -> 3(R) escape
            drawValvePlug(ctx, 155, yBottom, false);
            drawArrow(ctx, 155, yTop, 185, yBottom);

        } else if (isHydra) {
            // 4/2 VIAS HIDRÁULICA:
            // Face voltada ao Atuador (Cilindro): Linhas de Trabalho A em x=75, B em x=180, y=22
            ctx.fillStyle = '#FFFFFF'; ctx.fillText('A', 75, 22);
            ctx.fillStyle = '#FFFFFF'; ctx.fillText('B', 180, 22);
            // Face voltada à Bomba Hidráulica (HPU): Pressão P em x=75, Tanque T em x=180, y=238
            ctx.fillStyle = '#FFFFFF'; ctx.fillText('P', 75, 238);
            ctx.fillStyle = '#94A3B8'; ctx.fillText('T', 180, 238);

            // ENVELOPE ESQUERDO (Acionado): Passagem P -> A (Avanço) e B -> T (Retorno ao Tanque)
            drawArrow(ctx, 75, yBottom, 75, yTop);   // P -> A
            drawArrow(ctx, 105, yTop, 105, yBottom); // B -> T

            // ENVELOPE DIREITO (Repouso): Passagem P -> B (Recuo) e A -> T (Retorno ao Tanque)
            drawArrow(ctx, 145, yBottom, 180, yTop); // P -> B
            drawArrow(ctx, 145, yTop, 180, yBottom); // A -> T

        } else {
            // 5/2 VIAS PNEUMÁTICA:
            // Face voltada ao Atuador (Cilindro): Linhas de Trabalho 4(A) em x=75, 2(B) em x=180, y=22
            ctx.fillStyle = '#FFFFFF'; ctx.fillText('4(A)', 75, 22);
            ctx.fillStyle = '#FFFFFF'; ctx.fillText('2(B)', 180, 22);
            // Face voltada ao Suprimento (FRL Compressor) & Escapes: 5(S) em x=55, 1(P) em x=128, 3(R) em x=200, y=238
            ctx.fillStyle = '#94A3B8'; ctx.fillText('5(S)', 55, 238);
            ctx.fillStyle = '#FFFFFF'; ctx.fillText('1(P)', 128, 238);
            ctx.fillStyle = '#94A3B8'; ctx.fillText('3(R)', 200, 238);

            // ENVELOPE ESQUERDO (Acionado): 1(P) -> 4(A), 2(B) -> 3(R), 5(S) bloqueado
            drawArrow(ctx, 110, yBottom, 75, yTop);   // 1(P) -> 4(A)
            drawArrow(ctx, 110, yTop, 110, yBottom);  // 2(B) -> 3(R)
            drawValvePlug(ctx, 55, yBottom, false);   // 5(S) bloqueado

            // ENVELOPE DIREITO (Repouso): 1(P) -> 2(B), 4(A) -> 5(S), 3(R) bloqueado
            drawArrow(ctx, 146, yBottom, 180, yTop);  // 1(P) -> 2(B)
            drawArrow(ctx, 146, yTop, 146, yBottom);  // 4(A) -> 5(S)
            drawValvePlug(ctx, 200, yBottom, false);  // 3(R) bloqueado
        }

        // Rodapé da serigrafia
        ctx.fillStyle = '#64748B'; ctx.font = '10px Inter, sans-serif'; ctx.textAlign = 'center';
        ctx.fillText(isHydra ? 'PN 120 BAR • ISO VG 46' : 'PN 6 BAR • FESTO STD', 128, 200);

        return new THREE.CanvasTexture(canvas);
    }

    function drawArrow(ctx, fromX, fromY, toX, toY) {
        const headlen = 8; 
        const dx = toX - fromX;
        const dy = toY - fromY;
        const angle = Math.atan2(dy, dx);
        ctx.beginPath();
        ctx.moveTo(fromX, fromY);
        ctx.lineTo(toX, toY);
        ctx.lineTo(toX - headlen * Math.cos(angle - Math.PI / 6), toY - headlen * Math.sin(angle - Math.PI / 6));
        ctx.moveTo(toX, toY);
        ctx.lineTo(toX - headlen * Math.cos(angle + Math.PI / 6), toY - headlen * Math.sin(angle + Math.PI / 6));
        ctx.stroke();
    }

    function createSwitchTexture(nodeState, conf) {
        const canvas = document.createElement('canvas'); canvas.width = 128; canvas.height = 128;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#0F172A'; ctx.fillRect(0, 0, 128, 128);
        ctx.strokeStyle = nodeState ? conf.hex : '#334155'; ctx.lineWidth = 4; ctx.strokeRect(2, 2, 124, 124);
        ctx.fillStyle = nodeState ? conf.hex : '#64748B'; ctx.font = 'bold 50px monospace'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(nodeState ? '1' : '0', 64, 58);
        ctx.font = 'bold 14px monospace'; ctx.fillStyle = conf.hex; ctx.fillText(`SW ${conf.id}`, 64, 108);
        return new THREE.CanvasTexture(canvas);
    }

    function createPinLabel(text, isOutput) {
        const canvas = document.createElement('canvas'); 
        canvas.width = 128; 
        canvas.height = 64;
        const ctx = canvas.getContext('2d');
        
        const str = String(text || '');
        let fontSize = 34;
        if (str.length >= 6) fontSize = 20;
        else if (str.length >= 4) fontSize = 24;
        else if (str.length >= 3) fontSize = 28;

        ctx.fillStyle = '#0B132B';
        ctx.fillRect(0, 0, 128, 64);
        ctx.strokeStyle = isOutput ? '#0284C7' : '#334155';
        ctx.lineWidth = 3;
        ctx.strokeRect(2, 2, 124, 60);

        ctx.font = `bold ${fontSize}px monospace`;
        ctx.textAlign = 'center'; 
        ctx.textBaseline = 'middle';

        if (str === 'PE' || str.includes('PE') || str.includes('TERRA')) {
            ctx.fillStyle = '#22C55E';
        } else if (str === 'N' || str.includes('NEUTRO')) {
            ctx.fillStyle = '#38BDF8';
        } else if (str.includes('L1') || str.includes('L2') || str.includes('L3') || str.includes('T1') || str.includes('T2') || str.includes('T3')) {
            ctx.fillStyle = '#FACC15';
        } else {
            ctx.fillStyle = isOutput ? '#38BDF8' : '#F8FAFC';
        }
        ctx.fillText(str, 64, 33);

        const tex = new THREE.CanvasTexture(canvas);
        tex.minFilter = THREE.LinearFilter;
        const plane = new THREE.Mesh(
            new THREE.PlaneGeometry(0.24, 0.14), 
            new THREE.MeshBasicMaterial({ map: tex, transparent: true })
        );
        plane.rotation.x = -Math.PI / 2; 
        plane.position.y = -0.38;
        plane.userData = { isLabel: true };
        plane.castShadow = false;
        plane.receiveShadow = false;
        return plane;
    }

    function createBoxHighlightMesh(width, depth, zCenter) {
        const geo = new THREE.BufferGeometry();
        const points = [
            new THREE.Vector3(-width/2, -0.485, zCenter - depth/2), new THREE.Vector3( width/2, -0.485, zCenter - depth/2),
            new THREE.Vector3( width/2, -0.485, zCenter + depth/2), new THREE.Vector3(-width/2, -0.485, zCenter + depth/2),
            new THREE.Vector3(-width/2, -0.485, zCenter - depth/2)
        ];
        geo.setFromPoints(points);
        const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: 0x0EA5E9, transparent: true, opacity: 0.85 }));
        line.name = 'focusHighlight'; line.visible = false;
        return line;
    }

    function attachFluidPortsToMesh(root, type, isHydra) {
        const is32 = (type === core.COMP.VALVE_3_2_MONO);
        const is52 = (type === core.COMP.VALVE_5_2_MONO || type === core.COMP.VALVE_5_2_BI);
        const is42 = (type === core.COMP.VALVE_4_2_MONO || type === core.COMP.VALVE_4_2_BI);
        
        // Material do niple: latão usinado para pneumática, aço cromado para hidráulica
        const matNipple = new THREE.MeshStandardMaterial({
            color: isHydra ? 0xCBD5E1 : 0xD97706,
            roughness: isHydra ? 0.2 : 0.35,
            metalness: isHydra ? 0.9 : 0.8
        });
        const geoNipple = new THREE.CylinderGeometry(0.06, 0.06, 0.12, 16);
        
        // Silenciador de bronze sinterizado (#B45309) para escapes pneumáticos (3/R e 5/S)
        const matSilencerBronze = new THREE.MeshStandardMaterial({
            color: 0xB45309,
            roughness: 0.9,
            metalness: 0.35
        });
        const matBrassCollar = new THREE.MeshStandardMaterial({
            color: 0xD97706,
            roughness: 0.4,
            metalness: 0.8
        });

        const createNipple = (x, y, z, rotX) => {
            const nipple = new THREE.Mesh(geoNipple, matNipple);
            nipple.rotation.x = rotX;
            nipple.position.set(x, y, z);
            const hexGeo = new THREE.CylinderGeometry(0.08, 0.08, 0.04, 6);
            const collar = new THREE.Mesh(hexGeo, matNipple);
            collar.rotation.x = rotX;
            const isFront = (rotX > 0); 
            collar.position.set(x, y, isFront ? z - 0.04 : z + 0.04);
            nipple.castShadow = false;
            collar.castShadow = false;
            root.add(nipple);
            root.add(collar);
        };

        const createSilencer = (x, y, z, rotX) => {
            // Corpo cilíndrico poroso sinterizado
            const porousBody = new THREE.Mesh(new THREE.CylinderGeometry(0.065, 0.055, 0.14, 16), matSilencerBronze);
            porousBody.rotation.x = rotX;
            porousBody.position.set(x, y, z);
            // Colar sextavado de latão para aperto com chave
            const hexBase = new THREE.Mesh(new THREE.CylinderGeometry(0.085, 0.085, 0.04, 6), matBrassCollar);
            hexBase.rotation.x = rotX;
            const isFront = (rotX > 0);
            hexBase.position.set(x, y, isFront ? z - 0.04 : z + 0.04);
            porousBody.castShadow = false;
            hexBase.castShadow = false;
            root.add(porousBody);
            root.add(hexBase);
        };

        if (is32) {
            // SUL (+Z): Linha de trabalho 2(A) no centro
            createNipple(0.0, -0.31, 0.48, Math.PI / 2);
            // NORTE (-Z): Alimentação 1(P) à esquerda, Escape 3(R) à direita com silenciador de bronze
            createNipple(-0.20, -0.31, -0.48, -Math.PI / 2);   // 1(P)
            createSilencer(0.20, -0.31, -0.48, -Math.PI / 2); // 3(R) Silenciador
        } else if (is52) {
            // SUL (+Z): Linhas de trabalho 4(A) à esquerda e 2(B) à direita
            createNipple(-0.25, -0.31, 0.48, Math.PI / 2); // 4(A)
            createNipple(0.25, -0.31, 0.48, Math.PI / 2);  // 2(B)
            // NORTE (-Z): Escapes 5(S) à esquerda e 3(R) à direita (silenciadores), Alimentação 1(P) no centro (niple)
            createSilencer(-0.25, -0.31, -0.48, -Math.PI / 2); // 5(S) Silenciador
            createNipple(0.00, -0.31, -0.48, -Math.PI / 2);    // 1(P)
            createSilencer(0.25, -0.31, -0.48, -Math.PI / 2);  // 3(R) Silenciador
        } else if (is42) {
            // SUL (+Z): Linhas de trabalho A à esquerda e B à direita (aço cromado)
            createNipple(-0.25, -0.31, 0.48, Math.PI / 2); // A
            createNipple(0.25, -0.31, 0.48, Math.PI / 2);  // B
            // NORTE (-Z): Pressão P à esquerda e Tanque T à direita (aço cromado)
            createNipple(-0.25, -0.31, -0.48, -Math.PI / 2); // P
            createNipple(0.25, -0.31, -0.48, -Math.PI / 2);  // T
        }
    }

    function createBananaSocketMesh(x, y, z, labelText, isOutput, pinId, root, matMetal) {
        const socketColor = isOutput ? 0xD97706 : (pinId === 'A' ? 0x0284C7 : (pinId === 'B' ? 0xC026D3 : (pinId === 'C' ? 0xF59E0B : 0x16A34A)));
        const socketMat = new THREE.MeshStandardMaterial({ color: socketColor, roughness: 0.45, metalness: 0.25 });
        
        // Corpo cilíndrico do borne banana fêmea
        const socketBody = new THREE.Mesh(new THREE.CylinderGeometry(0.088, 0.092, 0.045, 20), socketMat);
        socketBody.position.set(x, y + 0.02, z);
        socketBody.userData = { isPin: true };
        socketBody.castShadow = false;
        socketBody.receiveShadow = false;
        root.add(socketBody);

        // Colar usinado de latão/níquel interno
        const collar = new THREE.Mesh(new THREE.CylinderGeometry(0.066, 0.066, 0.048, 16), matMetal);
        collar.position.set(x, y + 0.022, z);
        collar.userData = { isPin: true };
        collar.castShadow = false;
        collar.receiveShadow = false;
        root.add(collar);

        // Orifício / furo central escuro vazado para inserção do plugue banana
        const holeMat = new THREE.MeshBasicMaterial({ color: 0x020617 });
        const hole = new THREE.Mesh(new THREE.CylinderGeometry(0.038, 0.038, 0.052, 16), holeMat);
        hole.position.set(x, y + 0.024, z);
        hole.userData = { isPin: true };
        hole.castShadow = false;
        hole.receiveShadow = false;
        root.add(hole);

        // Anel indicador de estado lógico (iluminado quando ativo)
        const pLight = new THREE.Mesh(
            new THREE.CylinderGeometry(0.080, 0.080, 0.012, 16), 
            new THREE.MeshStandardMaterial({ color: isOutput ? COLOR_HIGH : COLOR_LOW, roughness: 0.4 })
        );
        pLight.position.set(x, y + 0.008, z);
        pLight.userData = { isPin: true, pinType: isOutput ? 'output' : 'input', pinId: pinId, localX: x, localZ: z };
        pLight.castShadow = false;
        pLight.receiveShadow = false;
        root.add(pLight);
        if (!root.userData) root.userData = {};
        if (!root.userData.outputMeshes) root.userData.outputMeshes = {};
        if (!root.userData.pinElements) root.userData.pinElements = {};
        if (isOutput && pinId) {
            root.userData.outputMeshes[pinId] = pLight;
        }

        // Rótulo de texto nítido na face superior
        const lbl = createPinLabel(labelText, isOutput);
        const textOffsetX = (x < 0) ? 0.18 : -0.18;
        lbl.position.set(x + textOffsetX, y + 0.028, z);
        lbl.userData = { isPin: true, isLabel: true, pinType: isOutput ? 'output' : 'input', pinId: pinId, localX: x, localZ: z };
        lbl.castShadow = false;
        lbl.receiveShadow = false;
        root.add(lbl);

        // Hitbox ampliada para o raycast
        const pHitbox = new THREE.Mesh(pinHitboxGeo, pinHitboxMat);
        pHitbox.position.set(x, y + 0.02, z);
        pHitbox.userData = { isPin: true, pinType: isOutput ? 'output' : 'input', pinId: pinId, localX: x, localZ: z };
        root.add(pHitbox);

        if (pinId) {
            root.userData.pinElements[pinId] = { base: socketBody, light: pLight, label: lbl, hitbox: pHitbox };
        }
    }

    function createScrewTerminalMesh(x, y, z, labelText, isOutput, pinId, root, matMetal, matDarkMetal) {
        const terminalGroup = new THREE.Group();
        try {
            if (!root) return terminalGroup;
            if (!root.userData) root.userData = {};
            if (!root.userData.terminals) root.userData.terminals = {};
            if (!root.userData.screwTerminals) root.userData.screwTerminals = {};
            if (!root.userData.pinElements) root.userData.pinElements = {};
            if (!root.userData.outputMeshes) root.userData.outputMeshes = {};

            const terminalId = pinId || labelText || `term_${Math.random().toString(36).substr(2, 5)}`;
            const mDark = matDarkMetal || new THREE.MeshStandardMaterial({ color: 0x475569, metalness: 0.7, roughness: 0.35 });
            const mLight = matMetal || new THREE.MeshStandardMaterial({ color: 0x94A3B8, metalness: 0.8, roughness: 0.3 });

            // Bloco de borne em degrau / cage clamp
            const block = new THREE.Mesh(new THREE.BoxGeometry(0.20, 0.12, 0.20), mDark);
            block.position.set(x, y - 0.04, z);
            block.userData = { isPin: true };
            block.castShadow = false;
            block.receiveShadow = false;
            terminalGroup.add(block);

            // Anel metálico de aperto / arruela
            const collar = new THREE.Mesh(new THREE.CylinderGeometry(0.065, 0.065, 0.02, 16), mLight);
            collar.position.set(x, y + 0.015, z);
            collar.userData = { isPin: true };
            collar.castShadow = false;
            collar.receiveShadow = false;
            terminalGroup.add(collar);

            // Parafuso metálico de fenda
            const screw = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.026, 12), mLight);
            screw.position.set(x, y + 0.025, z);
            screw.userData = { isPin: true };
            screw.castShadow = false;
            screw.receiveShadow = false;
            terminalGroup.add(screw);

            // Ranhura da fenda do parafuso
            const slot = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.005, 0.065), new THREE.MeshBasicMaterial({ color: 0x020617 }));
            slot.position.set(x, y + 0.038, z);
            slot.userData = { isPin: true };
            slot.castShadow = false;
            slot.receiveShadow = false;
            terminalGroup.add(slot);

            // Indicador de estado
            const pLight = new THREE.Mesh(
                new THREE.BoxGeometry(0.10, 0.02, 0.10), 
                new THREE.MeshStandardMaterial({ color: isOutput ? COLOR_HIGH : COLOR_LOW, roughness: 0.5 })
            );
            pLight.position.set(x, y - 0.01, z);
            pLight.userData = { isPin: true, pinType: isOutput ? 'output' : 'input', pinId: pinId, localX: x, localZ: z };
            pLight.castShadow = false;
            pLight.receiveShadow = false;
            terminalGroup.add(pLight);

            if (isOutput && pinId) {
                root.userData.outputMeshes[pinId] = pLight;
            }

            // Rótulo nítido
            const lbl = createPinLabel(labelText, isOutput);
            const labelOffsetZ = (z >= 0) ? -0.16 : 0.16;
            lbl.position.set(x, y + 0.035, z + labelOffsetZ);
            lbl.userData = { isPin: true, isLabel: true, pinType: isOutput ? 'output' : 'input', pinId: pinId, localX: x, localZ: z };
            lbl.castShadow = false;
            lbl.receiveShadow = false;
            terminalGroup.add(lbl);

            // Hitbox
            const pHitbox = new THREE.Mesh(pinHitboxGeo, pinHitboxMat);
            pHitbox.position.set(x, y + 0.02, z);
            pHitbox.userData = { isPin: true, pinType: isOutput ? 'output' : 'input', pinId: pinId, localX: x, localZ: z };
            terminalGroup.add(pHitbox);

            root.add(terminalGroup);

            // Registros protegidos
            root.userData.terminals[terminalId] = terminalGroup;
            root.userData.screwTerminals[terminalId] = terminalGroup;

            if (pinId) {
                root.userData.pinElements[pinId] = { base: block, light: pLight, label: lbl, hitbox: pHitbox, group: terminalGroup };
            }
        } catch (err) {
            console.error('[createScrewTerminalMesh Error]', err);
        }
        return terminalGroup;
    }

    function attachPinsToMesh(root, localPins, matMetal) {
        root.userData.pinElements = root.userData.pinElements || {};
        const cType = root.userData.componentType;
        if (cType === core.COMP.LAMP || cType === core.COMP.LED_RED || cType === core.COMP.BUZZER) return;
        const matDarkMetal = new THREE.MeshStandardMaterial({ color: 0x475569, metalness: 0.7, roughness: 0.35 });

        // A) PORTAS LÓGICAS (GATE_TYPES): Bornes banana fêmea na face superior (+Y)
        if (core.GATE_TYPES.has(cType)) {
            const yTop = -0.27;
            localPins.outputs.forEach(pOut => {
                createBananaSocketMesh(pOut.x, yTop, pOut.z, pOut.id === 'S' ? '▲ S' : pOut.id, true, pOut.id, root, matMetal);
            });
            localPins.inputs.forEach(pIn => {
                createBananaSocketMesh(pIn.x, yTop, pIn.z, pIn.id, false, pIn.id, root, matMetal);
            });
            return;
        }

        // B) MÓDULO RELÉ (RELAY): Bornes de parafuso metálicos em degraus chanfrados
        if (cType === core.COMP.RELAY) {
            const yTerminal = -0.32;
            localPins.outputs.forEach(pOut => {
                createScrewTerminalMesh(pOut.x, yTerminal, pOut.z, pOut.id, true, pOut.id, root, matMetal, matDarkMetal);
            });
            localPins.inputs.forEach(pIn => {
                createScrewTerminalMesh(pIn.x, yTerminal, pIn.z, pIn.id, false, pIn.id, root, matMetal, matDarkMetal);
            });
            // Adiciona terminal A2 (0V/GND) decorativo com parafuso no degrau traseiro
            createScrewTerminalMesh(0.0, yTerminal, -0.55, 'A2', false, null, root, matMetal, matDarkMetal);
            return;
        }

        // C) TEMPORIZADOR (TIMER_TON): Bornes de parafuso metálicos no topo/degrau
        if (cType === core.COMP.TIMER_TON) {
            const yTerminal = -0.20;
            localPins.outputs.forEach(pOut => {
                createScrewTerminalMesh(pOut.x, yTerminal, pOut.z, pOut.id, true, pOut.id, root, matMetal, matDarkMetal);
            });
            localPins.inputs.forEach(pIn => {
                createScrewTerminalMesh(pIn.x, yTerminal, pIn.z, pIn.id, false, pIn.id, root, matMetal, matDarkMetal);
            });
            // Adiciona terminal GND/A2 de referência com parafuso no topo
            createScrewTerminalMesh(0.50, yTerminal, -0.50, 'GND', false, null, root, matMetal, matDarkMetal);
            return;
        }

        // D) CONTATOR TRIPOLAR DE POTÊNCIA (CONTACTOR_3P): Bornes A1/A2, L1..L3, T1..T3, 13-14
        if (cType === core.COMP.CONTACTOR_3P) {
            const yTerminal = -0.22;
            localPins.inputs.forEach(pIn => {
                let lbl = pIn.id;
                if (pIn.id === '1/L1' || pIn.id === 'L1') lbl = 'L1';
                else if (pIn.id === '3/L2' || pIn.id === 'L2') lbl = 'L2';
                else if (pIn.id === '5/L3' || pIn.id === 'L3') lbl = 'L3';
                else if (pIn.id === '13') lbl = '13/NO';
                createScrewTerminalMesh(pIn.x, yTerminal, pIn.z, lbl, false, pIn.id, root, matMetal, matDarkMetal);
            });
            localPins.outputs.forEach(pOut => {
                let lbl = pOut.id;
                if (pOut.id === '2/T1' || pOut.id === 'T1') lbl = 'T1';
                else if (pOut.id === '4/T2' || pOut.id === 'T2') lbl = 'T2';
                else if (pOut.id === '6/T3' || pOut.id === 'T3') lbl = 'T3';
                else if (pOut.id === '14') lbl = '14/NO';
                createScrewTerminalMesh(pOut.x, yTerminal, pOut.z, lbl, true, pOut.id, root, matMetal, matDarkMetal);
            });
            ['1/L1', '3/L2', '5/L3', '2/T1', '4/T2', '6/T3'].forEach(idWithSlash => {
                const shortId = idWithSlash.split('/')[1];
                if (root.userData.pinElements[idWithSlash]) {
                    root.userData.pinElements[shortId] = root.userData.pinElements[idWithSlash];
                }
            });
            return;
        }

        // E) ALIMENTAÇÃO TRIFÁSICA & ATERRAMENTO PE (POWER_SUPPLY_3P)
        if (cType === core.COMP.POWER_SUPPLY_3P) {
            const yTerminal = -0.15;
            const matL1 = new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.6, metalness: 0.4 }); // Preto
            const matL2 = new THREE.MeshStandardMaterial({ color: 0x78350F, roughness: 0.6, metalness: 0.4 }); // Marrom
            const matL3 = new THREE.MeshStandardMaterial({ color: 0x475569, roughness: 0.6, metalness: 0.4 }); // Cinza
            const matN  = new THREE.MeshStandardMaterial({ color: 0x1D4ED8, roughness: 0.6, metalness: 0.4 }); // Azul
            const matPE = new THREE.MeshStandardMaterial({ color: 0x15803D, roughness: 0.6, metalness: 0.4 }); // Verde/Amarelo

            localPins.outputs.forEach(pOut => {
                let mCol = matDarkMetal;
                if (pOut.id === 'L1') mCol = matL1;
                else if (pOut.id === 'L2') mCol = matL2;
                else if (pOut.id === 'L3') mCol = matL3;
                else if (pOut.id === 'N') mCol = matN;
                else if (pOut.id === 'PE') mCol = matPE;

                createScrewTerminalMesh(pOut.x, yTerminal, pOut.z, pOut.id, true, pOut.id, root, matMetal, mCol);
            });
            return;
        }

        // D) VÁLVULAS DIRECIONAIS (VALVE_TYPES): Caixeta lateral padrão DIN 43650 com bornes Y1/Y2
        if (core.VALVE_TYPES.has(cType)) {
            const isBi = (cType === core.COMP.VALVE_5_2_BI || cType === core.COMP.VALVE_4_2_BI);
            const isHydra = (cType === core.COMP.VALVE_4_2_MONO || cType === core.COMP.VALVE_4_2_BI);
            const matDinBox = new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.85, metalness: 0.1 });
            const matScrew = new THREE.MeshStandardMaterial({ color: 0xCBD5E1, roughness: 0.25, metalness: 0.9 });
            
            // Caixeta DIN 43650 Solenoide Y1 (Lado Esquerdo -X)
            const dinBox1 = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.24, 0.24), matDinBox);
            dinBox1.position.set(-0.58, -0.24, 0.0);
            dinBox1.castShadow = true;
            root.add(dinBox1);
            const screw1 = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.02, 12), matScrew);
            screw1.position.set(-0.58, -0.11, 0.0);
            root.add(screw1);
            
            createScrewTerminalMesh(-0.62, -0.20, 0.0, isHydra ? 'YH1' : 'Y1', false, 'Y1', root, matMetal, matDarkMetal);
            if (root.userData.pinElements['Y1']) {
                root.userData.pinElements['YH1'] = root.userData.pinElements['Y1'];
                root.userData.pinElements['A1'] = root.userData.pinElements['Y1'];
            }
            createScrewTerminalMesh(-0.62, -0.20, -0.14, 'PE', false, null, root, matMetal, matDarkMetal);

            // Se biestável: Caixeta DIN 43650 Solenoide Y2 (Lado Direito +X)
            if (isBi) {
                const dinBox2 = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.24, 0.24), matDinBox);
                dinBox2.position.set(0.58, -0.24, 0.0);
                dinBox2.castShadow = true;
                root.add(dinBox2);
                const screw2 = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.02, 12), matScrew);
                screw2.position.set(0.58, -0.11, 0.0);
                root.add(screw2);
                
                createScrewTerminalMesh(0.62, -0.20, 0.0, isHydra ? 'YH2' : 'Y2', false, 'Y2', root, matMetal, matDarkMetal);
                if (root.userData.pinElements['Y2']) {
                    root.userData.pinElements['YH2'] = root.userData.pinElements['Y2'];
                    root.userData.pinElements['A2'] = root.userData.pinElements['Y2'];
                }
                createScrewTerminalMesh(0.62, -0.20, -0.14, 'PE', false, null, root, matMetal, matDarkMetal);
            }
            return;
        }

        // E) DEMAIS COMPONENTES INDUSTRIAIS: Anéis e bornes protegidos sem sombras parasitas
        const matGoldRing = new THREE.MeshStandardMaterial({ color: 0xF59E0B, metalness: 0.92, roughness: 0.2 });
        const matSilverRing = new THREE.MeshStandardMaterial({ color: 0xCBD5E1, metalness: 0.88, roughness: 0.25 });

        localPins.outputs.forEach(pOut => {
            const ring = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.13, 0.04, 16), matGoldRing);
            ring.position.set(pOut.x, -0.44, pOut.z);
            ring.userData = { isPin: true };
            ring.castShadow = false;
            ring.receiveShadow = false;
            root.add(ring);

            const pBase = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.10, 0.18), matMetal);
            pBase.position.set(pOut.x, WIRE_Y, pOut.z); 
            pBase.userData = { isPin: true, pinType: 'output', pinId: pOut.id, localX: pOut.x, localZ: pOut.z };
            pBase.castShadow = false;
            pBase.receiveShadow = false;
            root.add(pBase);

            const pLight = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.10, 0.14), new THREE.MeshStandardMaterial({ color: COLOR_HIGH, roughness: 0.5 }));
            pLight.position.set(pOut.x, -0.40, pOut.z); 
            pLight.userData = { isPin: true, pinType: 'output', pinId: pOut.id, localX: pOut.x, localZ: pOut.z };
            pLight.castShadow = false;
            pLight.receiveShadow = false;
            root.add(pLight);

            root.userData.outputMeshes[pOut.id] = pLight;
            const lbl = createPinLabel(pOut.id === 'S' ? '▲ S' : pOut.id, true); 
            lbl.position.set(pOut.x, -0.36, pOut.z - 0.16); 
            lbl.userData = { isPin: true, isLabel: true, pinType: 'output', pinId: pOut.id, localX: pOut.x, localZ: pOut.z };
            lbl.castShadow = false;
            lbl.receiveShadow = false;
            root.add(lbl);

            const pHitbox = new THREE.Mesh(pinHitboxGeo, pinHitboxMat);
            pHitbox.position.set(pOut.x, -0.38, pOut.z);
            pHitbox.userData = { isPin: true, pinType: 'output', pinId: pOut.id, localX: pOut.x, localZ: pOut.z };
            root.add(pHitbox);

            root.userData.pinElements[pOut.id] = { base: pBase, light: pLight, label: lbl, hitbox: pHitbox };
        });

        localPins.inputs.forEach(pIn => {
            const ring = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.12, 0.04, 16), matSilverRing);
            ring.position.set(pIn.x, -0.44, pIn.z);
            ring.userData = { isPin: true };
            ring.castShadow = false;
            ring.receiveShadow = false;
            root.add(ring);

            const pBase = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.10, 0.18), matMetal);
            pBase.position.set(pIn.x, WIRE_Y, pIn.z); 
            pBase.userData = { isPin: true, pinType: 'input', pinId: pIn.id, localX: pIn.x, localZ: pIn.z };
            pBase.castShadow = false;
            pBase.receiveShadow = false;
            root.add(pBase);

            const pLight = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.10, 0.14), new THREE.MeshStandardMaterial({ color: COLOR_LOW, roughness: 0.8 }));
            pLight.position.set(pIn.x, -0.40, pIn.z); 
            pLight.userData = { isPin: true, pinType: 'input', pinId: pIn.id, localX: pIn.x, localZ: pIn.z };
            pLight.castShadow = false;
            pLight.receiveShadow = false;
            root.add(pLight);

            const lbl = createPinLabel(pIn.id, false); 
            lbl.position.set(pIn.x, -0.36, pIn.z + 0.16); 
            lbl.userData = { isPin: true, isLabel: true, pinType: 'input', pinId: pIn.id, localX: pIn.x, localZ: pIn.z };
            lbl.castShadow = false;
            lbl.receiveShadow = false;
            root.add(lbl);

            const pHitbox = new THREE.Mesh(pinHitboxGeo, pinHitboxMat);
            pHitbox.position.set(pIn.x, -0.38, pIn.z);
            pHitbox.userData = { isPin: true, pinType: 'input', pinId: pIn.id, localX: pIn.x, localZ: pIn.z };
            root.add(pHitbox);

            root.userData.pinElements[pIn.id] = { base: pBase, light: pLight, label: lbl, hitbox: pHitbox };
        });
    }

    function buildComponentMesh(data) {
        if (window.LogicCraft && window.LogicCraft.machines && window.LogicCraft.machines.isMachineType && window.LogicCraft.machines.isMachineType(data.type)) {
            const helpers = {
                createPinLabel,
                createBoxHighlightMesh,
                pinHitboxGeo,
                pinHitboxMat,
                WIRE_Y,
                COLOR_HIGH,
                matMetal: new THREE.MeshStandardMaterial({ color: 0xCBD5E1, metalness: 0.9, roughness: 0.2 }),
                matPlastic: new THREE.MeshStandardMaterial({ color: 0x1E293B, roughness: 0.85, metalness: 0.15, depthWrite: true }),
                matDarkMetal: new THREE.MeshStandardMaterial({ color: 0x475569, metalness: 0.7, roughness: 0.35 })
            };
            return window.LogicCraft.machines.buildMachineMesh(data, helpers);
        }

        const root = new THREE.Group();
        root.userData.outputMeshes = {};
        root.userData.componentType = data.type;
        
        const matPlastic = new THREE.MeshStandardMaterial({ color: 0x1E293B, roughness: 0.85, metalness: 0.15, depthWrite: true });
        const matChipBody = new THREE.MeshStandardMaterial({ color: 0x1E293B, roughness: 0.85, metalness: 0.15, depthWrite: true });
        const matMetal = new THREE.MeshStandardMaterial({ color: 0xCBD5E1, metalness: 0.9, roughness: 0.2 });
        const matDarkMetal = new THREE.MeshStandardMaterial({ color: 0x475569, metalness: 0.7, roughness: 0.35 });

        if (data.type === core.COMP.WIRE) {
            // Gerado no fluxo dinâmico
        } else if (data.type === 'JUMPER') {
            const base = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.04, 0.36), matPlastic);
            base.position.y = WIRE_Y - 0.01; 
            root.add(base);
        } else if (data.type === core.COMP.SWITCH) {
            const conf = core.INPUT_CONFIG.find(c => c.id === data.switchId) ?? core.INPUT_CONFIG[0];
            const body = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.25, 0.7), matPlastic);
            body.position.y = -0.375; body.castShadow = true; root.add(body);
            
            const btn = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.08, 0.48), matDarkMetal);
            btn.position.y = -0.23; 
            btn.userData = { isSwitchButton: true };
            root.add(btn);

            const screen = new THREE.Mesh(new THREE.PlaneGeometry(0.48, 0.48), new THREE.MeshBasicMaterial({ map: createSwitchTexture(data.manualValue, conf) }));
            screen.rotation.x = -Math.PI / 2; screen.position.y = -0.185; 
            screen.userData = { isSwitchButton: true };
            root.add(screen);
            root.userData.screen = screen;

            const pOut = new THREE.Mesh(new THREE.BoxGeometry(0.20, 0.12, 0.20), new THREE.MeshStandardMaterial({ color: conf.color, metalness: 0.8 }));
            pOut.position.set(0, -0.40, core.GRID_SIZE);
            pOut.userData = { isPin: true, pinType: 'output', pinId: data.switchId, localX: 0, localZ: core.GRID_SIZE };
            root.add(pOut);

            const pOutHitbox = new THREE.Mesh(pinHitboxGeo, pinHitboxMat);
            pOutHitbox.position.set(0, -0.38, core.GRID_SIZE);
            pOutHitbox.userData = { isPin: true, pinType: 'output', pinId: data.switchId, localX: 0, localZ: core.GRID_SIZE };
            root.add(pOutHitbox);
            
        } else if (data.type === core.COMP.PUSH_BUTTON_NO || data.type === core.COMP.PUSH_BUTTON_NC) {
            const isNO = (data.type === core.COMP.PUSH_BUTTON_NO);
            const body = new THREE.Mesh(new THREE.BoxGeometry(0.76, 0.25, 0.76), matPlastic);
            body.position.y = -0.375; body.castShadow = true; root.add(body);

            // Aro chanfrado metálico de alto contraste
            const outerBezel = new THREE.Mesh(
                new THREE.CylinderGeometry(0.24, 0.27, 0.08, 24),
                new THREE.MeshStandardMaterial({ color: 0xE2E8F0, metalness: 0.92, roughness: 0.18 })
            );
            outerBezel.position.y = -0.22; root.add(outerBezel);

            const innerCollar = new THREE.Mesh(
                new THREE.CylinderGeometry(0.21, 0.21, 0.07, 24),
                new THREE.MeshStandardMaterial({ color: 0x0F172A, metalness: 0.5, roughness: 0.5 })
            );
            innerCollar.position.y = -0.21; root.add(innerCollar);

            const capColor = isNO ? 0x22C55E : 0xEF4444;
            const buttonCap = new THREE.Mesh(
                new THREE.CylinderGeometry(0.19, 0.19, 0.12, 24),
                new THREE.MeshStandardMaterial({ 
                    color: capColor, 
                    roughness: 0.35, 
                    metalness: 0.2,
                    emissive: isNO ? (data.isPressed ? 0x22C55E : 0x052e16) : (!data.isPressed ? 0x450a0a : 0xEF4444),
                    emissiveIntensity: data.isPressed ? 1.4 : 0.2
                })
            );
            buttonCap.position.y = data.isPressed ? -0.25 : -0.19;
            buttonCap.userData = { isButtonCap: true };
            root.add(buttonCap);
            root.userData.buttonCap = buttonCap;

            const pOut = new THREE.Mesh(new THREE.BoxGeometry(0.20, 0.12, 0.20), new THREE.MeshStandardMaterial({ color: capColor, metalness: 0.8 }));
            pOut.position.set(0, -0.40, core.GRID_SIZE);
            pOut.userData = { isPin: true, pinType: 'output', pinId: 'OUT', localX: 0, localZ: core.GRID_SIZE };
            root.add(pOut);

            const pOutHitbox = new THREE.Mesh(pinHitboxGeo, pinHitboxMat);
            pOutHitbox.position.set(0, -0.38, core.GRID_SIZE);
            pOutHitbox.userData = { isPin: true, pinType: 'output', pinId: 'OUT', localX: 0, localZ: core.GRID_SIZE };
            root.add(pOutHitbox);

            const lbl = createPinLabel(isNO ? 'NA' : 'NF', true);
            lbl.position.set(0, -0.36, core.GRID_SIZE - 0.16);
            lbl.userData = { isPin: true, pinType: 'output', pinId: 'OUT', localX: 0, localZ: core.GRID_SIZE };
            root.add(lbl);

            root.add(createBoxHighlightMesh(0.85, 0.85, 0));

        } else if (data.type === core.COMP.EMERGENCY_STOP) {
            const yellowBase = new THREE.Mesh(
                new THREE.BoxGeometry(0.8, 0.22, 0.8),
                new THREE.MeshStandardMaterial({ color: 0xFACC15, roughness: 0.5, metalness: 0.1 })
            );
            yellowBase.position.y = -0.39; yellowBase.castShadow = true; root.add(yellowBase);

            const collar = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.24, 0.10, 20), matPlastic);
            collar.position.y = -0.23; root.add(collar);

            const mushroomCap = new THREE.Mesh(
                new THREE.CylinderGeometry(0.35, 0.32, 0.16, 24),
                new THREE.MeshStandardMaterial({ color: 0xDC2626, roughness: 0.3, metalness: 0.15 })
            );
            mushroomCap.position.y = data.isEmergencyLocked ? -0.20 : -0.10;
            root.add(mushroomCap);
            root.userData.mushroomCap = mushroomCap;

            const pOut = new THREE.Mesh(new THREE.BoxGeometry(0.20, 0.12, 0.20), new THREE.MeshStandardMaterial({ color: 0xEF4444, metalness: 0.8 }));
            pOut.position.set(0, -0.40, core.GRID_SIZE);
            pOut.userData = { isPin: true, pinType: 'output', pinId: 'OUT', localX: 0, localZ: core.GRID_SIZE };
            root.add(pOut);

            const pOutHitbox = new THREE.Mesh(pinHitboxGeo, pinHitboxMat);
            pOutHitbox.position.set(0, -0.38, core.GRID_SIZE);
            pOutHitbox.userData = { isPin: true, pinType: 'output', pinId: 'OUT', localX: 0, localZ: core.GRID_SIZE };
            root.add(pOutHitbox);

            const lbl = createPinLabel('EMG', true);
            lbl.position.set(0, -0.36, core.GRID_SIZE - 0.16);
            lbl.userData = { isPin: true, pinType: 'output', pinId: 'OUT', localX: 0, localZ: core.GRID_SIZE };
            root.add(lbl);

            root.add(createBoxHighlightMesh(0.9, 0.9, 0));

        } else if (data.type === core.COMP.RELAY) {
            // --- 1. BASE DE ENCAIXE / SOQUETE TRILHO DIN 1.4 x 1.4 (Preto industrial fosco) ---
            const socketBase = new THREE.Mesh(
                new THREE.BoxGeometry(1.40, 0.16, 1.40),
                new THREE.MeshStandardMaterial({ color: 0x0B132B, roughness: 0.85, metalness: 0.15 })
            );
            socketBase.position.y = -0.42;
            socketBase.castShadow = true;
            root.add(socketBase);

            // Rebaixo/ranhura central de encaixe
            const socketGroove = new THREE.Mesh(
                new THREE.BoxGeometry(0.80, 0.05, 0.80),
                new THREE.MeshStandardMaterial({ color: 0x020617, roughness: 0.9 })
            );
            socketGroove.position.y = -0.33;
            root.add(socketGroove);

            // --- 2. MECANISMO INTERNO (Compacto e visível através da cúpula) ---
            
            // Bobina enrolada em cobre esmaltado
            const copperCoil = new THREE.Mesh(
                new THREE.CylinderGeometry(0.16, 0.16, 0.26, 20),
                new THREE.MeshStandardMaterial({ color: 0xB45309, roughness: 0.35, metalness: 0.85 })
            );
            copperCoil.position.set(-0.16, -0.22, 0);
            copperCoil.castShadow = true;
            root.add(copperCoil);

            // Núcleo ferromagnético central da bobina
            const coilCore = new THREE.Mesh(
                new THREE.CylinderGeometry(0.07, 0.07, 0.30, 16),
                new THREE.MeshStandardMaterial({ color: 0x94A3B8, metalness: 0.9, roughness: 0.2 })
            );
            coilCore.position.set(-0.16, -0.22, 0);
            root.add(coilCore);

            // Armadura basculante de ferro (palheta móvel articulada)
            const armaturePlate = new THREE.Mesh(
                new THREE.BoxGeometry(0.04, 0.22, 0.14),
                new THREE.MeshStandardMaterial({ color: 0xCBD5E1, metalness: 0.9, roughness: 0.2 })
            );
            armaturePlate.position.set(0.04, -0.21, 0);
            root.add(armaturePlate);
            root.userData.armaturePlate = armaturePlate;

            // Palhetas fixas de contato (NO / NC) em latão polido
            const contactNO = new THREE.Mesh(
                new THREE.BoxGeometry(0.03, 0.16, 0.06),
                new THREE.MeshStandardMaterial({ color: 0xF59E0B, metalness: 0.85, roughness: 0.25 })
            );
            contactNO.position.set(0.16, -0.24, -0.10);
            root.add(contactNO);

            const contactNC = new THREE.Mesh(
                new THREE.BoxGeometry(0.03, 0.16, 0.06),
                new THREE.MeshStandardMaterial({ color: 0xF59E0B, metalness: 0.85, roughness: 0.25 })
            );
            contactNC.position.set(0.16, -0.24, 0.10);
            root.add(contactNC);

            // Mini Placa de Circuito Impresso interna verde
            const internalPcb = new THREE.Mesh(
                new THREE.BoxGeometry(0.66, 0.02, 0.66),
                new THREE.MeshStandardMaterial({ color: 0x14532D, roughness: 0.6 })
            );
            internalPcb.position.set(0, -0.32, 0);
            root.add(internalPcb);

            // --- 3. CÚPULA ACRÍLICA TRANSPARENTE REDUZIDA (0.72 x 0.72) ---
            // Deixa margem livre para os 4 bornes externos em X=+-0.65, Z=+-0.65
            const glassCover = new THREE.Mesh(
                new THREE.BoxGeometry(0.72, 0.38, 0.72),
                new THREE.MeshPhysicalMaterial({
                    color: 0xE2E8F0,
                    transmission: 0.88,
                    opacity: 1,
                    transparent: true,
                    roughness: 0.12,
                    metalness: 0.1,
                    ior: 1.45,
                    reflectivity: 0.6
                })
            );
            glassCover.position.set(0, -0.15, 0);
            root.add(glassCover);

            // --- 4. INDICADOR PILOTO LED DA BOBINA & SERIGRAFIA ---
            const ledBezel = new THREE.Mesh(
                new THREE.CylinderGeometry(0.06, 0.06, 0.02, 16),
                matDarkMetal
            );
            ledBezel.position.set(0.20, -0.31, -0.20);
            root.add(ledBezel);

            const ledCoil = new THREE.Mesh(
                new THREE.CylinderGeometry(0.045, 0.045, 0.04, 16),
                new THREE.MeshStandardMaterial({ color: 0x052e16, roughness: 0.2 })
            );
            ledCoil.position.set(0.20, -0.28, -0.20);
            root.add(ledCoil);
            root.userData.relayArmLed = ledCoil;

            const labelPlate = new THREE.Mesh(
                new THREE.PlaneGeometry(0.40, 0.10),
                new THREE.MeshBasicMaterial({ color: 0x1E293B })
            );
            labelPlate.position.set(0, -0.12, 0.365);
            root.add(labelPlate);

            // Conexão dos bornes externos (A1, COM, NO, NC) nas 4 extremidades
            const localPins = core.getLocalComponentPins(data.type);
            attachPinsToMesh(root, localPins, matMetal);
            root.add(createBoxHighlightMesh(1.50, 1.50, 0));

        } else if (data.type === core.COMP.TIMER_TON) {
            const timerBody = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.38, 1.25), matPlastic);
            timerBody.position.y = -0.31; timerBody.castShadow = true; root.add(timerBody);
            const chamferShape = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.15, 0.85), matPlastic);
            chamferShape.rotation.x = Math.PI / 4; chamferShape.position.set(0, -0.15, -0.18); chamferShape.castShadow = true; root.add(chamferShape);
            
            const knobGroup = new THREE.Group(); knobGroup.position.set(0, -0.06, -0.18); knobGroup.rotation.x = Math.PI / 4;
            const dial = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.14, 0.08, 24), new THREE.MeshStandardMaterial({ color: 0x334155, metalness: 0.2, roughness: 0.8 }));
            knobGroup.add(dial);
            const dialIndicator = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.09, 0.12), new THREE.MeshStandardMaterial({ color: 0xFF7700, roughness: 0.5 }));
            dialIndicator.position.set(0, 0, -0.06); knobGroup.add(dialIndicator); root.add(knobGroup);
            
            const scaleCanvas = document.createElement('canvas'); scaleCanvas.width = 512; scaleCanvas.height = 512;
            const sCtx = scaleCanvas.getContext('2d'); sCtx.fillStyle = '#1E293B'; sCtx.fillRect(0,0,512,512); sCtx.translate(256, 256); sCtx.strokeStyle = '#CBD5E1'; sCtx.lineWidth = 8;
            sCtx.beginPath(); sCtx.arc(0, 0, 200, Math.PI * 0.75, Math.PI * 2.25); sCtx.stroke();
            sCtx.fillStyle = '#FFFFFF'; sCtx.font = 'bold 44px monospace'; sCtx.textAlign = 'center'; sCtx.textBaseline = 'middle';
            for (let i = 0; i <= 5; i++) { const ang = Math.PI * 0.75 + (i / 5) * (Math.PI * 1.5); sCtx.beginPath(); sCtx.moveTo(200 * Math.cos(ang), 200 * Math.sin(ang)); sCtx.lineTo(170 * Math.cos(ang), 170 * Math.sin(ang)); sCtx.stroke(); sCtx.fillText(i + 's', 130 * Math.cos(ang), 130 * Math.sin(ang)); }
            const scalePlate = new THREE.Mesh(new THREE.PlaneGeometry(0.4, 0.4), new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(scaleCanvas) }));
            scalePlate.rotation.x = -Math.PI / 2; scalePlate.position.set(0, 0.041, 0); knobGroup.add(scalePlate);
            
            const timerLed = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.02, 16), new THREE.MeshStandardMaterial({ color: 0x1E293B, emissive: 0x000000, roughness: 0.2 }));
            timerLed.position.set(-0.25, -0.11, 0.35); root.add(timerLed); root.userData.timerLed = timerLed;
            
            // LED metal ring
            const ledRing = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.01, 16), matMetal);
            ledRing.position.set(-0.25, -0.12, 0.35); root.add(ledRing);

            const canvas = document.createElement('canvas'); canvas.width = 512; canvas.height = 256; const ctx = canvas.getContext('2d'); 
            ctx.fillStyle = '#020617'; ctx.fillRect(0,0,512,256);
            const topTex = new THREE.CanvasTexture(canvas);
            root.userData.timerCanvas = canvas; root.userData.timerCtx = ctx; root.userData.timerTex = topTex; root.userData.lastTimerText = '';
            
            const labelMesh = new THREE.Mesh(new THREE.PlaneGeometry(0.40, 0.20), new THREE.MeshBasicMaterial({ map: topTex }));
            labelMesh.rotation.x = -Math.PI / 2; labelMesh.position.set(0.1, -0.11, 0.35); root.add(labelMesh);
            
            const localPins = core.getLocalComponentPins(data.type); attachPinsToMesh(root, localPins, matMetal);
            root.add(createBoxHighlightMesh(1.05, 1.35, 0));
            
        } else if (data.type === core.COMP.DEC_BCD_7SEG) {
            const topTex = createGenericTopTexture('SN74LS47N', '7447 BCD → 7SEG');
            const matDipTop = new THREE.MeshStandardMaterial({ map: topTex, roughness: 0.85, metalness: 0.08 });
            const mats = Array(6).fill(null).map((_,i) => i===2 ? matDipTop : matChipBody);
            
            const body = new THREE.Mesh(new THREE.BoxGeometry(3.6, 0.22, 1.2), mats);
            body.position.set(0, -0.36, 0);
            body.castShadow = true; 
            root.add(body);

            // Chanfro chanfrado / notch indicador
            const notch = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.23, 16, 1, false, 0, Math.PI), matPlastic);
            notch.position.set(-1.8, -0.36, 0);
            notch.rotation.y = Math.PI / 2;
            root.add(notch);

            // Indicador ponto Pino 1
            const pin1Dot = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.02, 12), new THREE.MeshStandardMaterial({ color: 0x94A3B8, roughness: 0.4 }));
            pin1Dot.position.set(-1.65, -0.245, -0.45);
            root.add(pin1Dot);

            // Pernas metálicas DIP do leadframe nos flancos
            for (let i = 0; i < 16; i++) {
                const zPos = -0.48 + (i / 15) * 0.96;
                const legL = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.16, 0.035), matMetal);
                legL.position.set(-1.82, -0.42, zPos);
                root.add(legL);
                const legR = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.16, 0.035), matMetal);
                legR.position.set(1.82, -0.42, zPos);
                root.add(legR);
            }
            
            const localPins = core.getLocalComponentPins(data.type, data.inputCount);
            attachPinsToMesh(root, localPins, matMetal);
            root.add(createBoxHighlightMesh(3.8, 1.6, 0));
            
        } else if (data.type === core.COMP.DISPLAY_7SEG) {
            const body = new THREE.Mesh(new THREE.BoxGeometry(3.6, 0.32, 1.6), matChipBody);
            body.position.set(0, -0.34, 0); body.castShadow = true; root.add(body);
            
            const maskCavity = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.05, 1.3), new THREE.MeshStandardMaterial({ color: 0x020617, roughness: 0.95 }));
            maskCavity.position.set(0, -0.17, 0.05);
            root.add(maskCavity);

            root.userData.segmentMeshes = {};
            const segOffsets = { 
                a: { x: 0, z: -0.30, w: 0.38, h: 0.08 }, 
                b: { x: 0.22, z: -0.16, w: 0.08, h: 0.24 }, 
                c: { x: 0.22, z: 0.16, w: 0.08, h: 0.24 }, 
                d: { x: 0, z: 0.30, w: 0.38, h: 0.08 }, 
                e: { x: -0.22, z: 0.16, w: 0.08, h: 0.24 }, 
                f: { x: -0.22, z: -0.16, w: 0.08, h: 0.24 }, 
                g: { x: 0, z: 0.0, w: 0.38, h: 0.08 } 
            };
            
            Object.keys(segOffsets).forEach(sId => {
                const conf = segOffsets[sId];
                const sm = new THREE.Mesh(
                    new THREE.BoxGeometry(conf.w, 0.03, conf.h), 
                    new THREE.MeshStandardMaterial({ 
                        color: 0x1E293B, 
                        emissive: 0x000000, 
                        emissiveIntensity: 0.0, 
                        roughness: 0.5 
                    })
                );
                sm.position.set(conf.x, -0.14, conf.z + 0.05); 
                root.add(sm); 
                root.userData.segmentMeshes[sId] = sm;
            });
            
            const localPins = core.getLocalComponentPins(data.type, data.inputCount);
            attachPinsToMesh(root, localPins, matMetal);
            root.add(createBoxHighlightMesh(3.8, 1.8, 0));
            
        } else if (core.GATE_TYPES.has(data.type)) {
            const topTex = createGateTopTexture(data.type, data.inputCount);
            const matDipTop = new THREE.MeshStandardMaterial({ map: topTex, roughness: 0.85, metalness: 0.08 });
            const mats = Array(6).fill(null).map((_, i) => i === 2 ? matDipTop : matChipBody);
            const zDepth = (data.inputCount === 4) ? 1.25 : 0.95; 
            const zCenter = 0.0;
            
            // Módulo didático industrial de bancada (sem perninhas DIP de protoboard)
            const block = new THREE.Mesh(new THREE.BoxGeometry(1.20, 0.22, zDepth), mats);
            block.position.set(0, -0.38, zCenter); 
            block.castShadow = true; 
            root.add(block);

            // Placa base de montagem industrial com rebaixo chanfrado
            const basePlate = new THREE.Mesh(new THREE.BoxGeometry(1.26, 0.04, zDepth + 0.06), matDarkMetal);
            basePlate.position.set(0, -0.47, zCenter); 
            root.add(basePlate);

            const localPins = core.getLocalGatePins(data.inputCount);
            attachPinsToMesh(root, localPins, matMetal);
            root.add(createBoxHighlightMesh(1.30, zDepth + 0.1, zCenter));
            
        } else if (data.type === core.COMP.SOLENOID_HYDRA || data.type === core.COMP.SOLENOID_PNEU) {
            const isPneu = (data.type === core.COMP.SOLENOID_PNEU);
            
            const coilBody = new THREE.Mesh(
                new THREE.BoxGeometry(0.5, 0.42, 0.5),
                new THREE.MeshStandardMaterial({ color: isPneu ? 0x0284C7 : 0x1E3A8A, roughness: 0.3, metalness: 0.6 })
            );
            coilBody.position.y = -0.28; coilBody.castShadow = true; root.add(coilBody);

            const dinPlug = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.16, 0.3), matDarkMetal);
            dinPlug.position.set(0, -0.01, 0); root.add(dinPlug);

            const led = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.04, 16), new THREE.MeshStandardMaterial({ color: 0x111827 }));
            led.position.set(0, 0.08, 0); root.add(led);
            root.userData.indicator = led;

            const pIn = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.1, 0.18), matMetal);
            pIn.position.set(0, -0.42, -core.GRID_SIZE);
            pIn.userData = { isPin: true, pinType: 'input', pinId: 'A1', localX: 0, localZ: -core.GRID_SIZE };
            root.add(pIn);

            const pInHitbox = new THREE.Mesh(pinHitboxGeo, pinHitboxMat);
            pInHitbox.position.set(0, -0.38, -core.GRID_SIZE);
            pInHitbox.userData = { isPin: true, pinType: 'input', pinId: 'A1', localX: 0, localZ: -core.GRID_SIZE };
            root.add(pInHitbox);

            const lblIn = createPinLabel('A1', false);
            lblIn.position.set(0, -0.38, -core.GRID_SIZE + 0.14);
            lblIn.userData = { isPin: true, pinType: 'input', pinId: 'A1', localX: 0, localZ: -core.GRID_SIZE };
            root.add(lblIn);
            root.add(createBoxHighlightMesh(0.7, 0.7, 0));

        } else if (data.type === core.COMP.VALVE_FLOW_CONTROL) {
            const body = new THREE.Mesh(
                new THREE.BoxGeometry(0.7, 0.3, 0.7),
                new THREE.MeshStandardMaterial({ color: 0x0284C7, roughness: 0.3, metalness: 0.6 })
            );
            body.position.y = -0.35; body.castShadow = true; root.add(body);

            const knob = new THREE.Mesh(
                new THREE.CylinderGeometry(0.18, 0.20, 0.12, 16),
                matDarkMetal
            );
            knob.position.set(0, -0.16, 0); root.add(knob);
            root.userData.knob = knob;

            const labelMesh = createPinLabel(`${data.openingPercent ?? 100}%`, true);
            labelMesh.position.set(0, -0.19, 0.22);
            root.add(labelMesh);
            root.userData.labelMesh = labelMesh;

            root.add(createBoxHighlightMesh(0.8, 0.8, 0));

        } else if (core.VALVE_TYPES.has(data.type)) {
            const isHydra = (data.type === core.COMP.VALVE_4_2_MONO || data.type === core.COMP.VALVE_4_2_BI);
            const is32 = (data.type === core.COMP.VALVE_3_2_MONO);
            const isMono = (is32 || data.type === core.COMP.VALVE_5_2_MONO || data.type === core.COMP.VALVE_4_2_MONO);
            const topTex = createValveTopTexture(is32 ? '3/2 MONO' : isHydra ? (isMono ? '4/2 MONO' : '4/2 BIEST') : (isMono ? '5/2 MONO' : '5/2 BIEST'), isHydra, isMono);

            const matAluminum = new THREE.MeshStandardMaterial({ color: isHydra ? 0x1E293B : 0x334155, roughness: 0.35, metalness: 0.75 });
            const mats = Array(6).fill(null).map((_, i) => i === 2 ? new THREE.MeshStandardMaterial({ map: topTex, roughness: 0.45, metalness: 0.2, emissive: isHydra ? 0x78350F : 0x0369A1, emissiveIntensity: 0.25 }) : matAluminum);

            const manifold = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.38, 0.95), mats);
            manifold.position.y = -0.31; manifold.castShadow = true; root.add(manifold);

            // Canister cilíndrico do Solenoide Y1 na lateral esquerda (-X)
            const matSolCanister = new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.7, metalness: 0.3 });
            const solCanister1 = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.26, 16), matSolCanister);
            solCanister1.rotation.z = Math.PI / 2; solCanister1.position.set(-0.52, -0.31, 0); root.add(solCanister1);

            // Lateral direita: Solenoide Y2 (biestável) ou Capa cilíndrica de Mola (monoestável)
            if (!isMono) {
                const solCanister2 = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.26, 16), matSolCanister);
                solCanister2.rotation.z = Math.PI / 2; solCanister2.position.set(0.52, -0.31, 0); root.add(solCanister2);
            } else {
                const springCap = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.16, 16), matAluminum);
                springCap.rotation.z = Math.PI / 2; springCap.position.set(0.50, -0.31, 0); root.add(springCap);
            }

            attachFluidPortsToMesh(root, data.type, isHydra);
            const localPins = core.getLocalComponentPins(data.type);
            attachPinsToMesh(root, localPins, matMetal);
            const spool = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.26, 16), matMetal);
            spool.rotation.z = Math.PI / 2; spool.position.set(-0.38, -0.31, 0); root.add(spool);
            root.userData.spool = spool;
            root.add(createBoxHighlightMesh(1.45, 1.1, 0));

        } else if (core.CYLINDER_TYPES.has(data.type)) {
            const isHydra = (data.type === core.COMP.CYL_HYDRA);
            const isSingle = (data.type === core.COMP.CYL_SINGLE);

            // Cores e materiais distintos para pneumática (alumínio escovado) e hidráulica (aço forjado escuro)
            const matCylBody = isHydra
                ? new THREE.MeshStandardMaterial({ color: 0x1E293B, roughness: 0.55, metalness: 0.70 })
                : new THREE.MeshStandardMaterial({ color: 0xCBD5E1, roughness: 0.35, metalness: 0.80 });

            const rearChamber = new THREE.Mesh(
                new THREE.CylinderGeometry(0.20, 0.20, 0.42, 24),
                isHydra ? matCylBody : new THREE.MeshStandardMaterial({ color: COLOR_LOW, roughness: 0.3, metalness: 0.4 })
            );
            rearChamber.rotation.x = Math.PI / 2; rearChamber.position.set(0, -0.27, -0.22);
            root.add(rearChamber); root.userData.rearChamber = rearChamber;

            const frontChamber = new THREE.Mesh(
                new THREE.CylinderGeometry(0.20, 0.20, 0.42, 24),
                isHydra ? matCylBody : new THREE.MeshStandardMaterial({ color: COLOR_LOW, roughness: 0.3, metalness: 0.4, transparent: isSingle, opacity: isSingle ? 0.45 : 1.0 })
            );
            frontChamber.rotation.x = Math.PI / 2; frontChamber.position.set(0, -0.27, 0.22);
            root.add(frontChamber); root.userData.frontChamber = frontChamber;

            if (isSingle) {
                const springPoints = [];
                for (let i = 0; i <= 30; i++) {
                    const a = i * 0.7;
                    const z = 0.05 + (i / 30) * 0.35;
                    springPoints.push(new THREE.Vector3(Math.cos(a) * 0.10, -0.27 + Math.sin(a) * 0.10, z));
                }
                const springCurve = new THREE.CatmullRomCurve3(springPoints);
                const springMesh = new THREE.Mesh(new THREE.TubeGeometry(springCurve, 32, 0.012, 6, false), matMetal);
                root.add(springMesh);
            }

            if (!isHydra) {
                // --- ESTRUTURA PNEUMÁTICA (ISO 15552): Perfil de Alumínio Anodizado Claro com Canais para Sensores ---
                const matAlumProfile = new THREE.MeshStandardMaterial({ color: 0xE2E8F0, roughness: 0.28, metalness: 0.85 });
                const profileBody = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.44, 0.88), matAlumProfile);
                profileBody.position.set(0, -0.27, 0); root.add(profileBody);

                // Ranhuras em T longitudinais nas laterais para fixação e deslizamento de sensores magnéticos S0/S1
                [-0.222, 0.222].forEach(tx => {
                    const tSlot = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.08, 0.86), matDarkMetal);
                    tSlot.position.set(tx, -0.27, 0); root.add(tSlot);
                });

                const capRear = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.46, 0.10), new THREE.MeshStandardMaterial({ color: 0x334155, roughness: 0.4, metalness: 0.7 }));
                capRear.position.set(0, -0.27, -0.46); root.add(capRear);

                const capFront = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.46, 0.10), new THREE.MeshStandardMaterial({ color: 0x334155, roughness: 0.4, metalness: 0.7 }));
                capFront.position.set(0, -0.27, 0.46); root.add(capFront);

                // Conexões de engate rápido push-in pneumáticas (corpo niquelado + colar azul Festo)
                [-0.44, 0.44].forEach(pz => {
                    if (pz > 0 && isSingle) return;
                    const fitBase = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.06, 12), matMetal);
                    fitBase.position.set(0, -0.02, pz); root.add(fitBase);
                    const fitRing = new THREE.Mesh(new THREE.CylinderGeometry(0.042, 0.042, 0.03, 12), new THREE.MeshStandardMaterial({ color: 0x0284C7, roughness: 0.4 }));
                    fitRing.position.set(0, 0.02, pz); root.add(fitRing);
                });
            } else {
                // --- ESTRUTURA HIDRÁULICA PESADA (ISO 6022): Corpo Cilíndrico de Aço Forjado Escuro, 4 Tirantes M14 e Flanges Reforçadas ---
                const matHydraBody = new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.6, metalness: 0.85 });
                const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.24, 0.88, 24), matHydraBody);
                barrel.rotation.x = Math.PI / 2; barrel.position.set(0, -0.27, 0); root.add(barrel);

                const flangeRear = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.52, 0.14), matDarkMetal);
                flangeRear.position.set(0, -0.27, -0.48); root.add(flangeRear);

                const flangeFront = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.52, 0.14), matDarkMetal);
                flangeFront.position.set(0, -0.27, 0.48); root.add(flangeFront);

                // 4 Tirantes externos M14 de aço forjado com porcas sextavadas pesadas
                [[-0.22, -0.05], [0.22, -0.05], [-0.22, -0.49], [0.22, -0.49]].forEach(([tx, ty]) => {
                    const tieRod = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.022, 1.12, 12), matMetal);
                    tieRod.rotation.x = Math.PI / 2; tieRod.position.set(tx, ty, 0); root.add(tieRod);
                    [-0.56, 0.56].forEach(nz => {
                        const nut = new THREE.Mesh(new THREE.CylinderGeometry(0.038, 0.038, 0.04, 6), matMetal);
                        nut.rotation.x = Math.PI / 2; nut.position.set(tx, ty, nz); root.add(nut);
                    });
                });

                // Colar guia de bronze sinterizado / retentor raspador de alta pressão
                const gland = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.17, 0.10, 20), new THREE.MeshStandardMaterial({ color: 0xD97706, metalness: 0.9, roughness: 0.25 }));
                gland.rotation.x = Math.PI / 2; gland.position.set(0, -0.27, 0.58); root.add(gland);

                // Pórticos rosqueados sextavados de alta pressão SAE/BSP nos topos
                [-0.48, 0.48].forEach(pz => {
                    const bspPort = new THREE.Mesh(new THREE.CylinderGeometry(0.065, 0.065, 0.08, 6), matDarkMetal);
                    bspPort.position.set(0, -0.01, pz); root.add(bspPort);
                });
            }

            // Haste do cilindro industrial retificada e cromada (comprimento aumentado para 1.10)
            const rodLength = 1.10;
            const rodRadius = isHydra ? 0.082 : 0.065;
            const rod = new THREE.Mesh(
                new THREE.CylinderGeometry(rodRadius, rodRadius, rodLength, 20),
                new THREE.MeshStandardMaterial({ color: 0xF8FAFC, metalness: 0.98, roughness: 0.06 })
            );
            rod.rotation.x = Math.PI / 2;
            rod.position.set(0, -0.27, 0.45);
            root.add(rod);
            root.userData.pistonRod = rod;

            // Bucha sextavada de acoplamento e contraporca M16 (integração sólida sem vão)
            const nutLength = 0.06;
            const nut = new THREE.Mesh(
                new THREE.CylinderGeometry(rodRadius + 0.025, rodRadius + 0.025, nutLength, 6),
                matDarkMetal
            );
            nut.rotation.x = Math.PI / 2;
            nut.position.set(0, -0.27, 0.45 + (rodLength / 2) - 0.01);
            root.add(nut);
            root.userData.pistonNut = nut;

            // Ponteira / Olhal do pistão (com sobreposição de 35mm para eliminar qualquer vão em qualquer ângulo)
            const headLength = 0.16;
            const headRadius = isHydra ? 0.15 : 0.135;
            const headGeo = new THREE.CylinderGeometry(headRadius, headRadius, headLength, 20);
            const head = new THREE.Mesh(headGeo, matDarkMetal);
            head.rotation.x = Math.PI / 2;
            head.position.set(0, -0.27, 0.45 + (rodLength / 2) + (headLength / 2) - 0.035);
            root.add(head);
            root.userData.pistonHead = head;

            const rail = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.06, 0.9), matDarkMetal);
            rail.position.set(-0.25, -0.27, 0); root.add(rail);

            const p0 = typeof data.posS0 === 'number' ? data.posS0 : 0.05;
            const p1 = typeof data.posS1 === 'number' ? data.posS1 : 0.95;
            const z0 = -0.38 + p0 * 0.76;
            const z1 = -0.38 + p1 * 0.76;

            const s0Mesh = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.09, 0.14), new THREE.MeshStandardMaterial({ color: 0xef4444, emissive: 0xef4444, emissiveIntensity: 1.2 }));
            s0Mesh.position.set(-0.25, -0.27, z0);
            s0Mesh.userData = { isSensor: true, sensorId: 'S0' };
            root.add(s0Mesh); root.userData.s0 = s0Mesh;

            const s1Mesh = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.09, 0.14), new THREE.MeshStandardMaterial({ color: 0x334155 }));
            s1Mesh.position.set(-0.25, -0.27, z1);
            s1Mesh.userData = { isSensor: true, sensorId: 'S1' };
            root.add(s1Mesh); root.userData.s1 = s1Mesh;

            const localPins = core.getLocalComponentPins(data.type, data.inputCount, p0, p1);
            attachPinsToMesh(root, localPins, matMetal);
            root.add(createBoxHighlightMesh(1.1, 1.4, 0));

        } else if (data.type === core.COMP.LAMP || data.type === core.COMP.LED_RED || data.type === core.COMP.BUZZER) {
            const isRed = (data.type === core.COMP.LED_RED);
            const isLamp = (data.type === core.COMP.LAMP);
            const isBuzzer = (data.type === core.COMP.BUZZER);

            if (isBuzzer) {
                // --- ALARME ACÚSTICO / SIRENE PIEZOELÉTRICA INDUSTRIAL (Werma/Mallory Style) ---
                // Base de montagem robusta com carcaça sólida sob os bornes (elimina bornes flutuantes)
                const bzBase = new THREE.Mesh(
                    new THREE.BoxGeometry(0.72, 0.10, 0.74),
                    new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.85, metalness: 0.2 })
                );
                bzBase.position.set(0, -0.45, 0.05);
                bzBase.castShadow = true;
                root.add(bzBase);

                // Carcaça cônica acústica tipo corneta industrial
                const bzHorn = new THREE.Mesh(
                    new THREE.CylinderGeometry(0.34, 0.26, 0.20, 24),
                    new THREE.MeshStandardMaterial({ color: 0x1E293B, roughness: 0.7, metalness: 0.3 })
                );
                bzHorn.position.set(0, -0.25, -0.06);
                bzHorn.castShadow = true;
                root.add(bzHorn);

                // Anel chanfrado externo de proteção acústica
                const bzRim = new THREE.Mesh(
                    new THREE.TorusGeometry(0.32, 0.035, 12, 32),
                    matDarkMetal
                );
                bzRim.rotation.x = Math.PI / 2;
                bzRim.position.set(0, -0.15, -0.06);
                root.add(bzRim);

                // Diafragma ressonador metálico central visível
                const bzDiaphragm = new THREE.Mesh(
                    new THREE.CylinderGeometry(0.20, 0.20, 0.015, 24),
                    new THREE.MeshStandardMaterial({ color: 0xD97706, metalness: 0.9, roughness: 0.2 })
                );
                bzDiaphragm.position.set(0, -0.21, -0.06);
                root.add(bzDiaphragm);

                // Grelha frontal com aletas de dispersão sonora radiais
                for (let a = 0; a < 6; a++) {
                    const ang = (a * Math.PI) / 3;
                    const slit = new THREE.Mesh(
                        new THREE.BoxGeometry(0.02, 0.025, 0.26),
                        matDarkMetal
                    );
                    slit.rotation.y = ang;
                    slit.position.set(0, -0.15, -0.06);
                    root.add(slit);
                }

                // Mini-LED central indicador de pulso sonoro
                const bLed = new THREE.Mesh(
                    new THREE.CylinderGeometry(0.04, 0.04, 0.03, 16),
                    new THREE.MeshStandardMaterial({ color: 0x1E293B, emissive: 0x000000, roughness: 0.2 })
                );
                bLed.position.set(0, -0.14, -0.06);
                root.add(bLed);
                root.userData.lens = bLed;

                // Prateleira sólida e barreira isolante sob os bornes traseiros X1 e X2 (elimina bornes flutuantes!)
                const bzTerminalShelf = new THREE.Mesh(
                    new THREE.BoxGeometry(0.66, 0.26, 0.32),
                    new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.85 })
                );
                bzTerminalShelf.position.set(0, -0.28, 0.28);
                bzTerminalShelf.castShadow = true;
                root.add(bzTerminalShelf);

                // Divisória isolante entre X1 e X2
                const bzFin = new THREE.Mesh(
                    new THREE.BoxGeometry(0.03, 0.16, 0.28),
                    new THREE.MeshStandardMaterial({ color: 0x020617, roughness: 0.9 })
                );
                bzFin.position.set(0, -0.18, 0.28);
                root.add(bzFin);

            } else {
                // --- SINALIZADOR LUMINOSO / LÂMPADA PILOTO 22mm (Schneider Harmony Style) ---
                // Flange e base de montagem no painel
                const base = new THREE.Mesh(
                    new THREE.BoxGeometry(0.72, 0.10, 0.74), 
                    new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.8, metalness: 0.15 })
                );
                base.position.set(0, -0.45, 0.05);
                base.castShadow = true; 
                root.add(base);

                // Colar cilíndrico de 22mm
                const collar22 = new THREE.Mesh(
                    new THREE.CylinderGeometry(0.34, 0.34, 0.12, 24),
                    new THREE.MeshStandardMaterial({ color: 0x1E293B, roughness: 0.7 })
                );
                collar22.position.set(0, -0.36, -0.06);
                root.add(collar22);

                // Aro frontal cromado biselado (Padrão Industrial 22mm usinado)
                const chromeBezel = new THREE.Mesh(
                    new THREE.CylinderGeometry(0.30, 0.33, 0.08, 32),
                    new THREE.MeshStandardMaterial({ color: 0xF1F5F9, metalness: 0.95, roughness: 0.12 })
                );
                chromeBezel.position.set(0, -0.28, -0.06);
                chromeBezel.castShadow = true;
                root.add(chromeBezel);

                // Anel interno de vedação / O-Ring
                const oRing = new THREE.Mesh(
                    new THREE.TorusGeometry(0.28, 0.02, 8, 32),
                    new THREE.MeshStandardMaterial({ color: 0x020617, roughness: 0.9 })
                );
                oRing.rotation.x = Math.PI / 2;
                oRing.position.set(0, -0.24, -0.06);
                root.add(oRing);

                // Calota esférica translúcida de 22mm com lente facetada
                const lensColor = isRed ? 0x7F1D1D : 0x854D0E;
                const bulb = new THREE.Mesh(
                    new THREE.SphereGeometry(0.24, 24, 16, 0, Math.PI * 2, 0, Math.PI * 0.5),
                    new THREE.MeshPhysicalMaterial({ 
                        color: lensColor, 
                        transmission: 0.85, 
                        opacity: 1.0, 
                        roughness: 0.15, 
                        metalness: 0.08,
                        transparent: true,
                        emissive: 0x000000,
                        emissiveIntensity: 0.0
                    })
                );
                bulb.position.set(0, -0.24, -0.06);
                bulb.castShadow = true;
                root.add(bulb);
                root.userData.lens = bulb;

                const pl = new THREE.PointLight(isRed ? 0xEF4444 : 0xFACC15, 0, 3.5);
                pl.position.set(0, -0.10, -0.06);
                root.add(pl);
                root.userData.pilotLight = pl;

                // Bloco traseiro de terminais sólido até a bancada (elimina bornes flutuantes!)
                const terminalBlock = new THREE.Mesh(
                    new THREE.BoxGeometry(0.66, 0.26, 0.32),
                    new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.85 })
                );
                terminalBlock.position.set(0, -0.28, 0.28);
                terminalBlock.castShadow = true;
                root.add(terminalBlock);
            }

            // Bornes traseiros visíveis e perfeitamente assentados sobre a carcaça traseira
            createScrewTerminalMesh(-0.22, -0.15, 0.28, 'X1', false, 'X1', root, matMetal, matDarkMetal);
            createScrewTerminalMesh( 0.22, -0.15, 0.28, 'X2', false, 'X2', root, matMetal, matDarkMetal);

            if (root.userData.pinElements['X1']) {
                root.userData.pinElements['A1'] = root.userData.pinElements['X1'];
                root.userData.pinElements['1'] = root.userData.pinElements['X1'];
            }
            if (root.userData.pinElements['X2']) {
                root.userData.pinElements['A2'] = root.userData.pinElements['X2'];
                root.userData.pinElements['2'] = root.userData.pinElements['X2'];
                root.userData.pinElements['GND'] = root.userData.pinElements['X2'];
            }

            root.add(createBoxHighlightMesh(0.85, 0.85, 0));
        } else if (data.type === core.COMP.SUPPLY_PNEU || data.type === core.COMP.SUPPLY_HYDRA) {
            if (data.type === core.COMP.SUPPLY_PNEU) {
                // --- UNIDADE DE CONSERVAÇÃO PNEUMÁTICA FRL (6 bar) ---
                const frlChassis = new THREE.Mesh(
                    new THREE.BoxGeometry(0.88, 0.38, 0.88),
                    new THREE.MeshStandardMaterial({ color: 0x334155, roughness: 0.65, metalness: 0.4 })
                );
                frlChassis.position.y = -0.33; frlChassis.castShadow = true;
                frlChassis.userData = { isSupplyUnit: true };
                root.add(frlChassis);

                // Manípulo regulador de pressão no topo
                const regKnob = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.14, 0.10, 24), matDarkMetal);
                regKnob.position.set(-0.25, -0.10, 0.1); root.add(regKnob);

                // Copo 1: Filtro de Policarbonato com Elemento de Bronze Sinterizado (#B45309)
                const filterGlass = new THREE.Mesh(
                    new THREE.CylinderGeometry(0.18, 0.16, 0.44, 24),
                    new THREE.MeshPhysicalMaterial({ color: 0xFFFFFF, transmission: 0.92, opacity: 1, roughness: 0.1, ior: 1.5, transparent: true })
                );
                filterGlass.position.set(-0.25, -0.70, 0.1);
                root.add(filterGlass);

                const filterElement = new THREE.Mesh(
                    new THREE.CylinderGeometry(0.10, 0.10, 0.32, 16),
                    new THREE.MeshStandardMaterial({ color: 0xB45309, roughness: 0.9, metalness: 0.35 })
                );
                filterElement.position.set(-0.25, -0.70, 0.1);
                root.add(filterElement);

                // Válvula manual de dreno de condensado na ponta do filtro
                const drainValv = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.08, 12), matDarkMetal);
                drainValv.position.set(-0.25, -0.96, 0.1);
                root.add(drainValv);

                // Copo 2: Lubrificador de Policarbonato com Óleo Mineral Amarelo Claro e Tubo Capilar
                const lubGlass = new THREE.Mesh(
                    new THREE.CylinderGeometry(0.18, 0.16, 0.44, 24),
                    new THREE.MeshPhysicalMaterial({ color: 0xFFFFFF, transmission: 0.92, opacity: 1, roughness: 0.1, ior: 1.5, transparent: true })
                );
                lubGlass.position.set(0.25, -0.70, 0.1);
                root.add(lubGlass);

                const lubOil = new THREE.Mesh(
                    new THREE.CylinderGeometry(0.16, 0.14, 0.22, 24),
                    new THREE.MeshPhysicalMaterial({ color: 0xFEF08A, transmission: 0.85, opacity: 0.75, roughness: 0.2, transparent: true })
                );
                lubOil.position.set(0.25, -0.79, 0.1);
                root.add(lubOil);

                // Tubo capilar de sucção vertical
                const capillaryTube = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.34, 12), matMetal);
                capillaryTube.position.set(0.25, -0.73, 0.1);
                root.add(capillaryTube);

                // Manômetro Cromado Saliente com dial graduado de 0 a 10 bar
                const gGroup2 = new THREE.Group(); gGroup2.position.set(-0.2, -0.15, -0.45); gGroup2.rotation.x = -Math.PI / 2;
                const gBase2 = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.24, 0.05, 32), new THREE.MeshStandardMaterial({ color: 0xCBD5E1, metalness: 0.92, roughness: 0.15 }));
                gBase2.rotation.x = Math.PI / 2; gGroup2.add(gBase2);
                
                const cGauge = document.createElement('canvas'); cGauge.width = 256; cGauge.height = 256;
                const ctxG = cGauge.getContext('2d');
                ctxG.fillStyle = '#FFFFFF'; ctxG.fillRect(0, 0, 256, 256);
                ctxG.beginPath(); ctxG.arc(128, 128, 114, 0, Math.PI * 2); ctxG.lineWidth = 6; ctxG.strokeStyle = '#000'; ctxG.stroke();
                // Faixas coloridas: Verde (0-6 bar), Amarelo (6-8 bar), Vermelho (8-10 bar)
                ctxG.beginPath(); ctxG.arc(128, 128, 100, Math.PI * 0.75, Math.PI * 1.65); ctxG.lineWidth = 12; ctxG.strokeStyle = '#22C55E'; ctxG.stroke();
                ctxG.beginPath(); ctxG.arc(128, 128, 100, Math.PI * 1.65, Math.PI * 1.95); ctxG.lineWidth = 12; ctxG.strokeStyle = '#EAB308'; ctxG.stroke();
                ctxG.beginPath(); ctxG.arc(128, 128, 100, Math.PI * 1.95, Math.PI * 2.25); ctxG.lineWidth = 12; ctxG.strokeStyle = '#EF4444'; ctxG.stroke();
                ctxG.fillStyle = '#0F172A'; ctxG.font = 'bold 26px sans-serif'; ctxG.textAlign = 'center'; ctxG.fillText('0..10 bar', 128, 175);
                
                const gFace2 = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.06, 32), new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(cGauge) }));
                gFace2.rotation.x = -Math.PI / 2; gFace2.rotation.z = Math.PI; gGroup2.add(gFace2);
                
                const needle2 = new THREE.Mesh(new THREE.BoxGeometry(0.015, 0.01, 0.18), new THREE.MeshBasicMaterial({ color: 0xDC2626 }));
                needle2.position.set(0, 0, 0.04); needle2.rotation.z = 0.85; gGroup2.add(needle2);
                
                const gLens = new THREE.Mesh(new THREE.CylinderGeometry(0.23, 0.23, 0.08, 32), new THREE.MeshPhysicalMaterial({ color: 0xFFFFFF, transmission: 0.9, opacity: 1, transparent: true }));
                gLens.rotation.x = Math.PI / 2; gGroup2.add(gLens);
                root.add(gGroup2); root.userData.needle = needle2;

                // Alavanca de acionamento manual
                const leverGroup = new THREE.Group(); leverGroup.position.set(0.2, -0.10, 0.2);
                const leverBase = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.08, 24), new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.9 }));
                const leverHandle = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.16, 0.28), new THREE.MeshStandardMaterial({ color: 0xDC2626, roughness: 0.5 }));
                leverHandle.position.y = 0.08;
                leverGroup.add(leverBase); leverGroup.add(leverHandle); root.add(leverGroup);
                leverGroup.userData = { isSupplyUnit: true };
                root.userData.lever = leverGroup;
                root.userData.valveHandle = leverGroup;

                // Niple de saída 1(P) em latão sextavado
                const brassPort = new THREE.Mesh(
                    new THREE.CylinderGeometry(0.08, 0.08, 0.12, 16),
                    new THREE.MeshStandardMaterial({ color: 0xD97706, roughness: 0.3, metalness: 0.85 })
                );
                brassPort.rotation.x = Math.PI / 2;
                brassPort.position.set(0, -0.32, 0.48);
                root.add(brassPort);

                const lblPort = createPinLabel('1(P)', true);
                lblPort.position.set(0, -0.22, 0.48);
                root.add(lblPort);

                const localPins = core.getLocalComponentPins(data.type);
                attachPinsToMesh(root, localPins, matMetal);
                root.add(createBoxHighlightMesh(0.98, 0.98, 0));

            } else if (data.type === core.COMP.SUPPLY_HYDRA) {
                // --- UNIDADE HIDRÁULICA HPU (120 bar) ---
                const tankBody = new THREE.Mesh(
                    new THREE.BoxGeometry(1.20, 0.48, 1.20),
                    new THREE.MeshStandardMaterial({ color: 0x1E293B, roughness: 0.7, metalness: 0.3 })
                );
                tankBody.position.y = -0.24; tankBody.castShadow = true;
                tankBody.userData = { isSupplyUnit: true };
                root.add(tankBody);

                // Bocal superior com respiro cromado
                const fillerNeck = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.12, 24), new THREE.MeshStandardMaterial({ color: 0xCBD5E1, metalness: 0.92, roughness: 0.2 }));
                fillerNeck.position.set(-0.40, 0.06, 0.35); root.add(fillerNeck);
                const fillerCap = new THREE.Mesh(new THREE.CylinderGeometry(0.10, 0.10, 0.05, 24), new THREE.MeshStandardMaterial({ color: 0xCBD5E1, metalness: 0.88, roughness: 0.25 }));
                fillerCap.position.set(-0.40, 0.13, 0.35); root.add(fillerCap);

                // Visor vertical de nível de fluido na face frontal
                const sightFrame = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.28, 0.08), new THREE.MeshStandardMaterial({ color: 0xCBD5E1, metalness: 0.85, roughness: 0.25 }));
                sightFrame.position.set(-0.61, -0.24, 0); root.add(sightFrame);
                const sightGlass = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.24, 0.05), new THREE.MeshPhysicalMaterial({ color: 0xD97706, transmission: 0.85, opacity: 0.8, roughness: 0.1, transparent: true }));
                sightGlass.position.set(-0.62, -0.24, 0); root.add(sightGlass);

                // Motor elétrico azul WEG (#1D4ED8) com aletas usinadas e caixa de ligação
                const motorGroup = new THREE.Group(); motorGroup.position.set(0, 0.32, -0.15);
                const matWeg = new THREE.MeshStandardMaterial({ color: 0x1D4ED8, roughness: 0.55, metalness: 0.25 });
                const motorBody = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.70, 24), matWeg);
                motorBody.rotation.z = Math.PI / 2; motorGroup.add(motorBody);
                for(let i = 0; i < 9; i++) {
                    const fin = new THREE.Mesh(new THREE.CylinderGeometry(0.245, 0.245, 0.02, 24), matWeg);
                    fin.rotation.z = Math.PI / 2; fin.position.x = -0.25 + (i * 0.06); motorGroup.add(fin);
                }
                const motorBox = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.12, 0.18), new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.8 }));
                motorBox.position.set(0, 0.24, 0); motorGroup.add(motorBox);
                root.add(motorGroup);

                // Painel Frontal (Botoeiras e Sinaleiro)
                const panel = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.18, 0.04), new THREE.MeshStandardMaterial({ color: 0x0F172A }));
                panel.position.set(0, -0.24, 0.61); root.add(panel);
                const btnStart = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.06, 16), new THREE.MeshStandardMaterial({ color: 0x22C55E }));
                btnStart.rotation.x = Math.PI / 2; btnStart.position.set(-0.12, -0.24, 0.63);
                btnStart.userData = { isSupplyUnit: true };
                root.add(btnStart);
                const btnStop = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.06, 16), new THREE.MeshStandardMaterial({ color: 0xEF4444 }));
                btnStop.rotation.x = Math.PI / 2; btnStop.position.set(0, -0.24, 0.63);
                btnStop.userData = { isSupplyUnit: true };
                root.add(btnStop);
                const lamp = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.06, 16), new THREE.MeshPhysicalMaterial({ color: 0xCA8A04, transmission: 0.9 }));
                lamp.rotation.x = Math.PI / 2; lamp.position.set(0.12, -0.24, 0.63); root.add(lamp);
                root.userData.runLed = lamp;
                
                // Manômetro Hidráulico (0 a 160 bar)
                const gGroup = new THREE.Group(); gGroup.position.set(-0.25, -0.32, 0.61);
                const gBase = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.02, 32), matMetal);
                gBase.rotation.x = Math.PI / 2; gGroup.add(gBase);
                const gFace = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.03, 32), new THREE.MeshBasicMaterial({ color: 0xFFFFFF }));
                gFace.rotation.x = Math.PI / 2; gGroup.add(gFace);
                const needle = new THREE.Mesh(new THREE.BoxGeometry(0.01, 0.005, 0.08), new THREE.MeshBasicMaterial({ color: 0xDC2626 }));
                needle.position.set(0, 0, 0.02); needle.rotation.z = 0.85; gGroup.add(needle);
                root.add(gGroup); root.userData.needle = needle;

                // Bloco de pórticos com niples de aço cromado P (pressão) e T (retorno)
                const portP = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.12, 16), matDarkMetal);
                portP.rotation.x = Math.PI / 2; portP.position.set(-0.30, -0.30, 0.60); root.add(portP);
                const lblP = createPinLabel('P', true); lblP.position.set(-0.30, -0.20, 0.60); root.add(lblP);

                const portT = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.12, 16), matDarkMetal);
                portT.rotation.x = Math.PI / 2; portT.position.set(0.30, -0.30, 0.60); root.add(portT);
                const lblT = createPinLabel('T', true); lblT.position.set(0.30, -0.20, 0.60); root.add(lblT);

                const localPins = core.getLocalComponentPins(data.type);
                attachPinsToMesh(root, localPins, matMetal);
                root.add(createBoxHighlightMesh(1.25, 1.25, 0));
            }
        } else if (data.type === core.COMP.CONTACTOR_3P) {
            // --- CONTATOR TRIPOLAR DE POTÊNCIA (Siemens Sirius / Schneider TeSys Style) ---
            // 1. Base Trilho DIN industrial (1.30 x 0.20 x 1.30)
            const dinBase = new THREE.Mesh(
                new THREE.BoxGeometry(1.30, 0.20, 1.30),
                new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.85, metalness: 0.2 })
            );
            dinBase.position.y = -0.40;
            dinBase.castShadow = true;
            root.add(dinBase);

            // Trava plástica amarela de engate rápido trilho DIN
            const dinLatch = new THREE.Mesh(
                new THREE.BoxGeometry(0.18, 0.08, 0.28),
                new THREE.MeshStandardMaterial({ color: 0xF59E0B, roughness: 0.5 })
            );
            dinLatch.position.set(0, -0.46, -0.56);
            root.add(dinLatch);

            // 2. Carcaça central cinza industrial robusta (Siemens Sirius / TeSys)
            const mainCase = new THREE.Mesh(
                new THREE.BoxGeometry(1.16, 0.38, 0.88),
                new THREE.MeshStandardMaterial({ color: 0x334155, roughness: 0.6, metalness: 0.3 })
            );
            mainCase.position.set(0, -0.16, 0);
            mainCase.castShadow = true;
            root.add(mainCase);

            // Prateleiras de bornes escalonadas (Shelves) que apoiam solidamente os bornes aéreos/parafusos em Z = +-0.50
            const topShelf = new THREE.Mesh(
                new THREE.BoxGeometry(1.16, 0.22, 0.24),
                new THREE.MeshStandardMaterial({ color: 0x1E293B, roughness: 0.7 })
            );
            topShelf.position.set(0, -0.26, -0.50);
            topShelf.castShadow = true;
            root.add(topShelf);

            const btmShelf = new THREE.Mesh(
                new THREE.BoxGeometry(1.16, 0.22, 0.24),
                new THREE.MeshStandardMaterial({ color: 0x1E293B, roughness: 0.7 })
            );
            btmShelf.position.set(0, -0.26, 0.50);
            btmShelf.castShadow = true;
            root.add(btmShelf);

            // Divisórias plásticas de barreira entre fases (L1, L2, L3, 13, A1)
            [-0.33, -0.11, 0.11, 0.33].forEach(bx => {
                [-0.50, 0.50].forEach(bz => {
                    const barrier = new THREE.Mesh(
                        new THREE.BoxGeometry(0.02, 0.14, 0.22),
                        new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.85 })
                    );
                    barrier.position.set(bx, -0.22, bz);
                    root.add(barrier);
                });
            });

            // Frisos laterais de dissipação e ventilação
            [-0.59, 0.59].forEach(sideX => {
                for (let i = -2; i <= 2; i++) {
                    const rib = new THREE.Mesh(
                        new THREE.BoxGeometry(0.02, 0.04, 0.70),
                        new THREE.MeshStandardMaterial({ color: 0x1E293B, roughness: 0.9 })
                    );
                    rib.position.set(sideX, -0.16 + i * 0.07, 0);
                    root.add(rib);
                }
            });

            // 3. Rebaixo frontal e carretel mecânico (Mechanical Plunger) que afunda quando energizado
            const cavity = new THREE.Mesh(
                new THREE.BoxGeometry(0.36, 0.12, 0.36),
                new THREE.MeshStandardMaterial({ color: 0x020617, roughness: 0.9 })
            );
            cavity.position.set(0, -0.06, 0);
            root.add(cavity);

            // Êmbolo indicador móvel laranja/azul
            const plunger = new THREE.Mesh(
                new THREE.BoxGeometry(0.30, 0.08, 0.30),
                new THREE.MeshStandardMaterial({ color: 0xEA580C, roughness: 0.4, metalness: 0.3 })
            );
            plunger.position.set(0, -0.04, 0);
            root.add(plunger);
            root.userData.plunger = plunger;

            // 4. LED piloto de energização da bobina A1/A2
            const coilLedBezel = new THREE.Mesh(
                new THREE.CylinderGeometry(0.05, 0.05, 0.02, 16),
                matDarkMetal
            );
            coilLedBezel.position.set(-0.35, -0.03, -0.25);
            root.add(coilLedBezel);

            const coilLed = new THREE.Mesh(
                new THREE.CylinderGeometry(0.038, 0.038, 0.025, 16),
                new THREE.MeshStandardMaterial({ color: 0x052E16, roughness: 0.4 })
            );
            coilLed.position.set(-0.35, -0.02, -0.25);
            root.add(coilLed);
            root.userData.coilLed = coilLed;

            // 5. Plaqueta serigráfica técnica superior
            const cvLabel = document.createElement('canvas');
            cvLabel.width = 256; cvLabel.height = 128;
            const ctxL = cvLabel.getContext('2d');
            ctxL.fillStyle = '#E2E8F0'; ctxL.fillRect(0, 0, 256, 128);
            ctxL.strokeStyle = '#0F172A'; ctxL.lineWidth = 3; ctxL.strokeRect(4, 4, 248, 120);
            ctxL.fillStyle = '#0F172A'; ctxL.font = 'bold 22px monospace'; ctxL.textAlign = 'center';
            ctxL.fillText('K1 - CONTATOR 3P', 128, 28);
            ctxL.fillStyle = '#0284C7'; ctxL.font = 'bold 14px monospace';
            ctxL.fillText('FORÇA & COMANDO: L1 - L2 - L3 | N', 128, 52);
            ctxL.fillStyle = '#0369A1'; ctxL.font = 'bold 13px sans-serif';
            ctxL.fillText('TOP: A1 | L1 | L2 | L3 | 13(NO)', 128, 80);
            ctxL.fillStyle = '#DC2626'; ctxL.font = 'bold 13px sans-serif';
            ctxL.fillText('BOT: A2 | T1 | T2 | T3 | 14(NO)', 128, 108);

            const labelTex = new THREE.CanvasTexture(cvLabel);
            const labelPlate = new THREE.Mesh(
                new THREE.PlaneGeometry(0.64, 0.32),
                new THREE.MeshStandardMaterial({ map: labelTex, roughness: 0.4 })
            );
            labelPlate.rotation.x = -Math.PI / 2;
            labelPlate.position.set(0, 0.035, 0.0);
            root.add(labelPlate);

            const localPins = core.getLocalComponentPins(data.type);
            attachPinsToMesh(root, localPins, matMetal);
            root.add(createBoxHighlightMesh(1.40, 1.40, 0));

        } else if (data.type === core.COMP.POWER_SUPPLY_3P) {
            // --- FONTE DE ALIMENTAÇÃO TRIFÁSICA & PROTEÇÃO PE (380V/220V 60Hz) ---
            // Gabinete industrial padrão bancada didática
            const chassis = new THREE.Mesh(
                new THREE.BoxGeometry(1.42, 0.38, 1.20),
                new THREE.MeshStandardMaterial({ color: 0x334155, roughness: 0.65, metalness: 0.4 })
            );
            chassis.position.y = -0.31;
            chassis.castShadow = true;
            root.add(chassis);

            // Rebaixo superior para painel serigráfico
            const panelBed = new THREE.Mesh(
                new THREE.BoxGeometry(1.36, 0.04, 1.14),
                new THREE.MeshStandardMaterial({ color: 0x1E293B, roughness: 0.8 })
            );
            panelBed.position.y = -0.11;
            root.add(panelBed);

            // 1. Chave Seletora Geral Rotativa (Rotary Switch) Liga/Desliga
            const swBase = new THREE.Mesh(
                new THREE.CylinderGeometry(0.12, 0.12, 0.04, 24),
                matDarkMetal
            );
            swBase.position.set(-0.42, -0.08, -0.22);
            root.add(swBase);

            const knobGroup = new THREE.Group();
            knobGroup.position.set(-0.42, -0.06, -0.22);
            const knobBody = new THREE.Mesh(
                new THREE.CylinderGeometry(0.08, 0.08, 0.08, 16),
                new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.7 })
            );
            knobGroup.add(knobBody);
            const knobPointer = new THREE.Mesh(
                new THREE.BoxGeometry(0.03, 0.06, 0.14),
                new THREE.MeshStandardMaterial({ color: 0xDC2626, roughness: 0.4 })
            );
            knobPointer.position.set(0, 0.02, 0.03);
            knobGroup.add(knobPointer);
            root.add(knobGroup);
            root.userData.mainSwitchKnob = knobGroup;

            // 2. Lâmpadas Piloto de Fase Ativa (R, S, T)
            const pilotConfigs = [
                { id: 'pilotR', color: 0xEF4444, x: -0.05, label: 'L1(R)' },
                { id: 'pilotS', color: 0xF59E0B, x: 0.12,  label: 'L2(S)' },
                { id: 'pilotT', color: 0x22C55E, x: 0.29,  label: 'L3(T)' }
            ];

            pilotConfigs.forEach(pc => {
                const pBezel = new THREE.Mesh(
                    new THREE.CylinderGeometry(0.06, 0.06, 0.03, 16),
                    matDarkMetal
                );
                pBezel.position.set(pc.x, -0.08, -0.22);
                root.add(pBezel);

                const pLens = new THREE.Mesh(
                    new THREE.CylinderGeometry(0.045, 0.045, 0.04, 16),
                    new THREE.MeshStandardMaterial({
                        color: pc.color,
                        emissive: pc.color,
                        emissiveIntensity: 0.0,
                        roughness: 0.3
                    })
                );
                pLens.position.set(pc.x, -0.06, -0.22);
                root.add(pLens);
                root.userData[pc.id] = pLens;
            });

            // 3. Serigrafia nítida do painel frontal
            const cvPs = document.createElement('canvas');
            cvPs.width = 512; cvPs.height = 128;
            const ctxPs = cvPs.getContext('2d');
            ctxPs.fillStyle = '#0F172A'; ctxPs.fillRect(0, 0, 512, 128);
            ctxPs.strokeStyle = '#38BDF8'; ctxPs.lineWidth = 4; ctxPs.strokeRect(6, 6, 500, 116);
            ctxPs.fillStyle = '#F8FAFC'; ctxPs.font = 'bold 22px sans-serif'; ctxPs.textAlign = 'center';
            ctxPs.fillText('REDE INDUSTRIAL TRIFÁSICA — 380V / 220V', 256, 36);
            ctxPs.fillStyle = '#38BDF8'; ctxPs.font = 'bold 15px monospace';
            ctxPs.fillText('ALIMENTAÇÃO & PROTEÇÃO DE POTÊNCIA (NR-10)', 256, 66);
            ctxPs.fillStyle = '#E2E8F0'; ctxPs.font = '13px monospace';
            ctxPs.fillText('CHAVE GERAL | FASES R-S-T | N | PE TERRA', 256, 96);

            const psTex = new THREE.CanvasTexture(cvPs);
            const psPlate = new THREE.Mesh(
                new THREE.PlaneGeometry(1.20, 0.30),
                new THREE.MeshStandardMaterial({ map: psTex, roughness: 0.5 })
            );
            psPlate.rotation.x = -Math.PI / 2;
            psPlate.position.set(0, -0.08, 0.05);
            root.add(psPlate);

            const localPins = core.getLocalComponentPins(data.type);
            attachPinsToMesh(root, localPins, matMetal);
            root.add(createBoxHighlightMesh(1.50, 1.30, 0));
        }
        
        root.traverse(child => {
            if (child.isMesh && !child.userData.isPin && !child.userData.isLabel) {
                child.castShadow = true;
                child.receiveShadow = true;
            }
        });
        root.rotation.y = data.rot * (Math.PI / 2);
        return root;
    }

    function updateWireGeometry(node) {
        if (!node || !node.mesh) return;
        if (node.type !== core.COMP.WIRE && node.type !== 'JUMPER') return;
        
        const group = node.mesh;
        while (group.children.length > 0) {
            const child = group.children[0];
            if (child.geometry && child.geometry !== pinHitboxGeo) child.geometry.dispose();
            if (child.material) {
                const mats = Array.isArray(child.material) ? child.material : [child.material];
                mats.forEach(m => {
                    if (!isCachedWireMaterial(m)) {
                        if (m.map && m.map.dispose) m.map.dispose();
                        m.dispose();
                    }
                });
            }
            group.remove(child);
        }

        if (node.type === 'JUMPER') {
            const basePlastica = new THREE.Mesh(
                new THREE.BoxGeometry(0.36, 0.03, 0.36),
                matJumperBase
            );
            basePlastica.position.y = -0.485;
            group.add(basePlastica);

            const matNS = getWireMaterial(node.colorNS || COLOR_LOW, node.stateNS);
            const matEW = getWireMaterial(node.colorEW || COLOR_LOW, node.stateEW);

            const segEW = new THREE.Mesh(new THREE.BoxGeometry(WIRE_LENGTH, WIRE_HEIGHT, WIRE_WIDTH), matEW);
            segEW.position.set(0, WIRE_Y, 0); group.add(segEW);

            const bridgeElevation = WIRE_Y + 0.12;
            const segNSTopo = new THREE.Mesh(new THREE.BoxGeometry(WIRE_WIDTH, WIRE_HEIGHT, WIRE_LENGTH), matNS);
            segNSTopo.position.set(0, bridgeElevation, 0); group.add(segNSTopo);

            const pilarN = new THREE.Mesh(new THREE.BoxGeometry(WIRE_WIDTH, 0.12, WIRE_WIDTH), matNS);
            pilarN.position.set(0, WIRE_Y + 0.06, -WIRE_LENGTH / 2 + WIRE_WIDTH / 2); group.add(pilarN);

            const pilarS = new THREE.Mesh(new THREE.BoxGeometry(WIRE_WIDTH, 0.12, WIRE_WIDTH), matNS);
            pilarS.position.set(0, WIRE_Y + 0.06, WIRE_LENGTH / 2 - WIRE_WIDTH / 2); group.add(pilarS);

            const hasDockedAirWire = state.airWires && state.airWires.some(w =>
                (Math.abs(w.from.nodeX - node.x) < 0.1 && Math.abs(w.from.nodeZ - node.z) < 0.1) ||
                (Math.abs(w.to.nodeX - node.x) < 0.1 && Math.abs(w.to.nodeZ - node.z) < 0.1)
            );
            if (hasDockedAirWire) {
                const sBase = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.16, 0.04, 24), new THREE.MeshStandardMaterial({ color: 0xF59E0B, metalness: 0.85, roughness: 0.25 }));
                sBase.position.set(0, bridgeElevation + 0.02, 0); group.add(sBase);
                const sCollar = new THREE.Mesh(new THREE.CylinderGeometry(0.10, 0.10, 0.05, 24), new THREE.MeshStandardMaterial({ color: 0xCBD5E1, metalness: 0.9, roughness: 0.2 }));
                sCollar.position.set(0, bridgeElevation + 0.045, 0); group.add(sCollar);
                const sHole = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.055, 16), new THREE.MeshBasicMaterial({ color: 0x020617 }));
                sHole.position.set(0, bridgeElevation + 0.05, 0); group.add(sHole);
            }

            const jHitbox = new THREE.Mesh(pinHitboxGeo, pinHitboxMat);
            jHitbox.position.set(0, WIRE_Y + 0.06, 0);
            jHitbox.userData = { isPin: true, pinType: 'io', pinId: 'P1', localX: 0, localZ: 0 };
            group.add(jHitbox);
            return;
        }

        const c = { north: false, south: false, east: false, west: false };
        const nN = state.circuit.get(core.key(node.x, node.y, node.z + core.GRID_SIZE));
        const nS = state.circuit.get(core.key(node.x, node.y, node.z - core.GRID_SIZE));
        const nE = state.circuit.get(core.key(node.x + core.GRID_SIZE, node.y, node.z));
        const nW = state.circuit.get(core.key(node.x - core.GRID_SIZE, node.y, node.z));

        const checkConn = (dx, dz, dir) => {
            const targetX = core.snap(node.x + dx);
            const targetZ = core.snap(node.z + dz);
            const nb = state.circuit.get(core.key(targetX, node.y, targetZ));

            // Proíbe cruzamentos ou braços laterais entre pinos de entrada vizinhos de portas lógicas (74xx)
            for (const other of state.circuit.values()) {
                if (!core.GATE_TYPES.has(other.type) && other.type !== core.COMP.DEC_BCD_7SEG) continue;
                const pins = core.getComponentPins(other);
                for (const pin of pins.inputs) {
                    const dNode = Math.hypot(node.x - pin.x, node.z - pin.z);
                    const dTarget = Math.hypot(targetX - pin.x, targetZ - pin.z);
                    if (dNode <= 0.6 || dTarget <= 0.6) {
                        if ((other.rot === 0 || other.rot === 2) && (dir === 'east' || dir === 'west')) return false;
                        if ((other.rot === 1 || other.rot === 3) && (dir === 'north' || dir === 'south')) return false;
                    }
                }
            }

            if (nb) {
                if (nb.type === 'JUMPER') {
                    if ((dir === 'north' || dir === 'south') && nb.x === node.x) return true;
                    if ((dir === 'east' || dir === 'west') && nb.z === node.z) return true;
                    return false;
                }
                if (nb.type === core.COMP.SWITCH || core.BUTTON_TYPES.has(nb.type) || core.ACTUATOR_TYPES.has(nb.type)) {
                    return true;
                }
                if (nb.type === core.COMP.WIRE) {
                    if (dir === 'east' || dir === 'west') {
                        const hasVertical = (nN && nN.type === core.COMP.WIRE) || (nS && nS.type === core.COMP.WIRE);
                        const nbHasVertical = (state.circuit.get(core.key(nb.x, nb.y, nb.z + core.GRID_SIZE))?.type === core.COMP.WIRE) ||
                                              (state.circuit.get(core.key(nb.x, nb.y, nb.z - core.GRID_SIZE))?.type === core.COMP.WIRE);
                        if (hasVertical && nbHasVertical) return false;
                    }
                    return true;
                }
            }

            for (const other of state.circuit.values()) {
                if (other.type === core.COMP.WIRE || other.type === 'JUMPER') continue;
                const pins = core.getComponentPins(other);
                const allPins = [...pins.outputs, ...pins.inputs];
                const matchingPin = allPins.find(p => Math.hypot(p.x - targetX, p.z - targetZ) <= 0.26);
                if (matchingPin) {
                    if (core.GATE_TYPES.has(other.type)) {
                        const isInput = pins.inputs.some(p => p.id === matchingPin.id);
                        if (isInput) {
                            if (other.rot === 0 && dir !== 'north') return false;
                            if (other.rot === 1 && dir !== 'east') return false;
                            if (other.rot === 2 && dir !== 'south') return false;
                            if (other.rot === 3 && dir !== 'west') return false;
                        } else {
                            if (other.rot === 0 && dir !== 'south') return false;
                            if (other.rot === 1 && dir !== 'west') return false;
                            if (other.rot === 2 && dir !== 'north') return false;
                            if (other.rot === 3 && dir !== 'east') return false;
                        }
                    }
                    return true;
                }
            }
            return false;
        };

        if (checkConn(0, core.GRID_SIZE, 'north')) c.north = true;
        if (checkConn(0, -core.GRID_SIZE, 'south')) c.south = true;
        if (checkConn(core.GRID_SIZE, 0, 'east')) c.east = true;
        if (checkConn(-core.GRID_SIZE, 0, 'west')) c.west = true;

        const mat = getWireMaterial(node.netColor || COLOR_LOW, node.state);
        const count = (c.north ? 1 : 0) + (c.south ? 1 : 0) + (c.east ? 1 : 0) + (c.west ? 1 : 0);

        if (count === 0) {
            const ilha = new THREE.Mesh(new THREE.BoxGeometry(WIRE_WIDTH, WIRE_HEIGHT, WIRE_WIDTH), mat);
            ilha.position.set(0, WIRE_Y, 0); group.add(ilha);
        } else if (c.east && c.west && !c.north && !c.south) {
            const segX = new THREE.Mesh(new THREE.BoxGeometry(WIRE_LENGTH, WIRE_HEIGHT, WIRE_WIDTH), mat);
            segX.position.set(0, WIRE_Y, 0); group.add(segX);
        } else if (c.north && c.south && !c.east && !c.west) {
            const segZ = new THREE.Mesh(new THREE.BoxGeometry(WIRE_WIDTH, WIRE_HEIGHT, WIRE_LENGTH), mat);
            segZ.position.set(0, WIRE_Y, 0); group.add(segZ);
        } else {
            const noCentral = new THREE.Mesh(new THREE.BoxGeometry(WIRE_WIDTH, WIRE_HEIGHT, WIRE_WIDTH), mat);
            noCentral.position.set(0, WIRE_Y, 0); group.add(noCentral);

            const metade = WIRE_LENGTH / 2;
            if (c.east) {
                const braçoE = new THREE.Mesh(new THREE.BoxGeometry(metade, WIRE_HEIGHT, WIRE_WIDTH), mat);
                braçoE.position.set(metade / 2, WIRE_Y, 0); group.add(braçoE);
            }
            if (c.west) {
                const braçoW = new THREE.Mesh(new THREE.BoxGeometry(metade, WIRE_HEIGHT, WIRE_WIDTH), mat);
                braçoW.position.set(-metade / 2, WIRE_Y, 0); group.add(braçoW);
            }
            if (c.north) {
                const braçoN = new THREE.Mesh(new THREE.BoxGeometry(WIRE_WIDTH, WIRE_HEIGHT, metade), mat);
                braçoN.position.set(0, WIRE_Y, metade / 2); group.add(braçoN);
            }
            if (c.south) {
                const braçoS = new THREE.Mesh(new THREE.BoxGeometry(WIRE_WIDTH, WIRE_HEIGHT, metade), mat);
                braçoS.position.set(0, WIRE_Y, -metade / 2); group.add(braçoS);
            }
        }

        const hasDockedAirWire = state.airWires && state.airWires.some(w =>
            (Math.abs(w.from.nodeX - node.x) < 0.1 && Math.abs(w.from.nodeZ - node.z) < 0.1) ||
            (Math.abs(w.to.nodeX - node.x) < 0.1 && Math.abs(w.to.nodeZ - node.z) < 0.1)
        );
        if (hasDockedAirWire) {
            // Ilha metálica de borne fêmea / banana jack industrial integrado à trilha terrestre
            const socketBase = new THREE.Mesh(
                new THREE.CylinderGeometry(0.14, 0.16, 0.04, 24),
                new THREE.MeshStandardMaterial({ color: 0xF59E0B, metalness: 0.85, roughness: 0.25 })
            );
            socketBase.position.set(0, WIRE_Y + 0.02, 0);
            group.add(socketBase);

            const socketCollar = new THREE.Mesh(
                new THREE.CylinderGeometry(0.10, 0.10, 0.05, 24),
                new THREE.MeshStandardMaterial({ color: 0xCBD5E1, metalness: 0.9, roughness: 0.2 })
            );
            socketCollar.position.set(0, WIRE_Y + 0.045, 0);
            group.add(socketCollar);

            const socketHole = new THREE.Mesh(
                new THREE.CylinderGeometry(0.045, 0.045, 0.055, 16),
                new THREE.MeshBasicMaterial({ color: 0x020617 })
            );
            socketHole.position.set(0, WIRE_Y + 0.05, 0);
            group.add(socketHole);
        }

        const wHitbox = new THREE.Mesh(pinHitboxGeo, pinHitboxMat);
        wHitbox.position.set(0, WIRE_Y + 0.02, 0);
        wHitbox.userData = { isPin: true, pinType: 'io', pinId: 'P1', localX: 0, localZ: 0 };
        group.add(wHitbox);
    }

    function refreshNeighborWires(x, y, z) {
        const coords = [[0,0], [0, core.GRID_SIZE], [0, -core.GRID_SIZE], [core.GRID_SIZE, 0], [-core.GRID_SIZE, 0]];
        coords.forEach(([dx, dz]) => {
            const node = state.circuit.get(core.key(x + dx, y, z + dz));
            if (node && (node.type === core.COMP.WIRE || node.type === 'JUMPER')) {
                updateWireGeometry(node);
            }
        });
    }

    function parseFluidTag(tag) {
        if (!tag || typeof tag !== 'string') return null;
        const clean = tag.trim().toUpperCase();
        const match = clean.match(/^([0-9]+|[A-Z]+)(?:([AVY])([0-9]*))?$/);
        if (!match) return { group: clean, prefix: null, index: null };
        return {
            group: match[1],
            prefix: match[2] || null,
            index: match[3] || null
        };
    }

    function updateFluidLinks() {
        state.circuit.forEach(node => {
            if (node.type === core.COMP.SOLENOID_PNEU || node.type === core.COMP.SOLENOID_HYDRA) {
                node.linkedValve = null;
            } else if (core.VALVE_TYPES.has(node.type)) {
                node.linkedSolenoidY1 = null;
                node.linkedSolenoidY2 = null;
                node.linkedCylinder = null;
                node.linkedSupply = null;
                node.hasPressure = false;
            } else if (core.CYLINDER_TYPES.has(node.type)) {
                node.linkedValve = null;
            }
        });

        // 0. Detecção das Unidades Geradoras de Pressão (FRL Pneumática / HPU Hidráulica)
        let pneuSupplyActive = false;
        let hydraSupplyActive = false;
        let pneuSupplyNode = null;
        let hydraSupplyNode = null;

        state.circuit.forEach(node => {
            if (node.type === core.COMP.SUPPLY_PNEU) {
                pneuSupplyNode = node;
                if (node.manualValue !== false) {
                    pneuSupplyActive = true;
                    node.pressureBar = 6.0;
                } else {
                    node.pressureBar = 0.0;
                }
            } else if (node.type === core.COMP.SUPPLY_HYDRA) {
                hydraSupplyNode = node;
                if (node.manualValue !== false) {
                    hydraSupplyActive = true;
                    node.pressureBar = 120.0;
                } else {
                    node.pressureBar = 0.0;
                }
            }
        });

        // 1. Mapeamento Direto por Mangueiras Físicas (state.hoses)
        const hoses = state.hoses || [];
        if (hoses.length > 0) {
            hoses.forEach(h => {
                const nFrom = state.circuit.get(core.key(h.from.nodeX, h.from.nodeY ?? 0, h.from.nodeZ));
                const nTo = state.circuit.get(core.key(h.to.nodeX, h.to.nodeY ?? 0, h.to.nodeZ));
                if (!nFrom || !nTo) return;

                // Suprimento para Válvula
                const isSupplyFrom = (nFrom.type === core.COMP.SUPPLY_PNEU || nFrom.type === core.COMP.SUPPLY_HYDRA);
                const isSupplyTo = (nTo.type === core.COMP.SUPPLY_PNEU || nTo.type === core.COMP.SUPPLY_HYDRA);
                const isValveFrom = core.VALVE_TYPES.has(nFrom.type);
                const isValveTo = core.VALVE_TYPES.has(nTo.type);

                if (isSupplyFrom && isValveTo) {
                    nTo.linkedSupply = nFrom;
                    nTo.hasPressure = Boolean(nFrom.manualValue !== false);
                    nTo.pressureBar = nTo.hasPressure ? (nFrom.pressureBar ?? (nFrom.type === core.COMP.SUPPLY_HYDRA ? 120.0 : 6.0)) : 0.0;
                } else if (isSupplyTo && isValveFrom) {
                    nFrom.linkedSupply = nTo;
                    nFrom.hasPressure = Boolean(nTo.manualValue !== false);
                    nFrom.pressureBar = nFrom.hasPressure ? (nTo.pressureBar ?? (nTo.type === core.COMP.SUPPLY_HYDRA ? 120.0 : 6.0)) : 0.0;
                }

                // Válvula para Cilindro
                const isCylFrom = core.CYLINDER_TYPES.has(nFrom.type);
                const isCylTo = core.CYLINDER_TYPES.has(nTo.type);

                if (isValveFrom && isCylTo) {
                    nFrom.linkedCylinder = nTo;
                    nTo.linkedValve = nFrom;
                } else if (isValveTo && isCylFrom) {
                    nTo.linkedCylinder = nFrom;
                    nFrom.linkedValve = nTo;
                }
            });
        }

        // 2. Fallback por tags / proximidade se não houver mangueiras de suprimento/cilindro
        state.circuit.forEach(valve => {
            if (!core.VALVE_TYPES.has(valve.type)) return;
            const isHydra = (valve.type === core.COMP.VALVE_4_2_MONO || valve.type === core.COMP.VALVE_4_2_BI);
            const targetSolType = isHydra ? core.COMP.SOLENOID_HYDRA : core.COMP.SOLENOID_PNEU;
            const maxDist = isHydra ? core.HYDRAULIC_LINK_DISTANCE : core.ACTUATOR_LINK_DISTANCE;

            if (!valve.linkedSupply) {
                valve.hasPressure = isHydra ? hydraSupplyActive : pneuSupplyActive;
                valve.pressureBar = valve.hasPressure ? (isHydra ? 120.0 : 6.0) : 0.0;
                valve.linkedSupply = isHydra ? hydraSupplyNode : pneuSupplyNode;
            }

            // Associação de solenoides externos (se houver componentes externos SOLENOID_PNEU / HYDRA)
            const valveParsed = parseFluidTag(valve.tag);
            if (valveParsed) {
                state.circuit.forEach(sol => {
                    if (sol.type === targetSolType && !sol.linkedValve) {
                        const solParsed = parseFluidTag(sol.tag);
                        if (solParsed && solParsed.group === valveParsed.group) {
                            if (solParsed.index === '2' && !valve.linkedSolenoidY2) {
                                valve.linkedSolenoidY2 = sol;
                                sol.linkedValve = valve;
                            } else if (!valve.linkedSolenoidY1) {
                                valve.linkedSolenoidY1 = sol;
                                sol.linkedValve = valve;
                            }
                        }
                    }
                });

                if (!valve.linkedCylinder) {
                    const targetCylMatch = (c) => isHydra ? (c.type === core.COMP.CYL_HYDRA) : (c.type === core.COMP.CYL_DOUBLE || c.type === core.COMP.CYL_SINGLE);
                    state.circuit.forEach(cyl => {
                        if (targetCylMatch(cyl) && !cyl.linkedValve && !valve.linkedCylinder) {
                            const cylParsed = parseFluidTag(cyl.tag);
                            if (cylParsed && cylParsed.group === valveParsed.group) {
                                valve.linkedCylinder = cyl;
                                cyl.linkedValve = valve;
                            }
                        }
                    });
                }
            }

            // Proximidade fallback para solenoides externos e cilindros
            const needsY1 = !valve.linkedSolenoidY1;
            const needsY2 = !valve.linkedSolenoidY2 && (valve.type === core.COMP.VALVE_5_2_BI || valve.type === core.COMP.VALVE_4_2_BI);
            if (needsY1 || needsY2) {
                const nearbySolenoids = [];
                state.circuit.forEach(sol => {
                    if (sol.type === targetSolType && !sol.linkedValve && !sol.tag) {
                        const d = Math.hypot(sol.x - valve.x, sol.z - valve.z);
                        if (d <= maxDist) nearbySolenoids.push({ sol, d });
                    }
                });
                nearbySolenoids.sort((a, b) => a.d - b.d);
                if (needsY1 && nearbySolenoids.length > 0) {
                    valve.linkedSolenoidY1 = nearbySolenoids[0].sol;
                    nearbySolenoids[0].sol.linkedValve = valve;
                }
                if (needsY2) {
                    const sol2 = nearbySolenoids.find(s => s.sol !== valve.linkedSolenoidY1);
                    if (sol2) {
                        valve.linkedSolenoidY2 = sol2.sol;
                        sol2.sol.linkedValve = valve;
                    }
                }
            }

            if (!valve.linkedCylinder) {
                const targetCylMatch = (c) => isHydra ? (c.type === core.COMP.CYL_HYDRA) : (c.type === core.COMP.CYL_DOUBLE || c.type === core.COMP.CYL_SINGLE);
                let closestCyl = null, minCylDist = maxDist;
                state.circuit.forEach(cyl => {
                    if (targetCylMatch(cyl) && !cyl.linkedValve && !cyl.tag) {
                        const d = Math.hypot(cyl.x - valve.x, cyl.z - valve.z);
                        if (d <= minCylDist) { minCylDist = d; closestCyl = cyl; }
                    }
                });
                if (closestCyl) {
                    valve.linkedCylinder = closestCyl;
                    closestCyl.linkedValve = valve;
                }
            }

            // 3. Atualização do Carretel (Spool): Reconhece nível lógico nativo em Y1/Y2 ou no solenoide externo
            const cp = core.getComponentPins(valve);
            const pinY1 = cp.inputs.find(p => p.id === 'Y1' || p.id === 'YH1' || p.id === 'A1');
            const pinY2 = cp.inputs.find(p => p.id === 'Y2' || p.id === 'YH2' || p.id === 'A2');
            
            const isValvePinActive = (pin) => {
                if (!pin) return false;
                for (const aw of (state.airWires || [])) {
                    if (!aw.state) continue;
                    const matchesTo = (Math.abs(aw.to.nodeX - valve.x) < 0.25 && Math.abs(aw.to.nodeZ - valve.z) < 0.25 && (aw.to.pinId === pin.id || (pin.aliases && pin.aliases.includes(aw.to.pinId))));
                    const matchesFrom = (Math.abs(aw.from.nodeX - valve.x) < 0.25 && Math.abs(aw.from.nodeZ - valve.z) < 0.25 && (aw.from.pinId === pin.id || (pin.aliases && pin.aliases.includes(aw.from.pinId))));
                    if (matchesTo || matchesFrom) return true;
                }
                const snapX = core.snap(pin.x);
                const snapZ = core.snap(pin.z);
                const direct = state.circuit.get(core.key(snapX, 0, snapZ));
                if (direct) {
                    if (direct.type === core.COMP.WIRE && direct.state) return true;
                    if (direct.type === 'JUMPER' && (direct.stateNS || direct.stateEW)) return true;
                }
                for (const n of state.circuit.values()) {
                    if ((n.type === core.COMP.WIRE && n.state) || (n.type === 'JUMPER' && (n.stateNS || n.stateEW))) {
                        if (Math.hypot(n.x - pin.x, n.z - pin.z) <= 0.35) return true;
                    }
                }
                return false;
            };

            let y1Active = Boolean(valve.linkedSolenoidY1 && valve.linkedSolenoidY1.state) || isValvePinActive(pinY1);
            let y2Active = Boolean(valve.linkedSolenoidY2 && valve.linkedSolenoidY2.state) || isValvePinActive(pinY2);

            const isMono = (valve.type === core.COMP.VALVE_5_2_MONO || valve.type === core.COMP.VALVE_4_2_MONO || valve.type === core.COMP.VALVE_3_2_MONO);
            if (isMono) {
                valve.spoolPosition = y1Active ? 1.0 : 0.0;
                valve.state = y1Active;
            } else {
                if (y1Active && !y2Active) {
                    valve.spoolPosition = 1.0;
                    valve.state = true;
                } else if (y2Active && !y1Active) {
                    valve.spoolPosition = 0.0;
                    valve.state = false;
                }
            }

            if (valve.linkedCylinder) {
                if (valve.hasPressure) {
                    valve.linkedCylinder.targetExtension = (valve.spoolPosition > 0.5) ? 1.0 : 0.0;
                } else {
                    valve.linkedCylinder.targetExtension = (valve.linkedCylinder.type === core.COMP.CYL_SINGLE) ? 0.0 : (valve.spoolPosition > 0.5 ? 1.0 : 0.0);
                }
            }
        });

        // Pré-cálculo e cache de vazão (flowScale) nos atuadores para execução O(1) no loop de renderização
        state.circuit.forEach(node => {
            if (core.CYLINDER_TYPES && core.CYLINDER_TYPES.has(node.type)) {
                let flowScale = 1.0;
                state.circuit.forEach(other => {
                    if (other.type === core.COMP.VALVE_FLOW_CONTROL) {
                        const sameTag = node.tag && other.tag && (node.tag.charAt(0) === other.tag.charAt(0));
                        const dist = Math.hypot(other.x - node.x, other.z - node.z);
                        if (sameTag || dist <= 3.0) {
                            flowScale = Math.min(flowScale, (other.openingPercent ?? 100) / 100);
                        }
                    }
                });
                node.flowScale = flowScale;
            }
        });

        updateFluidHoses();
    }

    let _cachedHydraFlowTex = null;
    let _cachedPneuFlowTex = null;

    function getHoseFlowTexture(isHydra) {
        if (isHydra) {
            if (!_cachedHydraFlowTex) _cachedHydraFlowTex = createHoseFlowTexture(true);
            return _cachedHydraFlowTex;
        } else {
            if (!_cachedPneuFlowTex) _cachedPneuFlowTex = createHoseFlowTexture(false);
            return _cachedPneuFlowTex;
        }
    }

    function createHoseFlowTexture(isHydra) {
        const canvas = document.createElement('canvas');
        canvas.width = 128;
        canvas.height = 32;
        const ctx = canvas.getContext('2d');

        const grad = ctx.createLinearGradient(0, 0, 128, 0);
        if (isHydra) {
            grad.addColorStop(0.0, '#F59E0B');
            grad.addColorStop(0.4, '#D97706');
            grad.addColorStop(0.5, '#78350F');
            grad.addColorStop(0.85, '#1E293B');
            grad.addColorStop(1.0, '#F59E0B');
        } else {
            grad.addColorStop(0.0, '#00E5FF');
            grad.addColorStop(0.35, '#E0F2FE');
            grad.addColorStop(0.5, '#0284C7');
            grad.addColorStop(0.85, '#0F172A');
            grad.addColorStop(1.0, '#00E5FF');
        }
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, 128, 32);

        ctx.strokeStyle = isHydra ? 'rgba(253, 230, 138, 0.75)' : 'rgba(255, 255, 255, 0.85)';
        ctx.lineWidth = 3;
        for (let x = 8; x < 128; x += 32) {
            ctx.beginPath();
            ctx.moveTo(x, 4);
            ctx.lineTo(x + 12, 16);
            ctx.lineTo(x, 28);
            ctx.stroke();
        }

        const tex = new THREE.CanvasTexture(canvas);
        tex.wrapS = THREE.RepeatWrapping;
        tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(6, 1);
        return tex;
    }

    function updateFluidHoses() {
        if (!fluidHosesGroup) return;

        while (fluidHosesGroup.children.length > 0) {
            const child = fluidHosesGroup.children[0];
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
                const mats = Array.isArray(child.material) ? child.material : [child.material];
                mats.forEach(m => {
                    // Descarta apenas o material sem descartar a textura compartilhada em cache
                    m.dispose();
                });
            }
            fluidHosesGroup.remove(child);
        }

        const buildHoseMesh = (pStart, pEnd, nStart, nEnd, isHydra, isPressurized, hoseData) => {
            const p0 = pStart.clone();
            const p3 = pEnd.clone();
            const dist = p0.distanceTo(p3);
            const extraElev = Math.min(0.40, dist * 0.08);

            // Sair do niple recuando 0.22 no sentido normal da face (+-Z) e elevar-se em Y (+0.45)
            const p1 = new THREE.Vector3(
                p0.x + (nStart ? nStart.x * 0.22 : 0),
                p0.y + 0.45 + extraElev,
                p0.z + (nStart ? nStart.z * 0.22 : 0)
            );
            const p2 = new THREE.Vector3(
                p3.x + (nEnd ? nEnd.x * 0.22 : 0),
                p3.y + 0.45 + extraElev,
                p3.z + (nEnd ? nEnd.z * 0.22 : 0)
            );

            const curve = new THREE.CubicBezierCurve3(p0, p1, p2, p3);
            const radius = isHydra ? 0.034 : 0.026;
            const geo = new THREE.TubeGeometry(curve, 36, radius, 8, false);

            const tex = getHoseFlowTexture(isHydra);
            const pressColor = isHydra ? COLOR_HYDRA_PRESSURIZED : COLOR_PNEU_PRESSURIZED;
            const exhaustColor = isHydra ? COLOR_HYDRA_EXHAUST : COLOR_PNEU_EXHAUST;
            const mat = new THREE.MeshStandardMaterial({
                color: isPressurized ? pressColor : exhaustColor,
                map: tex,
                emissive: isPressurized ? pressColor : 0x000000,
                emissiveIntensity: isPressurized ? 0.8 : 0.0,
                roughness: isHydra ? 0.7 : 0.4,
                metalness: isHydra ? 0.2 : 0.3
            });

            const hose = new THREE.Mesh(geo, mat);
            hose.castShadow = false;
            hose.receiveShadow = false;
            hose.userData = hoseData || {};
            fluidHosesGroup.add(hose);
            return hose;
        };

        // Renderização estrita e real do array state.hoses
        if (state.hoses && state.hoses.length > 0) {
            state.hoses.forEach(h => {
                const fromNode = state.circuit.get(core.key(h.from.nodeX, h.from.nodeY ?? 0, h.from.nodeZ));
                const toNode = state.circuit.get(core.key(h.to.nodeX, h.to.nodeY ?? 0, h.to.nodeZ));
                if (!fromNode || !toNode || !fromNode.mesh || !toNode.mesh) return;

                fromNode.mesh.updateMatrixWorld(true);
                toNode.mesh.updateMatrixWorld(true);

                const fromPorts = core.getComponentFluidPorts(fromNode);
                const toPorts = core.getComponentFluidPorts(toNode);
                const pFrom = fromPorts.find(p => p.id === h.from.portId) || fromPorts[0];
                const pTo = toPorts.find(p => p.id === h.to.portId) || toPorts[0];
                if (!pFrom || !pTo) return;

                const pFromY = (pFrom.ly !== undefined) ? pFrom.ly : (core.VALVE_TYPES.has(fromNode.type) ? -0.31 : -0.27);
                const pToY = (pTo.ly !== undefined) ? pTo.ly : (core.VALVE_TYPES.has(toNode.type) ? -0.31 : -0.27);
                const startWorld = fromNode.mesh.localToWorld(new THREE.Vector3(pFrom.lx, pFromY, pFrom.lz));
                const endWorld = toNode.mesh.localToWorld(new THREE.Vector3(pTo.lx, pToY, pTo.lz));

                // Normal da face do niple no espaço de mundo
                const getPortFaceNormal = (node, p) => {
                    const lz = (p && p.lz !== undefined) ? p.lz : 0;
                    const dir = new THREE.Vector3(0, 0, (lz <= 0) ? -1 : 1);
                    if (node && node.mesh) dir.applyQuaternion(node.mesh.quaternion);
                    return dir.normalize();
                };

                const nStart = getPortFaceNormal(fromNode, pFrom);
                const nEnd = getPortFaceNormal(toNode, pTo);

                const isHydra = Boolean(pFrom.isHydra || pTo.isHydra || fromNode.type === core.COMP.SUPPLY_HYDRA || toNode.type === core.COMP.SUPPLY_HYDRA || fromNode.type === core.COMP.CYL_HYDRA || toNode.type === core.COMP.CYL_HYDRA || fromNode.type === core.COMP.VALVE_4_2_MONO || fromNode.type === core.COMP.VALVE_4_2_BI);

                let valveNode = null;
                let cylNode = null;
                let isSupply = false;
                let isLineA = false;
                let isPressurized = false;

                if (fromNode.type === core.COMP.SUPPLY_PNEU || fromNode.type === core.COMP.SUPPLY_HYDRA) {
                    isSupply = true;
                    isPressurized = Boolean(fromNode.manualValue !== false);
                } else if (toNode.type === core.COMP.SUPPLY_PNEU || toNode.type === core.COMP.SUPPLY_HYDRA) {
                    isSupply = true;
                    isPressurized = Boolean(toNode.manualValue !== false);
                }

                if (core.VALVE_TYPES.has(fromNode.type)) {
                    valveNode = fromNode;
                    const vActive = Boolean(fromNode.state || fromNode.spoolPosition > 0.5);
                    const hasP = Boolean(fromNode.hasPressure);
                    if (['4(A)', 'A', '2(A)'].includes(h.from.portId)) {
                        isLineA = true;
                        isPressurized = hasP && vActive;
                    } else if (['2(B)', 'B'].includes(h.from.portId)) {
                        isLineA = false;
                        isPressurized = hasP && !vActive;
                    }
                } else if (core.VALVE_TYPES.has(toNode.type)) {
                    valveNode = toNode;
                    const vActive = Boolean(toNode.state || toNode.spoolPosition > 0.5);
                    const hasP = Boolean(toNode.hasPressure);
                    if (['4(A)', 'A', '2(A)'].includes(h.to.portId)) {
                        isLineA = true;
                        isPressurized = hasP && vActive;
                    } else if (['2(B)', 'B'].includes(h.to.portId)) {
                        isLineA = false;
                        isPressurized = hasP && !vActive;
                    }
                }

                if (core.CYLINDER_TYPES.has(fromNode.type)) cylNode = fromNode;
                if (core.CYLINDER_TYPES.has(toNode.type)) cylNode = toNode;

                buildHoseMesh(startWorld, endWorld, nStart, nEnd, isHydra, isPressurized, {
                    hoseId: h.id,
                    fromNode, toNode,
                    fromPort: h.from.portId, toPort: h.to.portId,
                    isHydra, isSupply, isLineA,
                    valve: valveNode,
                    cylinder: cylNode
                });
            });
        }
    }

    function updateWireNetworks() {
        state.circuit.forEach(node => {
            if (node.type === core.COMP.WIRE) {
                node.state = false; 
                node.netColor = COLOR_LOW;
                node.netSourceId = null;
            } else if (node.type === 'JUMPER') {
                node.stateNS = false; node.colorNS = COLOR_LOW; node.sourceNS = null;
                node.stateEW = false; node.colorEW = COLOR_LOW; node.sourceEW = null;
            }
        });

        // Todos os cabos aéreos começam desenergizados a cada varredura (condutores passivos)
        state.airWires.forEach(wire => {
            wire.state = false;
        });

        const queue = [];
        const visitedAW = new Set();

        const addSource = (x, y, z, color, sourceId) => {
            const snapX = core.snap(x);
            const snapZ = core.snap(z);
            const direct = state.circuit.get(core.key(snapX, 0, snapZ));
            if (direct) {
                if (direct.type === core.COMP.WIRE) {
                    if (!direct.state) {
                        direct.state = true;
                        direct.netColor = color;
                        direct.netSourceId = sourceId;
                        queue.push({ x: snapX, y: 0, z: snapZ, color, sourceId, dir: 'ALL' });
                        propagateAttachedAirWiresFromWire(snapX, snapZ, color, sourceId);
                    }
                    return;
                } else if (direct.type === 'JUMPER') {
                    let changed = false;
                    if (!direct.stateNS) { direct.stateNS = true; direct.colorNS = color; direct.sourceNS = sourceId; changed = true; }
                    if (!direct.stateEW) { direct.stateEW = true; direct.colorEW = color; direct.sourceEW = sourceId; changed = true; }
                    if (changed) {
                        queue.push({ x: snapX, y: 0, z: snapZ, color, sourceId, dir: 'ALL' });
                        propagateAttachedAirWiresFromWire(snapX, snapZ, color, sourceId);
                    }
                    return;
                }
            }
        };

        const propagateAttachedAirWiresFromWire = (wireX, wireZ, color, sourceId) => {
            state.airWires.forEach(aw => {
                if (visitedAW.has(aw.id)) return;
                const matchFrom = (Math.abs(aw.from.nodeX - wireX) < 0.25 && Math.abs(aw.from.nodeZ - wireZ) < 0.25);
                const matchTo = (Math.abs(aw.to.nodeX - wireX) < 0.25 && Math.abs(aw.to.nodeZ - wireZ) < 0.25);
                if (matchFrom) {
                    visitedAW.add(aw.id);
                    aw.state = true;
                    addSource(aw.to.nodeX, 0, aw.to.nodeZ, color, sourceId);
                } else if (matchTo) {
                    visitedAW.add(aw.id);
                    aw.state = true;
                    addSource(aw.from.nodeX, 0, aw.from.nodeZ, color, sourceId);
                }
            });
        };

        const propagateFromSourcePin = (nodeX, nodeZ, pinX, pinZ, pinId, aliases, color, sourceId) => {
            // 1. Energiza trilha PCB no pino ou na célula da grade correspondente
            addSource(pinX, 0, pinZ, color, sourceId);
            addSource(nodeX, 0, nodeZ, color, sourceId);

            // 2. Energiza cabos aéreos (jumpers) conectados a este pino de saída
            state.airWires.forEach(aw => {
                if (visitedAW.has(aw.id)) return;
                const matchPosFrom = (Math.abs(aw.from.nodeX - nodeX) < 0.25 && Math.abs(aw.from.nodeZ - nodeZ) < 0.25) ||
                                     (Math.abs(aw.from.nodeX - pinX) < 0.25 && Math.abs(aw.from.nodeZ - pinZ) < 0.25);
                const matchPinFrom = (!pinId || aw.from.pinId === pinId || (aliases && aliases.includes(aw.from.pinId)) || aw.from.pinId === 'OUT' || aw.from.pinId === 'P1');

                const matchPosTo = (Math.abs(aw.to.nodeX - nodeX) < 0.25 && Math.abs(aw.to.nodeZ - nodeZ) < 0.25) ||
                                   (Math.abs(aw.to.nodeX - pinX) < 0.25 && Math.abs(aw.to.nodeZ - pinZ) < 0.25);
                const matchPinTo = (!pinId || aw.to.pinId === pinId || (aliases && aliases.includes(aw.to.pinId)) || aw.to.pinId === 'OUT' || aw.to.pinId === 'P1');

                if (matchPosFrom && matchPinFrom) {
                    visitedAW.add(aw.id);
                    aw.state = true;
                    addSource(aw.to.nodeX, 0, aw.to.nodeZ, color, sourceId);
                } else if (matchPosTo && matchPinTo) {
                    visitedAW.add(aw.id);
                    aw.state = true;
                    addSource(aw.from.nodeX, 0, aw.from.nodeZ, color, sourceId);
                }
            });
        };

        state.circuit.forEach(node => {
            if (node.type === core.COMP.SWITCH && node.state) {
                const conf = core.INPUT_CONFIG.find(c => c.id === node.switchId);
                const color = conf ? conf.color : COLOR_HIGH;
                const pins = core.getComponentPins(node);
                pins.outputs.forEach(p => {
                    propagateFromSourcePin(node.x, node.z, p.x, p.z, p.id, p.aliases, color, `SW_${node.switchId}`);
                });
                // Permite conexão via trilha PCB direta ou em qualquer dos 4 vizinhos imediatos
                const adjNeighbors = [[0, core.GRID_SIZE], [0, -core.GRID_SIZE], [core.GRID_SIZE, 0], [-core.GRID_SIZE, 0]];
                adjNeighbors.forEach(([dx, dz]) => {
                    addSource(node.x + dx, 0, node.z + dz, color, `SW_${node.switchId}`);
                });
            }
            if (core.GATE_TYPES.has(node.type) || core.COMBINATIONAL_TYPES.has(node.type)) {
                const pins = core.getComponentPins(node);
                pins.outputs.forEach(p => {
                    if (node.outputStates && node.outputStates[p.id]) {
                        propagateFromSourcePin(node.x, node.z, p.x, p.z, p.id, p.aliases, COLOR_HIGH, `GATE_${core.key(node.x, 0, node.z)}_${p.id}`);
                    }
                });
            }
            if (node.type === core.COMP.RELAY) {
                const pins = core.getComponentPins(node);
                pins.outputs.forEach(p => {
                    if (node.outputStates && node.outputStates[p.id]) {
                        propagateFromSourcePin(node.x, node.z, p.x, p.z, p.id, p.aliases, 0x22C55E, `RELAY_${core.key(node.x, 0, node.z)}_${p.id}`);
                    }
                });
            }
            if (node.type === core.COMP.CONTACTOR_3P) {
                const pins = core.getComponentPins(node);
                pins.outputs.forEach(p => {
                    if (node.outputStates && node.outputStates[p.id]) {
                        propagateFromSourcePin(node.x, node.z, p.x, p.z, p.id, p.aliases, 0x22C55E, `CONTACTOR_${core.key(node.x, 0, node.z)}_${p.id}`);
                    }
                });
            }
            if (node.type === core.COMP.POWER_SUPPLY_3P && (node.manualValue !== false)) {
                const pins = core.getComponentPins(node);
                pins.outputs.forEach(p => {
                    if (p.id === 'L1' || p.id === 'L2' || p.id === 'L3') {
                        propagateFromSourcePin(node.x, node.z, p.x, p.z, p.id, p.aliases, 0xEF4444, `PS3P_${core.key(node.x, 0, node.z)}_${p.id}`);
                    }
                });
            }
            if (core.BUTTON_TYPES.has(node.type) && node.state) {
                const pins = core.getComponentPins(node);
                pins.outputs.forEach(p => {
                    propagateFromSourcePin(node.x, node.z, p.x, p.z, p.id, p.aliases, 0x22C55E, `BTN_${core.key(node.x, 0, node.z)}`);
                });
                const adjNeighbors = [[0, core.GRID_SIZE], [0, -core.GRID_SIZE], [core.GRID_SIZE, 0], [-core.GRID_SIZE, 0]];
                adjNeighbors.forEach(([dx, dz]) => {
                    addSource(node.x + dx, 0, node.z + dz, 0x22C55E, `BTN_${core.key(node.x, 0, node.z)}`);
                });
            }
            if (node.type === core.COMP.TIMER_TON && node.state) {
                const pins = core.getComponentPins(node);
                pins.outputs.forEach(p => {
                    propagateFromSourcePin(node.x, node.z, p.x, p.z, p.id, p.aliases, 0xF59E0B, `TIMER_${core.key(node.x, 0, node.z)}`);
                });
            }
            if (core.CYLINDER_TYPES.has(node.type)) {
                const pins = core.getComponentPins(node);
                pins.outputs.forEach(p => {
                    const isEnergized = (p.id === 'S0' && node.sensorS0) || (p.id === 'S1' && node.sensorS1);
                    if (isEnergized) {
                        propagateFromSourcePin(node.x, node.z, p.x, p.z, p.id, p.aliases, 0x38BDF8, `CYL_${core.key(node.x, 0, node.z)}_${p.id}`);
                    }
                });
            }
        });

        while (queue.length > 0) {
            const curr = queue.shift();
            
            const neighbors = [];
            if (curr.dir === 'ALL' || curr.dir === 'NS') {
                neighbors.push({ dx: 0, dz: core.GRID_SIZE, flow: 'NS' });
                neighbors.push({ dx: 0, dz: -core.GRID_SIZE, flow: 'NS' });
            }
            if (curr.dir === 'ALL' || curr.dir === 'EW') {
                neighbors.push({ dx: core.GRID_SIZE, dz: 0, flow: 'EW' });
                neighbors.push({ dx: -core.GRID_SIZE, dz: 0, flow: 'EW' });
            }

            for (const n of neighbors) {
                const nx = curr.x + n.dx;
                const nz = curr.z + n.dz;
                const nKey = core.key(nx, 0, nz);
                const nbNode = state.circuit.get(nKey);

                if (nbNode) {
                    if (nbNode.type === core.COMP.WIRE) {
                        if (n.flow === 'EW') {
                            const curHasVertical = state.circuit.get(core.key(curr.x, 0, curr.z + core.GRID_SIZE))?.type === core.COMP.WIRE ||
                                                   state.circuit.get(core.key(curr.x, 0, curr.z - core.GRID_SIZE))?.type === core.COMP.WIRE;
                            const nbHasVertical = state.circuit.get(core.key(nx, 0, nz + core.GRID_SIZE))?.type === core.COMP.WIRE ||
                                                  state.circuit.get(core.key(nx, 0, nz - core.GRID_SIZE))?.type === core.COMP.WIRE;
                            if (curHasVertical && nbHasVertical) continue;
                        }

                        // Proíbe saltos laterais de sinal cruzando entradas de portas lógicas 74xx
                        let blockedLateral = false;
                        for (const other of state.circuit.values()) {
                            if (!core.GATE_TYPES.has(other.type) && other.type !== core.COMP.DEC_BCD_7SEG) continue;
                            const pins = core.getComponentPins(other);
                            for (const pin of pins.inputs) {
                                if (Math.hypot(curr.x - pin.x, curr.z - pin.z) <= 0.6 || Math.hypot(nx - pin.x, nz - pin.z) <= 0.6) {
                                    if ((other.rot === 0 || other.rot === 2) && n.flow === 'EW') { blockedLateral = true; break; }
                                    if ((other.rot === 1 || other.rot === 3) && n.flow === 'NS') { blockedLateral = true; break; }
                                }
                            }
                            if (blockedLateral) break;
                        }
                        if (blockedLateral) continue;

                        if (!nbNode.state) {
                            nbNode.state = true;
                            nbNode.netColor = curr.color;
                            nbNode.netSourceId = curr.sourceId;
                            queue.push({ x: nx, y: 0, z: nz, color: curr.color, sourceId: curr.sourceId, dir: 'ALL' });
                            propagateAttachedAirWiresFromWire(nx, nz, curr.color, curr.sourceId);
                        }
                    } else if (nbNode.type === 'JUMPER') {
                        if (n.flow === 'NS' && !nbNode.stateNS) {
                            nbNode.stateNS = true; 
                            nbNode.colorNS = curr.color;
                            nbNode.sourceNS = curr.sourceId;
                            queue.push({ x: nx, y: 0, z: nz, color: curr.color, sourceId: curr.sourceId, dir: 'NS' });
                            propagateAttachedAirWiresFromWire(nx, nz, curr.color, curr.sourceId);
                        } else if (n.flow === 'EW' && !nbNode.stateEW) {
                            nbNode.stateEW = true; 
                            nbNode.colorEW = curr.color;
                            nbNode.sourceEW = curr.sourceId;
                            queue.push({ x: nx, y: 0, z: nz, color: curr.color, sourceId: curr.sourceId, dir: 'EW' });
                            propagateAttachedAirWiresFromWire(nx, nz, curr.color, curr.sourceId);
                        }
                    }
                }
            }
        }
    }

    function createAirWireMesh(p1, p2, isFromSwitch, wireId, isSameNode = false) {
        const group = new THREE.Group();
        const dist = p1.distanceTo(p2);
        const distH = Math.hypot(p2.x - p1.x, p2.z - p1.z);

        const baseColor = isFromSwitch ? AIR_SWITCH_OFF : AIR_GATE_OFF;
        const mat = new THREE.MeshStandardMaterial({ color: baseColor, roughness: 0.35, metalness: 0.2 });

        // Plugues Banana Industriais 3D Rígidos (altura vertical 0.18)
        const PLUG_HEIGHT = 0.18;
        const matPinMetal = new THREE.MeshStandardMaterial({ color: 0xD1D5DB, metalness: 0.9, roughness: 0.2 });
        const matPlugSleeve = new THREE.MeshStandardMaterial({ color: baseColor, roughness: 0.45, metalness: 0.15 });
        const matBoot = new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.85 });

        [p1, p2].forEach(p => {
            // Pino metálico condutor inferior (entra no furo do borne)
            const pinMetal = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.032, 0.06, 16), matPinMetal);
            pinMetal.position.set(p.x, p.y + 0.03, p.z);
            pinMetal.castShadow = false;
            pinMetal.receiveShadow = false;
            group.add(pinMetal);

            // Capa isolante plástica colorida (corpo do plugue banana 4mm)
            const sleeve = new THREE.Mesh(new THREE.CylinderGeometry(0.048, 0.048, 0.10, 16), matPlugSleeve);
            sleeve.position.set(p.x, p.y + 0.09, p.z);
            sleeve.castShadow = false;
            sleeve.receiveShadow = false;
            group.add(sleeve);

            // Bocal superior / anel de alívio de tensão de onde sai o cabo
            const boot = new THREE.Mesh(new THREE.CylinderGeometry(0.036, 0.046, 0.04, 16), matBoot);
            boot.position.set(p.x, p.y + 0.16, p.z);
            boot.castShadow = false;
            boot.receiveShadow = false;
            group.add(boot);
        });

        // O cabo sai RIGOROSAMENTE do topo vertical do plugue banana
        const P0 = new THREE.Vector3(p1.x, p1.y + PLUG_HEIGHT, p1.z);
        const P3 = new THREE.Vector3(p2.x, p2.y + PLUG_HEIGHT, p2.z);

        // Elevação de catenária e gravidade aparente quando a distância horizontal é maior
        const catenaryElev = (distH > 2.0) ? 0.25 + Math.min(0.40, distH * 0.06) : Math.min(0.20, distH * 0.08);

        // Pontos de Controle de Bézier:
        // P1 DEVE ter exatamente o mesmo X e Z de P0 -> FORÇA saída 100% vertical
        // P2 DEVE ter exatamente o mesmo X e Z de P3 -> FORÇA descida 100% vertical
        let P1 = new THREE.Vector3(P0.x, P0.y + 0.35 + catenaryElev, P0.z);
        let P2 = new THREE.Vector3(P3.x, P3.y + 0.35 + catenaryElev, P3.z);

        if (isSameNode && dist < 1.5) {
            const sideOffset = (p1.x < 0 ? -1 : 1) * 0.35;
            P1.x += sideOffset;
            P2.x += sideOffset;
            P1.y += 0.20;
            P2.y += 0.20;
        }

        const curve = new THREE.CubicBezierCurve3(P0, P1, P2, P3);
        const geometry = new THREE.TubeGeometry(curve, 36, 0.024, 8, false);

        const cableMesh = new THREE.Mesh(geometry, mat);
        cableMesh.castShadow = false;
        cableMesh.receiveShadow = false;
        cableMesh.userData = { isAirWireMesh: true, wireId: wireId };
        group.add(cableMesh);

        group.userData = { cableMesh, mat, wireId: wireId, matPlugSleeve };
        return group;
    }

    function addAirWire(from, to, skipHistory = false) {
        // Previne cabos aéreos duplicados conectando os mesmos pinos
        const duplicate = state.airWires.some(w => 
            ((w.from.nodeX === from.nodeX && w.from.nodeZ === from.nodeZ && w.from.pinId === from.pinId &&
              w.to.nodeX === to.nodeX && w.to.nodeZ === to.nodeZ && w.to.pinId === to.pinId) ||
             (w.from.nodeX === to.nodeX && w.from.nodeZ === to.nodeZ && w.from.pinId === to.pinId &&
              w.to.nodeX === from.nodeX && w.to.nodeZ === from.nodeZ && w.to.pinId === from.pinId))
        );
        if (duplicate) return;

        const srcNode = state.circuit.get(core.key(from.nodeX, from.nodeY, from.nodeZ));
        const targetNode = state.circuit.get(core.key(to.nodeX, to.nodeY, to.nodeZ));

        let p1 = new THREE.Vector3(from.x !== undefined ? from.x : from.nodeX, from.y ?? -0.38, from.z !== undefined ? from.z : from.nodeZ);
        let p2 = new THREE.Vector3(to.x !== undefined ? to.x : to.nodeX, to.y ?? -0.38, to.z !== undefined ? to.z : to.nodeZ);

        // Recálculo absoluto das coordenadas a partir dos pinos dos nós
        if (srcNode) {
            srcNode.mesh?.updateMatrixWorld(true);
            const srcPins = core.getComponentPins(srcNode);
            const allSrcPins = [...srcPins.outputs, ...srcPins.inputs];
            const pFrom = allSrcPins.find(p => p.id === from.pinId || (p.aliases && p.aliases.includes(from.pinId)));
            if (pFrom) {
                const py = (pFrom.ly !== undefined) ? pFrom.ly : ((pFrom.y !== undefined) ? pFrom.y : -0.38);
                if (srcNode.mesh) {
                    p1 = srcNode.mesh.localToWorld(new THREE.Vector3(pFrom.lx !== undefined ? pFrom.lx : 0, py, pFrom.lz !== undefined ? pFrom.lz : 0));
                } else {
                    p1 = new THREE.Vector3(pFrom.x, (pFrom.y !== undefined) ? pFrom.y : py, pFrom.z);
                }
                from.x = p1.x; from.y = p1.y; from.z = p1.z;
            }
        }

        if (targetNode) {
            targetNode.mesh?.updateMatrixWorld(true);
            const targetPins = core.getComponentPins(targetNode);
            const allTargetPins = [...targetPins.inputs, ...targetPins.outputs];
            const pTo = allTargetPins.find(p => p.id === to.pinId || (p.aliases && p.aliases.includes(to.pinId)));
            if (pTo) {
                const py = (pTo.ly !== undefined) ? pTo.ly : ((pTo.y !== undefined) ? pTo.y : -0.38);
                if (targetNode.mesh) {
                    p2 = targetNode.mesh.localToWorld(new THREE.Vector3(pTo.lx !== undefined ? pTo.lx : 0, py, pTo.lz !== undefined ? pTo.lz : 0));
                } else {
                    p2 = new THREE.Vector3(pTo.x, (pTo.y !== undefined) ? pTo.y : py, pTo.z);
                }
                to.x = p2.x; to.y = p2.y; to.z = p2.z;
            }
        }

        const isFromSwitch = (srcNode && srcNode.type === core.COMP.SWITCH);
        const isSameNode = (from.nodeX === to.nodeX && from.nodeZ === to.nodeZ);
        const wireId = `aw_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

        const groupMesh = createAirWireMesh(p1, p2, isFromSwitch, wireId, isSameNode);
        scene.add(groupMesh);

        const wireObj = {
            id: wireId,
            from: from,
            to: to,
            isFromSwitch: isFromSwitch,
            mesh: groupMesh,
            state: false
        };
        state.airWires.push(wireObj);

        const nFrom = state.circuit.get(core.key(from.nodeX, from.nodeY ?? 0, from.nodeZ));
        const nTo = state.circuit.get(core.key(to.nodeX, to.nodeY ?? 0, to.nodeZ));
        if (nFrom && (nFrom.type === core.COMP.WIRE || nFrom.type === 'JUMPER')) updateWireGeometry(nFrom);
        if (nTo && (nTo.type === core.COMP.WIRE || nTo.type === 'JUMPER')) updateWireGeometry(nTo);

        if (!skipHistory) {
            core.pushHistory({ type: 'addAirWire', data: { from, to, isFromSwitch, id: wireId } });
            updateUndoRedoButtons();
            core.saveCircuitToLocalStorage();
        }

        solveCircuit();
        notify('success', `Cabo conectado: [${from.pinId}] ➔ [${to.pinId}]`);
    }

    function removeAirWireById(id, skipHistory = false) {
        const idx = state.airWires.findIndex(w => w.id === id);
        if (idx !== -1) {
            const wire = state.airWires[idx];
            scene.remove(wire.mesh);
            disposeMeshTree(wire.mesh);
            state.airWires.splice(idx, 1);

            const nFrom = state.circuit.get(core.key(wire.from.nodeX, wire.from.nodeY ?? 0, wire.from.nodeZ));
            const nTo = state.circuit.get(core.key(wire.to.nodeX, wire.to.nodeY ?? 0, wire.to.nodeZ));
            if (nFrom && (nFrom.type === core.COMP.WIRE || nFrom.type === 'JUMPER')) updateWireGeometry(nFrom);
            if (nTo && (nTo.type === core.COMP.WIRE || nTo.type === 'JUMPER')) updateWireGeometry(nTo);

            if (!skipHistory) {
                core.pushHistory({ type: 'removeAirWire', data: { from: wire.from, to: wire.to, isFromSwitch: wire.isFromSwitch, id: wire.id } });
                updateUndoRedoButtons();
                core.saveCircuitToLocalStorage();
            }
            solveCircuit();
            notify('info', 'Ligação aérea removida.');
        }
    }

    function addHose(from, to, silent = false) {
        state.hoses = state.hoses || [];
        if (!from || !to) return null;

        const fromNode = state.circuit.get(core.key(from.nodeX, from.nodeY ?? 0, from.nodeZ));
        const toNode = state.circuit.get(core.key(to.nodeX, to.nodeY ?? 0, to.nodeZ));
        if (fromNode && toNode) {
            const isFluid = (n) => (
                (core.SUPPLY_TYPES && core.SUPPLY_TYPES.has(n.type) && n.type !== core.COMP.POWER_SUPPLY_3P) ||
                (core.VALVE_TYPES && core.VALVE_TYPES.has(n.type)) ||
                (core.CYLINDER_TYPES && core.CYLINDER_TYPES.has(n.type)) ||
                (core.FLOW_CONTROL_TYPES && core.FLOW_CONTROL_TYPES.has(n.type))
            );
            if (!isFluid(fromNode) || !isFluid(toNode)) {
                if (!silent) notify('warning', 'Conexão inválida: Mangueiras só podem ser conectadas em componentes pneumáticos ou hidráulicos!');
                return null;
            }
        }

        const duplicate = state.hoses.some(h => 
            ((h.from.nodeX === from.nodeX && h.from.nodeZ === from.nodeZ && h.from.portId === from.portId &&
              h.to.nodeX === to.nodeX && h.to.nodeZ === to.nodeZ && h.to.portId === to.portId) ||
             (h.from.nodeX === to.nodeX && h.from.nodeZ === to.nodeZ && h.from.portId === to.portId &&
              h.to.nodeX === from.nodeX && h.to.nodeZ === from.nodeZ && h.to.portId === from.portId))
        );
        if (duplicate) return null;

        const hoseId = `hose_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
        const hoseObj = {
            id: hoseId,
            from: from,
            to: to
        };
        state.hoses.push(hoseObj);

        updateFluidLinks();
        if (!silent) {
            core.saveCircuitToLocalStorage();
            notify('success', `Mangueira conectada: [${from.portId || 'P'}] ➔ [${to.portId || 'P'}]`);
        }
        return hoseObj;
    }

    function removeHoseById(id) {
        if (!state.hoses) return;
        const idx = state.hoses.findIndex(h => h.id === id);
        if (idx !== -1) {
            state.hoses.splice(idx, 1);
            updateFluidLinks();
            core.saveCircuitToLocalStorage();
            notify('info', 'Mangueira removida.');
        }
    }

    function refreshAirWireMesh(wire) {
        if (!wire || !wire.mesh || !wire.mesh.userData?.cableMesh) return;
        const srcNode = state.circuit.get(core.key(wire.from.nodeX, wire.from.nodeY, wire.from.nodeZ));
        const targetNode = state.circuit.get(core.key(wire.to.nodeX, wire.to.nodeY, wire.to.nodeZ));

        let p1 = new THREE.Vector3(wire.from.x, wire.from.y, wire.from.z);
        let p2 = new THREE.Vector3(wire.to.x, wire.to.y, wire.to.z);

        if (srcNode) {
            const srcPins = core.getComponentPins(srcNode);
            const allPins = [...srcPins.outputs, ...srcPins.inputs];
            const p = allPins.find(p => p.id === wire.from.pinId);
            if (p) {
                p1 = (srcNode.mesh && p.lx !== undefined) ? srcNode.mesh.localToWorld(new THREE.Vector3(p.lx, -0.38, p.lz)) : new THREE.Vector3(p.x, -0.38, p.z);
                wire.from.x = p1.x; wire.from.y = p1.y; wire.from.z = p1.z;
            }
        }

        if (targetNode) {
            const targetPins = core.getComponentPins(targetNode);
            const allPins = [...targetPins.inputs, ...targetPins.outputs];
            const p = allPins.find(p => p.id === wire.to.pinId);
            if (p) {
                p2 = (targetNode.mesh && p.lx !== undefined) ? targetNode.mesh.localToWorld(new THREE.Vector3(p.lx, -0.38, p.lz)) : new THREE.Vector3(p.x, -0.38, p.z);
                wire.to.x = p2.x; wire.to.y = p2.y; wire.to.z = p2.z;
            }
        }

        const isSameNode = (wire.from.nodeX === wire.to.nodeX && wire.from.nodeZ === wire.to.nodeZ);
        const dist = p1.distanceTo(p2);
        const vMid1 = new THREE.Vector3().lerpVectors(p1, p2, 0.35);
        const vMid2 = new THREE.Vector3().lerpVectors(p1, p2, 0.65);

        if (isSameNode && dist < 1.6) {
            const sideOffset = -0.45;
            vMid1.x += sideOffset;
            vMid2.x += sideOffset;
            vMid1.y = Math.max(p1.y, p2.y) + 0.32;
            vMid2.y = Math.max(p1.y, p2.y) + 0.32;
        } else if (Math.abs(p1.x - p2.x) < 0.05 && Math.abs(p1.z - p2.z) > 0.4) {
            vMid1.x = p1.x;
            vMid2.x = p2.x;
            const elevation = 0.45 + Math.min(0.20, dist * 0.08);
            vMid1.y = Math.max(p1.y, p2.y) + elevation;
            vMid2.y = Math.max(p1.y, p2.y) + elevation;
        } else {
            vMid1.y = Math.max(p1.y, p2.y) + 0.45 + (dist * 0.12);
            vMid2.y = Math.max(p1.y, p2.y) + 0.45 + (dist * 0.12);
        }

        const curve = new THREE.CubicBezierCurve3(p1, vMid1, vMid2, p2);
        const cableMesh = wire.mesh.userData.cableMesh;
        if (cableMesh.geometry) cableMesh.geometry.dispose();
        cableMesh.geometry = new THREE.TubeGeometry(curve, 32, 0.028, 8, false);

        // Atualiza terminais (jacks) nas extremidades
        const jacks = wire.mesh.children.filter(c => c !== cableMesh);
        if (jacks[0]) jacks[0].position.copy(p1);
        if (jacks[1]) jacks[1].position.copy(p2);
    }

    function updateCylinderSensors3D(node) {
        if (!node || !node.mesh) return;
        const p0 = typeof node.posS0 === 'number' ? node.posS0 : 0.05;
        const p1 = typeof node.posS1 === 'number' ? node.posS1 : 0.95;
        const z0 = -0.38 + p0 * 0.76;
        const z1 = -0.38 + p1 * 0.76;

        if (node.mesh.userData.s0) {
            node.mesh.userData.s0.position.z = z0;
        }
        if (node.mesh.userData.s1) {
            node.mesh.userData.s1.position.z = z1;
        }

        const pinEls = node.mesh.userData.pinElements;
        if (pinEls) {
            if (pinEls['S0']) {
                const el = pinEls['S0'];
                if (el.base) { el.base.position.z = z0; el.base.userData.localZ = z0; }
                if (el.light) { el.light.position.z = z0; el.light.userData.localZ = z0; }
                if (el.hitbox) { el.hitbox.position.z = z0; el.hitbox.userData.localZ = z0; }
                if (el.label) { el.label.position.z = z0 - 0.16; el.label.userData.localZ = z0; }
            }
            if (pinEls['S1']) {
                const el = pinEls['S1'];
                if (el.base) { el.base.position.z = z1; el.base.userData.localZ = z1; }
                if (el.light) { el.light.position.z = z1; el.light.userData.localZ = z1; }
                if (el.hitbox) { el.hitbox.position.z = z1; el.hitbox.userData.localZ = z1; }
                if (el.label) { el.label.position.z = z1 - 0.16; el.label.userData.localZ = z1; }
            }
        }

        // Estica dinamicamente todos os cabos aéreos conectados a S0 ou S1
        state.airWires.forEach(w => {
            const connectsToThis = 
                (w.from.nodeX === node.x && w.from.nodeY === node.y && w.from.nodeZ === node.z && (w.from.pinId === 'S0' || w.from.pinId === 'S1')) ||
                (w.to.nodeX === node.x && w.to.nodeY === node.y && w.to.nodeZ === node.z && (w.to.pinId === 'S0' || w.to.pinId === 'S1'));
            if (connectsToThis) {
                refreshAirWireMesh(w);
            }
        });
    }

    function adjustSensorPosition(node, sensorId, delta) {
        if (!node || !core.CYLINDER_TYPES.has(node.type)) return;
        if (sensorId === 'S0') {
            const cur = typeof node.posS0 === 'number' ? node.posS0 : 0.05;
            node.posS0 = Math.max(0.00, Math.min(0.45, Math.round((cur + delta) * 100) / 100));
            notify('info', `Sensor S0 ajustado para ${(node.posS0 * 100).toFixed(0)}% do curso.`);
        } else if (sensorId === 'S1') {
            const cur = typeof node.posS1 === 'number' ? node.posS1 : 0.95;
            node.posS1 = Math.max(0.55, Math.min(1.00, Math.round((cur + delta) * 100) / 100));
            notify('info', `Sensor S1 ajustado para ${(node.posS1 * 100).toFixed(0)}% do curso.`);
        }
        updateCylinderSensors3D(node);
        core.saveCircuitToLocalStorage();
        solveCircuit();
    }

    function updateAirWiresVisuals() {
        state.airWires.forEach(wire => {
            const isHigh = Boolean(wire.state);
            const mat = wire.mesh.userData?.mat;
            const matSleeve = wire.mesh.userData?.matPlugSleeve;
            if (mat) {
                if (wire.isFromSwitch) {
                    const c = isHigh ? AIR_SWITCH_ON : AIR_SWITCH_OFF;
                    mat.color.setHex(c);
                    mat.emissive.setHex(isHigh ? AIR_SWITCH_ON : 0x000000);
                    mat.emissiveIntensity = isHigh ? 1.6 : 0.0;
                    if (matSleeve) matSleeve.color.setHex(c);
                } else {
                    const c = isHigh ? AIR_GATE_ON : AIR_GATE_OFF;
                    mat.color.setHex(c);
                    mat.emissive.setHex(isHigh ? AIR_GATE_ON : 0x000000);
                    mat.emissiveIntensity = isHigh ? 1.6 : 0.0;
                    if (matSleeve) matSleeve.color.setHex(c);
                }
            }
        });
    }

    function runLogicalPass() {
        let stateChanged = false;
        updateWireNetworks();

        state.circuit.forEach(node => {
            if (node.type === core.COMP.WIRE || node.type === 'JUMPER') return;

            const isPinEnergized = (pIn) => {
                for (const aw of state.airWires) {
                    if (!aw.state) continue;
                    const targetNodeKey = core.key(node.x, node.y, node.z);
                    const awToKey = core.key(aw.to.nodeX, aw.to.nodeY, aw.to.nodeZ);
                    const awFromKey = core.key(aw.from.nodeX, aw.from.nodeY, aw.from.nodeZ);
                    const matchesTo = (targetNodeKey === awToKey) && (aw.to.pinId === pIn.id || (pIn.aliases && pIn.aliases.includes(aw.to.pinId)));
                    const matchesFrom = (targetNodeKey === awFromKey) && (aw.from.pinId === pIn.id || (pIn.aliases && pIn.aliases.includes(aw.from.pinId)));
                    if (matchesTo || matchesFrom) return true;
                }

                const snapX = core.snap(pIn.x);
                const snapZ = core.snap(pIn.z);
                const directNode = state.circuit.get(core.key(snapX, 0, snapZ)) || state.circuit.get(core.key(pIn.x, pIn.y, pIn.z));
                if (directNode) {
                    if (directNode.type === core.COMP.WIRE) return Boolean(directNode.state);
                    if (directNode.type === 'JUMPER') return Boolean(directNode.stateNS || directNode.stateEW);
                    if (directNode.outputStates && directNode.outputStates[pIn.id] !== undefined) {
                        return Boolean(directNode.outputStates[pIn.id]);
                    }
                    if (directNode.type === core.COMP.SWITCH || core.BUTTON_TYPES.has(directNode.type) || directNode.type === core.COMP.TIMER_TON) {
                        return Boolean(directNode.state);
                    }
                }
                
                // Tolerância espacial para trilhas PCB próximas ao pino (<= 0.35)
                for (const cand of state.circuit.values()) {
                    if (cand.type === core.COMP.WIRE) {
                        if (Math.hypot(cand.x - pIn.x, cand.z - pIn.z) <= 0.35) {
                            if (cand.state) return true;
                        }
                    } else if (cand.type === 'JUMPER') {
                        if (Math.hypot(cand.x - pIn.x, cand.z - pIn.z) <= 0.35) {
                            if (cand.stateNS || cand.stateEW) return true;
                        }
                    }
                }

                // Conexão direta com saídas de outros componentes vizinhos imediatos
                for (const other of state.circuit.values()) {
                    if (other === node) continue;
                    if (other.type === core.COMP.SWITCH && other.state) {
                        if (Math.hypot(other.x - pIn.x, other.z - pIn.z) <= 0.6) return true;
                    }
                    if (core.BUTTON_TYPES.has(other.type) && other.state) {
                        if (Math.hypot(other.x - pIn.x, other.z - pIn.z) <= 0.6) return true;
                    }
                    if (other.outputStates) {
                        const otherPins = core.getComponentPins(other);
                        const match = otherPins.outputs.find(o => Math.hypot(o.x - pIn.x, o.z - pIn.z) <= 0.35);
                        if (match && other.outputStates[match.id]) return true;
                    }
                }
                return false;
            };

            if (node.type === core.COMP.DISPLAY_7SEG) {
                const cp = core.getComponentPins(node);
                const currentSegs = {};
                cp.inputs.forEach(pIn => { currentSegs[pIn.id] = isPinEnergized(pIn); });
                let diff = false;
                ['a','b','c','d','e','f','g'].forEach(s => {
                    if (node.segmentStates[s] !== currentSegs[s]) {
                        node.segmentStates[s] = currentSegs[s]; 
                        diff = true;
                    }
                });
                if (diff) {
                    const match = core.BCD_TABLE.find(item => ['a','b','c','d','e','f','g'].every(s => Boolean(item.segs[s]) === Boolean(node.segmentStates[s])));
                    node.displayDigit = match ? match.dec : null;
                    stateChanged = true;
                }
            } else if (node.type === core.COMP.RELAY) {
                const cp = core.getComponentPins(node);
                const pinA1 = cp.inputs.find(p => p.id === 'A1');
                const pinCOM = cp.inputs.find(p => p.id === 'COM');
                const coilActive = pinA1 ? isPinEnergized(pinA1) : false;
                const comPower = pinCOM ? isPinEnergized(pinCOM) : true;

                const outNO = coilActive && comPower;
                const outNC = !coilActive && comPower;

                if (!node.outputStates) node.outputStates = {};
                if (node.outputStates.NO !== outNO || node.outputStates.NC !== outNC || node.state !== coilActive) {
                    node.outputStates.NO = outNO;
                    node.outputStates.NC = outNC;
                    if (node.state !== coilActive) {
                        window.LogicCraft?.audio?.playContactorClack(!coilActive);
                    }
                    node.state = coilActive;
                    stateChanged = true;
                }
            } else if (node.type === core.COMP.CONTACTOR_3P) {
                const cp = core.getComponentPins(node);
                const pinA1 = cp.inputs.find(p => p.id === 'A1');
                const pin13 = cp.inputs.find(p => p.id === '13');
                const pin14 = cp.outputs.find(p => p.id === '14');
                const pinL1 = cp.inputs.find(p => p.id === '1/L1' || p.id === 'L1');
                const pinL2 = cp.inputs.find(p => p.id === '3/L2' || p.id === 'L2');
                const pinL3 = cp.inputs.find(p => p.id === '5/L3' || p.id === 'L3');

                const coilActive = pinA1 ? isPinEnergized(pinA1) : false;

                const outT1 = coilActive && (pinL1 ? isPinEnergized(pinL1) : false);
                const outT2 = coilActive && (pinL2 ? isPinEnergized(pinL2) : false);
                const outT3 = coilActive && (pinL3 ? isPinEnergized(pinL3) : false);

                const in13 = pin13 ? isPinEnergized(pin13) : false;
                const in14 = pin14 ? isPinEnergized(pin14) : false;
                const out14 = coilActive && in13;
                const out13 = coilActive && in14;

                if (!node.outputStates) node.outputStates = {};
                const changed = (node.outputStates['2/T1'] !== outT1 ||
                                 node.outputStates['4/T2'] !== outT2 ||
                                 node.outputStates['6/T3'] !== outT3 ||
                                 node.outputStates['14'] !== out14 ||
                                 node.state !== coilActive);

                if (changed) {
                    node.outputStates['2/T1'] = outT1;
                    node.outputStates['T1'] = outT1;
                    node.outputStates['4/T2'] = outT2;
                    node.outputStates['T2'] = outT2;
                    node.outputStates['6/T3'] = outT3;
                    node.outputStates['T3'] = outT3;
                    node.outputStates['14'] = out14;
                    node.outputStates['13'] = out13;
                    if (node.state !== coilActive) {
                        window.LogicCraft?.audio?.playContactorClack(!coilActive);
                    }
                    node.state = coilActive;
                    stateChanged = true;
                }
            } else if (node.type === core.COMP.POWER_SUPPLY_3P) {
                const isPwrOn = (node.manualValue !== false);
                if (!node.outputStates) node.outputStates = {};
                const changed = (node.outputStates.L1 !== isPwrOn || node.state !== isPwrOn);
                if (changed) {
                    node.outputStates.L1 = isPwrOn;
                    node.outputStates.L2 = isPwrOn;
                    node.outputStates.L3 = isPwrOn;
                    node.outputStates.N = false;
                    node.outputStates.PE = false;
                    node.state = isPwrOn;
                    stateChanged = true;
                }
            } else if (node.type === core.COMP.TIMER_TON) {
                const cp = core.getComponentPins(node);
                const pinIN = cp.inputs.find(p => p.id === 'IN');
                const inActive = pinIN ? isPinEnergized(pinIN) : false;
                node.inputActive = inActive;
                const outQ = Boolean(node.timerDone);
                if (!node.outputStates) node.outputStates = {};
                if (node.outputStates.Q !== outQ || node.state !== outQ) {
                    node.outputStates.Q = outQ;
                    node.state = outQ;
                    stateChanged = true;
                }
            } else if (core.BUTTON_TYPES.has(node.type)) {
                let btnActive = false;
                if (node.type === core.COMP.PUSH_BUTTON_NO) btnActive = Boolean(node.isPressed);
                else if (node.type === core.COMP.PUSH_BUTTON_NC) btnActive = !Boolean(node.isPressed);
                else if (node.type === core.COMP.EMERGENCY_STOP) btnActive = !Boolean(node.isEmergencyLocked);
                if (!node.outputStates) node.outputStates = {};
                if (node.outputStates.OUT !== btnActive || node.state !== btnActive) {
                    node.outputStates.OUT = btnActive;
                    node.state = btnActive;
                    stateChanged = true;
                }
            } else if (core.ACTUATOR_TYPES.has(node.type)) {
                const old = node.state;
                const pins = core.getComponentPins(node);
                let energized = false;
                pins.inputs.forEach(pIn => { if (isPinEnergized(pIn)) energized = true; });
                node.state = energized;
                if (old !== node.state) {
                    stateChanged = true;
                    if (node.type === core.COMP.BUZZER) {
                        window.LogicCraft?.audio?.setBuzzerActive(energized);
                    }
                }
            } else if (core.GATE_TYPES.has(node.type)) {
                const cp = core.getComponentPins(node);
                node.evaluatedInputs = cp.inputs.map(pIn => isPinEnergized(pIn));
                const oldState = node.state;
                node.state = core.computeGateLogic(node.type, node.evaluatedInputs);
                node.outputStates = { S: node.state };
                if (oldState !== node.state) stateChanged = true;
            } else if (node.type === core.COMP.DEC_BCD_7SEG) {
                const cp = core.getComponentPins(node);
                const inMap = {};
                cp.inputs.forEach(pIn => { inMap[pIn.id] = isPinEnergized(pIn); });
                node.evaluatedInputsMap = inMap;
                const res = core.computeBcd7Segment(inMap);
                ['a','b','c','d','e','f','g'].forEach(s => {
                    if (node.outputStates[s] !== res.segments[s]) {
                        node.outputStates[s] = res.segments[s]; 
                        stateChanged = true;
                    }
                });
                node.valid = res.valid; 
                node.state = res.valid;
            } else if (core.VALVE_TYPES.has(node.type)) {
                const cp = core.getComponentPins(node);
                const pinY1 = cp.inputs.find(p => p.id === 'Y1' || p.id === 'YH1' || p.id === 'A1');
                const pinY2 = cp.inputs.find(p => p.id === 'Y2' || p.id === 'YH2' || p.id === 'A2');
                const y1Direct = pinY1 ? isPinEnergized(pinY1) : false;
                const y2Direct = pinY2 ? isPinEnergized(pinY2) : false;
                const y1Active = y1Direct || Boolean(node.linkedSolenoidY1 && node.linkedSolenoidY1.state);
                const y2Active = y2Direct || Boolean(node.linkedSolenoidY2 && node.linkedSolenoidY2.state);
                const isMono = (node.type === core.COMP.VALVE_3_2_MONO || node.type === core.COMP.VALVE_5_2_MONO || node.type === core.COMP.VALVE_4_2_MONO);
                const oldSpool = node.spoolPosition ?? 0;
                const oldState = node.state ?? false;

                if (isMono) {
                    node.spoolPosition = y1Active ? 1.0 : 0.0;
                    node.state = y1Active;
                } else {
                    if (y1Active && !y2Active) {
                        node.spoolPosition = 1.0;
                        node.state = true;
                    } else if (y2Active && !y1Active) {
                        node.spoolPosition = 0.0;
                        node.state = false;
                    }
                }
                if (node.spoolPosition !== oldSpool || node.state !== oldState) {
                    stateChanged = true;
                    window.LogicCraft?.audio?.playPneumaticPuff(0.18, 0.40);
                }
            }
        });
        return stateChanged;
    }

    function propagateSignals() {
        state.circuit.forEach(n => {
            if (n.type === core.COMP.SWITCH) {
                n.state = n.manualValue;
                n.outputStates = { [n.switchId]: n.manualValue };
            } else if (n.type === core.COMP.PUSH_BUTTON_NO) {
                n.state = Boolean(n.isPressed);
                n.outputStates = { OUT: n.state };
            } else if (n.type === core.COMP.PUSH_BUTTON_NC) {
                n.state = !Boolean(n.isPressed);
                n.outputStates = { OUT: n.state };
            } else if (n.type === core.COMP.EMERGENCY_STOP) {
                n.state = !Boolean(n.isEmergencyLocked);
                n.outputStates = { OUT: n.state };
            }
        });
        let stable = false, passes = 0, maxPasses = 40;
        while (!stable && passes < maxPasses) { passes++; stable = !runLogicalPass(); }
        updateFluidLinks();
        return { stable, passes };
    }

    function updateVisuals() {
        updateAirWiresVisuals();

        state.circuit.forEach((node, k) => {
            if (!node || !node.mesh) return;
            if (node.type === core.COMP.WIRE || node.type === 'JUMPER') {
                updateWireGeometry(node);
            } else if (node.type === core.COMP.SWITCH) {
                const scr = node.mesh.userData.screen;
                const conf = core.INPUT_CONFIG.find(c => c.id === node.switchId) ?? core.INPUT_CONFIG[0];
                if (scr) {
                    if (scr.userData.lastTextureState !== node.state) {
                        if (scr.material.map) scr.material.map.dispose();
                        scr.material.map = createSwitchTexture(node.state, conf);
                        scr.material.map.needsUpdate = true;
                        scr.userData.lastTextureState = node.state;
                    }
                }
            } else if (node.type === core.COMP.PUSH_BUTTON_NO || node.type === core.COMP.PUSH_BUTTON_NC) {
                const cap = node.mesh.userData.buttonCap;
                if (cap) {
                    cap.position.y = node.isPressed ? -0.25 : -0.19;
                    const isNO = (node.type === core.COMP.PUSH_BUTTON_NO);
                    const isHigh = Boolean(node.state);
                    if (isNO) {
                        cap.material.emissive.setHex(isHigh ? 0x22C55E : 0x052e16);
                        cap.material.emissiveIntensity = isHigh ? 1.4 : 0.2;
                    } else {
                        cap.material.emissive.setHex(!isHigh ? 0xEF4444 : 0x450a0a);
                        cap.material.emissiveIntensity = !isHigh ? 1.4 : 0.2;
                    }
                }
            } else if (node.type === core.COMP.RELAY) {
                const ledArm = node.mesh.userData.relayArmLed;
                const armature = node.mesh.userData.armaturePlate;
                if (ledArm) {
                    ledArm.material.color.setHex(node.state ? 0x22C55E : 0x052E16);
                    ledArm.material.emissive.setHex(node.state ? 0x22C55E : 0x000000);
                    ledArm.material.emissiveIntensity = node.state ? 2.5 : 0.0;
                }
                if (armature) {
                    armature.rotation.z = node.state ? -0.20 : 0.0;
                    armature.position.x = node.state ? 0.00 : 0.04;
                }
            } else if (node.type === core.COMP.DEC_BCD_7SEG) {
                const outMeshes = node.mesh.userData.outputMeshes;
                if (outMeshes) {
                    ['a','b','c','d','e','f','g'].forEach(s => {
                        const isAct = Boolean(node.outputStates?.[s]);
                        const m = outMeshes[s];
                        if (m) {
                            m.material.color.setHex(isAct ? COLOR_HIGH : COLOR_LOW);
                            m.material.emissive.setHex(isAct ? COLOR_HIGH : 0x000000);
                            m.material.emissiveIntensity = isAct ? 1.2 : 0.0;
                        }
                    });
                }
            } else if (node.type === core.COMP.DISPLAY_7SEG) {
                const segMeshes = node.mesh.userData.segmentMeshes;
                if (segMeshes) {
                    ['a','b','c','d','e','f','g'].forEach(s => {
                        const isAct = Boolean(node.segmentStates?.[s]);
                        const sm = segMeshes[s];
                        if (sm) { 
                            sm.material.color.setHex(isAct ? 0xEF4444 : 0x1E293B); 
                            sm.material.emissive.setHex(isAct ? 0xEF4444 : 0x000000);
                            sm.material.emissiveIntensity = isAct ? 2.8 : 0.0; 
                        }
                    });
                }
            } else if (node.type === core.COMP.LED_RED || node.type === core.COMP.LAMP || node.type === core.COMP.BUZZER) {
                const lens = node.mesh.userData.lens;
                const light = node.mesh.userData.pilotLight;
                const isRed = (node.type === core.COMP.LED_RED);
                const isBuzzer = (node.type === core.COMP.BUZZER);
                if (lens) {
                    if (isBuzzer) {
                        lens.material.color.setHex(node.state ? 0xEF4444 : 0x1E293B);
                        lens.material.emissive.setHex(node.state ? 0xEF4444 : 0x000000);
                        lens.material.emissiveIntensity = node.state ? 2.0 : 0.0;
                    } else if (isRed) {
                        lens.material.color.setHex(node.state ? 0xFF2222 : 0x7F1D1D);
                        lens.material.emissive.setHex(node.state ? 0xEF4444 : 0x000000);
                        lens.material.emissiveIntensity = node.state ? 2.5 : 0.05;
                    } else {
                        lens.material.color.setHex(node.state ? 0xFEF08A : 0x854D0E);
                        lens.material.emissive.setHex(node.state ? 0xFACC15 : 0x000000);
                        lens.material.emissiveIntensity = node.state ? 2.2 : 0.05;
                    }
                }
                if (light) {
                    light.intensity = node.state ? 2.2 : 0.0;
                }
            } else if (node.type === core.COMP.SOLENOID_PNEU || node.type === core.COMP.SOLENOID_HYDRA) {
                const indicator = node.mesh.userData.indicator;
                if (indicator) {
                    indicator.material.emissive.setHex(node.state ? (node.type === core.COMP.SOLENOID_PNEU ? 0x0EA5E9 : 0x38bdf8) : 0x000000);
                    indicator.material.emissiveIntensity = node.state ? 2.2 : 0;
                }
            } else if (core.VALVE_TYPES.has(node.type)) {
                const spool = node.mesh.userData.spool;
                if (spool) spool.position.x = node.state ? -0.20 : -0.38;
            }

            const isFocused = (k === state.focusedNodeKey);
            const hl = node.mesh?.children ? node.mesh.children.find(c => c.name === 'focusHighlight') : null;
            if (hl) hl.visible = isFocused;
        });

        if (window.LogicCraft.ui) {
            if (state.currentOpenModalType === 'truth-table' && window.LogicCraft.ui.renderTruthTableModal) window.LogicCraft.ui.renderTruthTableModal();
            else if (state.currentOpenModalType === 'fundamentals' && window.LogicCraft.ui.renderFundamentalsModal) window.LogicCraft.ui.renderFundamentalsModal();
            else if (state.currentOpenModalType === 'karnaugh' && window.LogicCraft.ui.renderKarnaughModal) window.LogicCraft.ui.renderKarnaughModal();
        }
    }

    function solveCircuit() {
        propagateSignals();
        if (window.LogicCraft && window.LogicCraft.machines && window.LogicCraft.machines.solvePowerCircuit) {
            window.LogicCraft.machines.solvePowerCircuit(state.circuit, state.airWires);
        }
        updateVisuals();
    }

    function toggleSwitch(node) {
        if (!node || node.type !== core.COMP.SWITCH) return;
        const oldVal = node.manualValue;
        node.manualValue = !node.manualValue;
        window.LogicCraft?.audio?.playClick(!node.manualValue);
        core.pushHistory({ type: 'toggleSwitch', data: { x: node.x, y: node.y, z: node.z, oldVal, newVal: node.manualValue } });
        updateUndoRedoButtons();
        core.saveCircuitToLocalStorage();
        notify('info', `Chave ${node.switchId}: ${node.manualValue ? '1 (HIGH)' : '0 (LOW)'}`);
        solveCircuit();
    }

    function addBlock(x, y, z, config, skipHistory = false) {
        const k = core.key(x, y, z);
        if (state.circuit.has(k)) {
            notify('warning', 'Posição ocupada: selecione outro ponto na malha.');
            return false;
        }

        let inCount = config.inputCount ?? 2;
        if (config.type === core.COMP.NOT || config.type === core.COMP.BUFFER) inCount = 1;
        else if (config.type === core.COMP.DEC_BCD_7SEG) inCount = 4;
        else if (config.type === core.COMP.DISPLAY_7SEG) inCount = 7;
        else if (config.type === core.COMP.RELAY) inCount = 2;
        else if (core.GATE_TYPES.has(config.type) && inCount < 2) inCount = 2;

        const rotToUse = (config.rot !== undefined) ? config.rot : state.currentRotation;

        const node = {
            x: core.snap(x), y: core.snap(y), z: core.snap(z),
            type: config.type,
            rot: rotToUse,
            tag: config.tag || null,
            switchId: config.switchId ?? 'A',
            btnId: config.btnId || null,
            isPressed: Boolean(config.isPressed),
            isEmergencyLocked: Boolean(config.isEmergencyLocked),
            pressureBar: typeof config.pressureBar === 'number' ? config.pressureBar : 0,
            openingPercent: typeof config.openingPercent === 'number' ? config.openingPercent : 100,
            delayTime: typeof config.delayTime === 'number' ? config.delayTime : 2.0,
            elapsedTime: typeof config.elapsedTime === 'number' ? config.elapsedTime : 0.0,
            posS0: typeof config.posS0 === 'number' ? config.posS0 : 0.05,
            posS1: typeof config.posS1 === 'number' ? config.posS1 : 0.95,
            manualValue: Boolean(config.manualValue),
            inputCount: inCount,
            evaluatedInputs: [], evaluatedInputsMap: {},
            outputStates: config.outputStates ? { ...config.outputStates } : {},
            valid: true, errorMessage: null, activePin: null,
            state: Boolean(config.state),
            extension: typeof config.extension === 'number' ? config.extension : 0.0,
            targetExtension: typeof config.targetExtension === 'number' ? config.targetExtension : (typeof config.extension === 'number' ? config.extension : 0.0),
            spoolPosition: typeof config.spoolPosition === 'number' ? config.spoolPosition : 0.0,
            motionState: config.motionState || 'RECUADO',
            sensorS0: config.sensorS0 !== undefined ? Boolean(config.sensorS0) : true,
            sensorS1: config.sensorS1 !== undefined ? Boolean(config.sensorS1) : false,
            displayDigit: null,
            segmentStates: { a:false, b:false, c:false, d:false, e:false, f:false, g:false },
            linkedValve: null, linkedSolenoidY1: null, linkedSolenoidY2: null, linkedCylinder: null,
            mesh: null
        };

        node.mesh = buildComponentMesh(node);
        node.mesh.position.set(node.x, node.y, node.z);
        node.mesh.updateMatrixWorld(true);

        // Sincroniza 3D imediatamente para atuadores com extensão prévia
        if (node.extension > 0) {
            const rod = node.mesh.userData.pistonRod;
            const head = node.mesh.userData.pistonHead;
            const nut = node.mesh.userData.pistonNut;
            if (rod && head) {
                const travelDist = node.extension * 0.70;
                rod.position.z = 0.45 + travelDist;
                head.position.z = rod.position.z + 0.595;
                if (nut) nut.position.z = rod.position.z + 0.540;
            }
        }
        if (node.mesh.userData.spool && node.spoolPosition > 0.5) {
            node.mesh.userData.spool.position.x = -0.20;
        }

        scene.add(node.mesh);
        state.circuit.set(k, node);

        // Define foco no componente que acabou de ser inserido
        state.focusedNodeKey = k;

        if (node.type === core.COMP.WIRE || node.type === 'JUMPER') updateWireGeometry(node);
        refreshNeighborWires(node.x, node.y, node.z);

        const cp = core.getComponentPins(node);
        cp.outputs.forEach(p => refreshNeighborWires(p.x, p.y, p.z));
        cp.inputs.forEach(p => refreshNeighborWires(p.x, p.y, p.z));

        updateFluidLinks();
        
        if (!skipHistory) {
            core.pushHistory({ type: 'add', data: { x: node.x, y: node.y, z: node.z, config: config } });
            updateUndoRedoButtons();
            core.saveCircuitToLocalStorage();
        }

        solveCircuit();
        return true;
    }

    function removeBlock(x, y, z, skipHistory = false) {
        const k = core.key(x, y, z);
        if (!state.circuit.has(k)) return;
        const n = state.circuit.get(k);
        const pinCoords = [];
        const cp = core.getComponentPins(n);
        cp.outputs.forEach(p => pinCoords.push([p.x, p.y, p.z]));
        cp.inputs.forEach(p => pinCoords.push([p.x, p.y, p.z]));
        
        const wiresToRemove = state.airWires.filter(w => 
            (w.from.nodeX === n.x && w.from.nodeZ === n.z) || 
            (w.to.nodeX === n.x && w.to.nodeZ === n.z)
        );
        wiresToRemove.forEach(w => removeAirWireById(w.id, true));

        if (!skipHistory) {
            const nodeSnapshot = {
                type: n.type,
                x: n.x, y: n.y, z: n.z,
                rot: n.rot,
                tag: n.tag,
                switchId: n.switchId,
                manualValue: n.manualValue,
                inputCount: n.inputCount,
                extension: n.extension,
                targetExtension: n.targetExtension,
                spoolPosition: n.spoolPosition,
                motionState: n.motionState,
                sensorS0: n.sensorS0,
                sensorS1: n.sensorS1,
                state: n.state,
                outputStates: n.outputStates ? { ...n.outputStates } : undefined
            };
            core.pushHistory({ type: 'remove', data: { nodeData: nodeSnapshot, removedWires: wiresToRemove } });
            updateUndoRedoButtons();
            core.saveCircuitToLocalStorage();
        }

        if (state.focusedNodeKey === k) {
            state.focusedNodeKey = null;
            if (window.LogicCraft.ui && window.LogicCraft.ui.closeAllModals) window.LogicCraft.ui.closeAllModals();
        }

        scene.remove(n.mesh);
        disposeMeshTree(n.mesh);
        state.circuit.delete(k);

        refreshNeighborWires(n.x, n.y, n.z);
        pinCoords.forEach(([px, py, pz]) => refreshNeighborWires(px, py, pz));

        updateFluidLinks();
        solveCircuit();
    }

    function clearCircuit(skipHistory = false) {
        if(window.AudioEngine) AudioEngine.stopAll();
        if (window.LogicCraft?.audio?.stopAllAudio) window.LogicCraft.audio.stopAllAudio();
        if (window.LogicCraft?.conveyorCell?.isActive && window.LogicCraft.conveyorCell.isActive()) {
            window.LogicCraft.conveyorCell.closeDemo();
        }
        if(window.LogicCraftGuard && window.LogicCraftGuard.purgeOrphanMaterials) LogicCraftGuard.purgeOrphanMaterials(scene);
        if (!skipHistory && state.circuit.size > 0) {
            core.pushHistory({ type: 'clear', data: { nodes: core.serializeCircuit(), airWires: [...state.airWires] } });
            updateUndoRedoButtons();
            core.saveCircuitToLocalStorage();
        }
        
        state.airWires.forEach(w => {
            scene.remove(w.mesh);
            disposeMeshTree(w.mesh);
        });
        state.airWires = [];
        state.hoses = [];
        airWireStart = null;

        state.circuit.forEach(n => {
            if (n && n.mesh) {
                scene.remove(n.mesh);
                disposeMeshTree(n.mesh);
            }
        });
        state.circuit.clear();

        if (fluidHosesGroup) {
            while (fluidHosesGroup.children.length > 0) {
                const child = fluidHosesGroup.children[0];
                if (child.geometry) child.geometry.dispose();
                if (child.material) {
                    const mats = Array.isArray(child.material) ? child.material : [child.material];
                    mats.forEach(m => {
                        if (m.map && m.map.dispose) m.map.dispose();
                        m.dispose();
                    });
                }
                fluidHosesGroup.remove(child);
            }
        }
        state.focusedNodeKey = null;
        core.clearKarnaughGroups();
        if (window.LogicCraft.ui && window.LogicCraft.ui.closeAllModals) window.LogicCraft.ui.closeAllModals();
        notify('info', 'Bancada desobstruída.');
        solveCircuit();
    }

    function undoLastAction() {
        if (state.history.length === 0) return;
        const action = state.history.pop();
        state.redoStack.push(action);
        
        if (action.type === 'add') {
            removeBlock(action.data.x, action.data.y, action.data.z, true);
        } else if (action.type === 'remove') {
            addBlock(action.data.nodeData.x, action.data.nodeData.y, action.data.nodeData.z, action.data.nodeData, true);
            const n = state.circuit.get(core.key(action.data.nodeData.x, action.data.nodeData.y, action.data.nodeData.z));
            if (n) {
                n.rot = action.data.nodeData.rot;
                n.mesh.rotation.y = n.rot * (Math.PI / 2);
                n.manualValue = action.data.nodeData.manualValue;
                n.tag = action.data.nodeData.tag || null;
                if (typeof action.data.nodeData.extension === 'number') {
                    n.extension = action.data.nodeData.extension;
                    n.targetExtension = action.data.nodeData.targetExtension ?? n.extension;
                    n.motionState = action.data.nodeData.motionState ?? 'RECUADO';
                    n.sensorS0 = action.data.nodeData.sensorS0 ?? (n.extension <= 0.04);
                    n.sensorS1 = action.data.nodeData.sensorS1 ?? (n.extension >= 0.96);
                    const rod = n.mesh.userData.pistonRod;
                    const head = n.mesh.userData.pistonHead;
                    const nut = n.mesh.userData.pistonNut;
                    if (rod && head) {
                        const travelDist = n.extension * 0.70;
                        rod.position.z = 0.45 + travelDist;
                        head.position.z = rod.position.z + 0.595;
                        if (nut) nut.position.z = rod.position.z + 0.540;
                    }
                    if (n.mesh.userData.s0 && n.mesh.userData.s1) {
                        n.mesh.userData.s0.material.color.setHex(n.sensorS0 ? 0xef4444 : 0x334155);
                        n.mesh.userData.s0.material.emissiveIntensity = n.sensorS0 ? 1.5 : 0;
                        n.mesh.userData.s1.material.color.setHex(n.sensorS1 ? 0x22c55e : 0x334155);
                        n.mesh.userData.s1.material.emissiveIntensity = n.sensorS1 ? 1.5 : 0;
                    }
                }
                if (typeof action.data.nodeData.spoolPosition === 'number') {
                    n.spoolPosition = action.data.nodeData.spoolPosition;
                    if (n.mesh.userData.spool) {
                        n.mesh.userData.spool.position.x = (n.spoolPosition > 0.5) ? -0.20 : -0.38;
                    }
                }
                if (action.data.nodeData.outputStates) {
                    n.outputStates = { ...action.data.nodeData.outputStates };
                }
                if (action.data.nodeData.state !== undefined) {
                    n.state = action.data.nodeData.state;
                }
                if (n.mesh.userData.armaturePlate) {
                    n.mesh.userData.armaturePlate.rotation.z = n.state ? -0.18 : 0.0;
                    n.mesh.userData.armaturePlate.position.x = n.state ? 0.01 : 0.04;
                }
            }
            if (action.data.removedWires) {
                action.data.removedWires.forEach(w => {
                    const fromNode = state.circuit.get(core.key(w.from.nodeX, w.from.nodeY, w.from.nodeZ));
                    const toNode = state.circuit.get(core.key(w.to.nodeX, w.to.nodeY, w.to.nodeZ));
                    if (fromNode && toNode) {
                        const fromPins = core.getComponentPins(fromNode);
                        const toPins = core.getComponentPins(toNode);
                        const allFrom = [...fromPins.outputs, ...fromPins.inputs];
                        const allTo = [...toPins.outputs, ...toPins.inputs];
                        const pFrom = allFrom.find(p => p.id === w.from.pinId);
                        const pTo = allTo.find(p => p.id === w.to.pinId);
                        const updatedFrom = pFrom ? { ...w.from, x: pFrom.x, y: pFrom.y, z: pFrom.z } : w.from;
                        const updatedTo = pTo ? { ...w.to, x: pTo.x, y: pTo.y, z: pTo.z } : w.to;
                        addAirWire(updatedFrom, updatedTo, true);
                    } else {
                        addAirWire(w.from, w.to, true);
                    }
                });
            }
        } else if (action.type === 'rotate') {
            const n = state.circuit.get(core.key(action.data.x, action.data.y, action.data.z));
            if (n) {
                n.rot = action.data.oldRot;
                n.mesh.rotation.y = n.rot * (Math.PI / 2);
            }
        } else if (action.type === 'toggleSwitch') {
            const n = state.circuit.get(core.key(action.data.x, action.data.y, action.data.z));
            if (n) n.manualValue = action.data.oldVal;
        } else if (action.type === 'addAirWire') {
            removeAirWireById(action.data.id, true);
        } else if (action.type === 'removeAirWire') {
            addAirWire(action.data.from, action.data.to, true);
        } else if (action.type === 'clear') {
            action.data.nodes.forEach(item => {
                addBlock(item.x, item.y, item.z, item, true);
                const n = state.circuit.get(core.key(item.x, item.y, item.z));
                if (n) {
                    n.rot = item.rot;
                    n.mesh.rotation.y = n.rot * (Math.PI / 2);
                    n.manualValue = item.manualValue;
                    n.tag = item.tag || null;
                    if (typeof item.extension === 'number') {
                        n.extension = item.extension;
                        n.targetExtension = item.targetExtension ?? n.extension;
                        n.motionState = item.motionState ?? 'RECUADO';
                        n.sensorS0 = item.sensorS0 ?? (n.extension <= 0.04);
                        n.sensorS1 = item.sensorS1 ?? (n.extension >= 0.96);
                        const rod = n.mesh.userData.pistonRod;
                        const head = n.mesh.userData.pistonHead;
                        const nut = n.mesh.userData.pistonNut;
                        if (rod && head) {
                            const travelDist = n.extension * 0.70;
                            rod.position.z = 0.45 + travelDist;
                            head.position.z = rod.position.z + 0.595;
                            if (nut) nut.position.z = rod.position.z + 0.540;
                        }
                    }
                    if (typeof item.spoolPosition === 'number' && n.mesh.userData.spool) {
                        n.spoolPosition = item.spoolPosition;
                        n.mesh.userData.spool.position.x = (n.spoolPosition > 0.5) ? -0.20 : -0.38;
                    }
                    if (item.outputStates) n.outputStates = { ...item.outputStates };
                    if (item.state !== undefined) n.state = item.state;
                }
            });
            if (action.data.airWires) {
                action.data.airWires.forEach(w => {
                    const fromNode = state.circuit.get(core.key(w.from.nodeX, w.from.nodeY, w.from.nodeZ));
                    const toNode = state.circuit.get(core.key(w.to.nodeX, w.to.nodeY, w.to.nodeZ));
                    if (fromNode && toNode) {
                        const fromPins = core.getComponentPins(fromNode);
                        const toPins = core.getComponentPins(toNode);
                        const allFrom = [...fromPins.outputs, ...fromPins.inputs];
                        const allTo = [...toPins.outputs, ...toPins.inputs];
                        const pFrom = allFrom.find(p => p.id === w.from.pinId);
                        const pTo = allTo.find(p => p.id === w.to.pinId);
                        const updatedFrom = pFrom ? { ...w.from, x: pFrom.x, y: pFrom.y, z: pFrom.z } : w.from;
                        const updatedTo = pTo ? { ...w.to, x: pTo.x, y: pTo.y, z: pTo.z } : w.to;
                        addAirWire(updatedFrom, updatedTo, true);
                    } else {
                        addAirWire(w.from, w.to, true);
                    }
                });
            }
        }
        
        solveCircuit();
        updateUndoRedoButtons();
        core.saveCircuitToLocalStorage();
        notify('info', 'Ação desfeita.');
    }

    function redoLastAction() {
        if (!state.redoStack || state.redoStack.length === 0) return;
        const action = state.redoStack.pop();
        state.history.push(action);

        if (action.type === 'add') {
            addBlock(action.data.x, action.data.y, action.data.z, action.data.config, true);
        } else if (action.type === 'remove') {
            removeBlock(action.data.nodeData.x, action.data.nodeData.y, action.data.nodeData.z, true);
        } else if (action.type === 'rotate') {
            const n = state.circuit.get(core.key(action.data.x, action.data.y, action.data.z));
            if (n) {
                n.rot = action.data.newRot;
                n.mesh.rotation.y = n.rot * (Math.PI / 2);
            }
        } else if (action.type === 'toggleSwitch') {
            const n = state.circuit.get(core.key(action.data.x, action.data.y, action.data.z));
            if (n) n.manualValue = action.data.newVal;
        } else if (action.type === 'addAirWire') {
            addAirWire(action.data.from, action.data.to, true);
        } else if (action.type === 'removeAirWire') {
            removeAirWireById(action.data.id, true);
        } else if (action.type === 'clear') {
            clearCircuit(true);
        }

        solveCircuit();
        updateUndoRedoButtons();
        core.saveCircuitToLocalStorage();
        notify('info', 'Ação refeita.');
    }

    function exportCircuitToFile() {
        const data = core.serializeCircuit();
        if (data.length === 0) {
            notify('warning', 'Bancada vazia: nada a exportar.');
            return;
        }
        const payload = {
            components: data,
            airWires: state.airWires.map(w => ({ from: w.from, to: w.to, isFromSwitch: w.isFromSwitch })),
            hoses: (state.hoses || []).map(h => ({ from: h.from, to: h.to, hoseType: h.hoseType }))
        };
        const json = JSON.stringify(payload, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `logiccraft-circuito-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        notify('success', `Exportado: ${data.length} nós, ${state.airWires.length} cabos e ${(state.hoses || []).length} mangueiras.`);
    }

    function importCircuitFromFile(jsonData) {
        let parsed;
        try {
            parsed = JSON.parse(jsonData);
        } catch (e) {
            notify('error', 'Falha ao importar: arquivo malformado.');
            return;
        }

        const componentsData = Array.isArray(parsed) ? parsed : (parsed.components || []);
        const airWiresData = Array.isArray(parsed) ? [] : (parsed.airWires || []);
        const hosesData = Array.isArray(parsed) ? [] : (parsed.hoses || []);

        clearCircuit(true);

        const prevRotation = state.currentRotation;
        let successCount = 0;
        componentsData.forEach(item => {
            state.currentRotation = (item.rot !== undefined) ? item.rot : 0;
            const ok = addBlock(item.x, item.y, item.z, {
                type: item.type,
                rot: item.rot,
                tag: item.tag,
                switchId: item.switchId,
                btnId: item.btnId,
                isPressed: item.isPressed,
                isEmergencyLocked: item.isEmergencyLocked,
                pressureBar: item.pressureBar,
                openingPercent: item.openingPercent,
                delayTime: item.delayTime,
                elapsedTime: item.elapsedTime,
                posS0: item.posS0,
                posS1: item.posS1,
                manualValue: item.manualValue,
                inputCount: item.inputCount,
                state: item.state,
                extension: item.extension,
                targetExtension: item.targetExtension,
                spoolPosition: item.spoolPosition,
                outputStates: item.outputStates
            }, true);
            if (ok) {
                const node = state.circuit.get(core.key(item.x, item.y, item.z));
                if (node) {
                    node.manualValue = Boolean(item.manualValue);
                    if (item.tag) node.tag = item.tag;
                    if (typeof item.pressureBar === 'number') node.pressureBar = item.pressureBar;
                    if (typeof item.openingPercent === 'number') node.openingPercent = item.openingPercent;
                    if (typeof item.delayTime === 'number') node.delayTime = item.delayTime;
                    if (typeof item.posS0 === 'number') node.posS0 = item.posS0;
                    if (typeof item.posS1 === 'number') node.posS1 = item.posS1;
                    if (item.isEmergencyLocked !== undefined) node.isEmergencyLocked = Boolean(item.isEmergencyLocked);
                    if (item.btnId) node.btnId = item.btnId;
                    if (item.isPressed !== undefined) node.isPressed = Boolean(item.isPressed);
                }
                successCount++;
            }
        });
        state.currentRotation = prevRotation;

        airWiresData.forEach(w => addAirWire(w.from, w.to, true));
        hosesData.forEach(h => addHose(h.from, h.to, true));

        updateFluidLinks();
        solveCircuit();
        notify('success', `Importação concluída: ${successCount} componentes.`);
    }

    function resetCameraView() {
        camera.position.set(0, 4.2, 6.2);
        yaw = 0; pitch = -0.42;
        moveF = false; moveB = false; moveL = false; moveR = false; moveUp = false; moveDown = false;
        _tempEuler.set(pitch, yaw, 0, 'YXZ');
        camera.quaternion.setFromEuler(_tempEuler);
        notify('info', 'Câmera reposicionada.');
    }

    function getTargetUnderCrosshair(rayCoords = _zeroVector) {
        raycaster.setFromCamera(rayCoords, camera);
        const candidates = [];
        
        state.airWires.forEach(w => {
            w.mesh.traverse(ch => { if (ch.isMesh) candidates.push(ch); });
        });

        state.circuit.forEach(c => {
            if (c && c.mesh) {
                c.mesh.traverse(ch => {
                    if (ch.isMesh && ch.name !== 'holo' && ch.name !== 'focusHighlight') {
                        candidates.push(ch);
                    }
                });
            }
        });

        const hits = raycaster.intersectObjects(candidates, false);
        if (hits.length > 0 && hits[0].distance < 30) {
            const hit = hits[0];
            let obj = hit.object;

            if (obj.userData?.isAirWireMesh) {
                return { isAirWire: true, wireId: obj.userData.wireId, hitPoint: hit.point };
            }

            // Busca estrita pela raiz direta do grupo na cena e identificação de pinData / sensorData
            let pinData = null;
            let sensorData = null;
            let currObj = obj;
            while (currObj && currObj !== scene) {
                if (currObj.userData?.isSensor) {
                    sensorData = currObj.userData;
                    break;
                }
                if (currObj.userData?.isPin) {
                    pinData = currObj.userData;
                    if (pinData.pinId === 'S0' || pinData.pinId === 'S1') {
                        sensorData = { isSensor: true, sensorId: pinData.pinId };
                    }
                    break;
                }
                if (currObj.parent === scene) break;
                currObj = currObj.parent;
            }

            let isSwitchBtn = obj.userData?.isSwitchButton;

            let rootObj = obj;
            while (rootObj.parent && rootObj.parent !== scene) {
                rootObj = rootObj.parent;
            }

            const worldNormal = hit.face ? hit.face.normal.clone().transformDirection(obj.matrixWorld).round() : new THREE.Vector3(0, 1, 0);
            const node = state.circuit.get(core.key(rootObj.position.x, rootObj.position.y, rootObj.position.z));
            
            return { 
                node, 
                worldNormal, 
                pos: rootObj.position, 
                pinData, 
                sensorData,
                isSwitchBtn, 
                hitPoint: hit.point 
            };
        }
        return null;
    }

    function setupEvents() {
        let isOrbiting = false;
        let rightDownX = 0, rightDownY = 0;
        let rightDragged = false;
        let isPointerLocked = false;

        const crosshairEl = document.getElementById('crosshair');
        const pointerHintEl = document.getElementById('pointer-hint');

        document.addEventListener('pointerlockchange', () => {
            isPointerLocked = (document.pointerLockElement === renderer.domElement);
            if (crosshairEl) {
                crosshairEl.style.display = isPointerLocked ? 'block' : 'none';
            }
            if (pointerHintEl) {
                pointerHintEl.textContent = isPointerLocked
                    ? "Modo Câmera 3D Ativo | ESC para liberar o cursor"
                    : "Clique na bancada para navegar em 3D | ESC para liberar o cursor";
            }
            if (!isPointerLocked) {
                renderer.domElement.style.cursor = 'default';
            }
        });

        document.addEventListener('mousedown', e => {
            if (document.getElementById('modal-screen')?.classList.contains('open') || 
                document.getElementById('onboarding-screen')?.classList.contains('open')) {
                return;
            }

            if (e.target !== renderer.domElement) return;

            const rect = renderer.domElement.getBoundingClientRect();
            if (!isPointerLocked) {
                mouseVector.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
                mouseVector.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
            } else {
                mouseVector.set(0, 0);
            }

            if (e.button === 1 || e.button === 2) {
                isOrbiting = true;
                if (e.button === 2) {
                    rightDownX = e.clientX;
                    rightDownY = e.clientY;
                    rightDragged = false;
                }
                if (e.button === 1) e.preventDefault();
            }

            let target = null;
            if (!state.currentOpenModalType) {
                target = getTargetUnderCrosshair(mouseVector);
                if (target?.node) {
                    const nKey = core.key(target.node.x, target.node.y, target.node.z);
                    state.lockedInspectionNodeKey = nKey;
                    if (state.focusedNodeKey !== nKey) {
                        state.focusedNodeKey = nKey;
                        solveCircuit();
                    }
                } else if (!target?.pinData && !target?.isAirWire) {
                    state.lockedInspectionNodeKey = null;
                }
            } else {
                target = getTargetUnderCrosshair(mouseVector);
            }

            if (e.button === 0) {
                const isInteractive = target && (
                    target.isAirWire ||
                    target.pinData ||
                    target.sensorData ||
                    state.currentSelection.type === 'AIR_WIRE' ||
                    e.shiftKey ||
                    target.node?.type === core.COMP.SWITCH ||
                    core.BUTTON_TYPES.has(target.node?.type) ||
                    target.node?.type === core.COMP.VALVE_FLOW_CONTROL ||
                    target.node?.type === core.COMP.SUPPLY_PNEU ||
                    target.node?.type === core.COMP.SUPPLY_HYDRA ||
                    target.node?.type === core.COMP.POWER_SUPPLY_3P
                );

                if (!isPointerLocked && !isInteractive) {
                    renderer.domElement.requestPointerLock();
                    return;
                }

                if (target?.isAirWire) {
                    if (e.shiftKey || state.currentSelection.type !== 'AIR_WIRE') {
                        removeAirWireById(target.wireId);
                    }
                    return;
                }

                if (state.currentSelection.type === 'AIR_WIRE') {
                    if (target?.node) {
                        const pins = core.getComponentPins(target.node);
                        const allPins = [...pins.outputs.map(p => ({...p, isOutput: true})), ...pins.inputs.map(p => ({...p, isOutput: false}))];
                        
                        let closest = null;
                        if (target.pinData) {
                            closest = allPins.find(p => p.id === target.pinData.pinId || (p.aliases && p.aliases.includes(target.pinData.pinId)));
                        }

                        if (!closest && target.hitPoint && target.node.mesh) {
                            const localHit = target.node.mesh.worldToLocal(target.hitPoint.clone());
                            let minDist = Infinity;
                            allPins.forEach(p => {
                                const lx = (p.lx !== undefined) ? p.lx : 0;
                                const lz = (p.lz !== undefined) ? p.lz : 0;
                                const d = Math.hypot(lx - localHit.x, lz - localHit.z);
                                if (d < minDist) { minDist = d; closest = p; }
                            });
                            if (minDist > 1.8) {
                                closest = null;
                            }
                        }

                        if (closest) {
                            let pinWorldPos;
                            if (target.node.mesh) {
                                const py = (closest.ly !== undefined) ? closest.ly : ((closest.y !== undefined) ? closest.y : -0.38);
                                pinWorldPos = target.node.mesh.localToWorld(new THREE.Vector3(closest.lx !== undefined ? closest.lx : 0, py, closest.lz !== undefined ? closest.lz : 0));
                            } else {
                                pinWorldPos = new THREE.Vector3(closest.x, closest.y !== undefined ? closest.y : -0.38, closest.z);
                            }
                            const pinAnchor = {
                                nodeX: target.node.x,
                                nodeY: target.node.y,
                                nodeZ: target.node.z,
                                pinId: closest.id,
                                isOutput: closest.isOutput,
                                x: pinWorldPos.x,
                                y: pinWorldPos.y,
                                z: pinWorldPos.z
                            };

                            if (!airWireStart) {
                                airWireStart = pinAnchor;
                                notify('info', `Origem ancorada em [${pinAnchor.pinId}]. Agora clique no pino de destino.`);
                            } else {
                                if (airWireStart.nodeX === pinAnchor.nodeX && 
                                    airWireStart.nodeZ === pinAnchor.nodeZ && 
                                    airWireStart.pinId === pinAnchor.pinId) {
                                    airWireStart = null;
                                    notify('warning', 'Conexão cancelada.');
                                } else {
                                    let fromPin = airWireStart;
                                    let toPin = pinAnchor;
                                    if (!airWireStart.isOutput && pinAnchor.isOutput) {
                                        fromPin = pinAnchor;
                                        toPin = airWireStart;
                                    }
                                    addAirWire(fromPin, toPin);
                                    airWireStart = null;
                                }
                            }
                            return;
                        } else {
                            notify('warning', 'Mire diretamente sobre os pinos metálicos do componente.');
                            return;
                        }
                    } else {
                        if (airWireStart) {
                            airWireStart = null;
                            notify('info', 'Ligação cancelada.');
                        }
                        return;
                    }
                }

                if (target?.node) {
                    if (e.shiftKey) {
                        removeBlock(target.pos.x, target.pos.y, target.pos.z);
                        return;
                    }

                    if (target.node.type === core.COMP.SWITCH) {
                        toggleSwitch(target.node);
                        return;
                    }
                    if (target.node.type === core.COMP.PUSH_BUTTON_NO || target.node.type === core.COMP.PUSH_BUTTON_NC) {
                        target.node.isPressed = !Boolean(target.node.isPressed);
                        window.LogicCraft?.audio?.playClick(!target.node.isPressed);
                        if (target.node.mesh?.userData?.buttonCap) {
                            target.node.mesh.userData.buttonCap.position.y = target.node.isPressed ? -0.25 : -0.19;
                        }
                        solveCircuit();
                        if (target.node.type === core.COMP.PUSH_BUTTON_NO) {
                            notify('info', target.node.isPressed ? 'Botoeira NA (13/14) Ligada / Afundada (Nível 1)' : 'Botoeira NA (13/14) Liberada (Nível 0)');
                        } else {
                            notify('info', target.node.isPressed ? 'Botoeira NF (11/12) Acionada / Aberta (Nível 0)' : 'Botoeira NF (11/12) Liberada / Fechada (Nível 1)');
                        }
                        return;
                    }
                    if (target.node.type === core.COMP.EMERGENCY_STOP) {
                        target.node.isEmergencyLocked = !target.node.isEmergencyLocked;
                        window.LogicCraft?.audio?.playContactorClack(target.node.isEmergencyLocked);
                        if (target.node.mesh?.userData?.mushroomCap) {
                            target.node.mesh.userData.mushroomCap.position.y = target.node.isEmergencyLocked ? -0.20 : -0.10;
                        }
                        solveCircuit();
                        if (target.node.isEmergencyLocked) {
                            notify('error', '⚠️ PARADA DE EMERGÊNCIA ATUADA (NR-12) - Linha Bloqueada!');
                        } else {
                            notify('success', 'Emergência destravada (Girar p/ destravar). Linha liberada.');
                        }
                        return;
                    }
                    if (target.node.type === core.COMP.VALVE_FLOW_CONTROL) {
                        const steps = [100, 75, 50, 25, 10, 0];
                        const cur = target.node.openingPercent ?? 100;
                        const next = steps[(steps.indexOf(cur) + 1) % steps.length];
                        target.node.openingPercent = next;
                        if (target.node.mesh?.userData?.knob) {
                            target.node.mesh.userData.knob.rotation.y = (1.0 - next / 100) * Math.PI * 1.5;
                        }
                        if (target.node.mesh?.userData?.labelMesh) {
                            const lblMesh = target.node.mesh.userData.labelMesh;
                            if (lblMesh.material.map) lblMesh.material.map.dispose();
                            const canvas = document.createElement('canvas'); canvas.width = 64; canvas.height = 64;
                            const ctx = canvas.getContext('2d');
                            ctx.fillStyle = next === 0 ? '#EF4444' : '#38BDF8'; 
                            ctx.font = 'bold 36px monospace'; 
                            ctx.textAlign = 'center'; 
                            ctx.textBaseline = 'middle'; 
                            ctx.fillText(`${next}%`, 32, 32);
                            lblMesh.material.map = new THREE.CanvasTexture(canvas);
                            lblMesh.material.map.needsUpdate = true;
                        }
                        notify(next === 0 ? 'warning' : 'info', next === 0 ? 'Reguladora de Fluxo fechada (0%).' : `Reguladora de Fluxo ajustada para ${next}%.`);
                        return;
                    }
                    if (target.node.type === core.COMP.SUPPLY_PNEU) {
                        target.node.manualValue = (target.node.manualValue === false) ? true : false;
                        target.node.pressureBar = target.node.manualValue ? 6.0 : 0.0;
                        updateFluidLinks();
                        solveCircuit();
                        notify(target.node.manualValue ? 'success' : 'warning',
                               target.node.manualValue ? 'Unidade Pneumática FRL Ligada (Pressão: 6.0 bar)' : 'Unidade Pneumática FRL Desligada (0 bar)');
                        return;
                    }
                    if (target.node.type === core.COMP.SUPPLY_HYDRA) {
                        target.node.manualValue = (target.node.manualValue === false) ? true : false;
                        target.node.pressureBar = target.node.manualValue ? 120.0 : 0.0;
                        updateFluidLinks();
                        solveCircuit();
                        notify(target.node.manualValue ? 'success' : 'warning',
                               target.node.manualValue ? 'Unidade Hidráulica HPU Partida (Bomba Ativa - 120 bar)' : 'Unidade Hidráulica HPU Desligada (0 bar)');
                        return;
                    }
                    if (target.node.type === core.COMP.POWER_SUPPLY_3P) {
                        target.node.manualValue = (target.node.manualValue === false) ? true : false;
                        solveCircuit();
                        notify(target.node.manualValue ? 'success' : 'warning',
                               target.node.manualValue ? 'Alimentação Trifásica Energizada (380V/220V — Fases L1, L2, L3 ativas)' : 'Alimentação Trifásica Seccionada (0V)');
                        return;
                    }
                    return;
                }
            }
        });

        window.addEventListener('mouseup', e => {
            if (e.button === 1 || e.button === 2) {
                isOrbiting = false;
            }

            if (e.button === 2 && !rightDragged && e.target === renderer.domElement) {
                if (state.currentSelection.type === 'AIR_WIRE') {
                    airWireStart = null;
                    notify('info', 'Ligação cancelada.');
                } else {
                    const aimVec = isPointerLocked ? _zeroVector : mouseVector;
                    const target = getTargetUnderCrosshair(aimVec);
                    if (target?.node) {
                        const nx = core.snap(target.pos.x + target.worldNormal.x * core.GRID_SIZE);
                        const ny = core.snap(target.pos.y + target.worldNormal.y * core.GRID_SIZE);
                        const nz = core.snap(target.pos.z + target.worldNormal.z * core.GRID_SIZE);
                        addBlock(nx, ny, nz, state.currentSelection);
                    } else {
                        raycaster.setFromCamera(aimVec, camera);
                        const planeHits = raycaster.intersectObject(board);
                        if (planeHits.length > 0 && planeHits[0].distance < 30) {
                            addBlock(core.snap(planeHits[0].point.x), 0, core.snap(planeHits[0].point.z), state.currentSelection);
                        }
                    }
                }
            }
        });

        window.addEventListener('contextmenu', e => e.preventDefault());

        window.addEventListener('wheel', e => {
            if (e.target && e.target.closest('#hud-left, #hud-right, #challenge-panel, .panel, .modal, .hud-scroll-content, .palette')) {
                return;
            }
            if (document.getElementById('modal-screen')?.classList.contains('open') || 
                document.getElementById('onboarding-screen')?.classList.contains('open')) {
                return;
            }
            const rect = renderer.domElement.getBoundingClientRect();
            if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) return;

            mouseVector.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            mouseVector.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

            const target = getTargetUnderCrosshair(mouseVector);
            if (target?.sensorData && target?.node && core.CYLINDER_TYPES.has(target.node.type)) {
                e.preventDefault();
                const delta = e.deltaY < 0 ? 0.02 : -0.02;
                adjustSensorPosition(target.node, target.sensorData.sensorId, delta);
            } else {
                if (e.target === renderer.domElement || e.target.closest('#game-canvas')) e.preventDefault();
                const zoomStep = (e.deltaY < 0 ? 1 : -1) * 0.75;
                const camDir = new THREE.Vector3();
                camera.getWorldDirection(camDir);
                camera.position.addScaledVector(camDir, zoomStep);
                camera.position.x = THREE.MathUtils.clamp(camera.position.x, -25, 25);
                camera.position.y = THREE.MathUtils.clamp(camera.position.y, 0.8, 15);
                camera.position.z = THREE.MathUtils.clamp(camera.position.z, -25, 25);
            }
        }, { passive: false });

        document.addEventListener('keydown', e => {
            if (e.code === 'Escape') {
                state.lockedInspectionNodeKey = null;
                if (document.pointerLockElement) {
                    document.exitPointerLock();
                }
                moveF = false; moveB = false; moveL = false; moveR = false; moveUp = false; moveDown = false;
                renderer.domElement.style.cursor = 'default';
                if (airWireStart) {
                    airWireStart = null;
                    notify('info', 'Ligação cancelada.');
                }
                return;
            }

            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            if (document.getElementById('modal-screen')?.classList.contains('open') || 
                document.getElementById('onboarding-screen')?.classList.contains('open')) {
                return;
            }

            if (e.code === 'KeyW') moveF = true;
            if (e.code === 'KeyS') moveB = true;
            if (e.code === 'KeyA') moveL = true;
            if (e.code === 'KeyD') moveR = true;
            if (e.code === 'Space') { moveUp = true; e.preventDefault(); }
            if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') moveDown = true;
            if (e.code === 'Home') { resetCameraView(); e.preventDefault(); }

            if (e.code === 'BracketLeft' || e.code === 'BracketRight') {
                const target = getTargetUnderCrosshair(mouseVector);
                if (target?.node && core.CYLINDER_TYPES.has(target.node.type)) {
                    const sId = target.sensorData?.sensorId || 'S0';
                    const delta = (e.code === 'BracketRight') ? 0.03 : -0.03;
                    adjustSensorPosition(target.node, sId, delta);
                    e.preventDefault();
                }
            }

            if (e.code === 'Delete' || e.code === 'Backspace') {
                const target = getTargetUnderCrosshair(mouseVector);
                if (target?.isAirWire) {
                    removeAirWireById(target.wireId);
                } else if (target?.node) {
                    removeBlock(target.pos.x, target.pos.y, target.pos.z);
                }
                return;
            }

            if (e.code === 'KeyR' || e.code === 'KeyE') {
                const target = getTargetUnderCrosshair(mouseVector);
                if (target?.node) {
                    const oldRot = target.node.rot;
                    const oldPins = core.getComponentPins(target.node);
                    target.node.rot = (target.node.rot + 1) % 4;
                    target.node.mesh.rotation.y = target.node.rot * (Math.PI / 2);
                    target.node.mesh.updateMatrixWorld(true);
                    oldPins.inputs.forEach(p => refreshNeighborWires(p.x, p.y, p.z));
                    oldPins.outputs.forEach(p => refreshNeighborWires(p.x, p.y, p.z));
                    const newPins = core.getComponentPins(target.node);
                    newPins.inputs.forEach(p => refreshNeighborWires(p.x, p.y, p.z));
                    newPins.outputs.forEach(p => refreshNeighborWires(p.x, p.y, p.z));
                    refreshNeighborWires(target.node.x, target.node.y, target.node.z);
                    
                    core.pushHistory({ type: 'rotate', data: { x: target.node.x, y: target.node.y, z: target.node.z, oldRot: oldRot, newRot: target.node.rot } });
                    updateUndoRedoButtons();
                    core.saveCircuitToLocalStorage();
                    
                    updateFluidLinks();
                    solveCircuit();
                    notify('info', `Orientação ajustada (${target.node.rot * 90}°).`);
                } else {
                    state.currentRotation = (state.currentRotation + 1) % 4;
                    const rotText = state.currentRotation * 90;
                    const hudRot = document.getElementById('hud-insertion-rot');
                    if (hudRot) hudRot.textContent = `Rotação de Inserção: ${rotText}°`;
                    notify('info', `Rotação de inserção: ${rotText}°`);
                }
            }

            if (e.code === 'KeyT') {
                const target = getTargetUnderCrosshair(mouseVector);
                if (target?.node?.type === core.COMP.SWITCH) {
                    toggleSwitch(target.node);
                }
            }
        });

        document.addEventListener('keyup', e => {
            if (e.code === 'KeyW') moveF = false;
            if (e.code === 'KeyS') moveB = false;
            if (e.code === 'KeyA') moveL = false;
            if (e.code === 'KeyD') moveR = false;
            if (e.code === 'Space') moveUp = false;
            if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') moveDown = false;
        });

        document.addEventListener('mousemove', e => {
            if (document.getElementById('modal-screen')?.classList.contains('open') || 
                document.getElementById('onboarding-screen')?.classList.contains('open')) {
                return;
            }

            if (isPointerLocked) {
                yaw -= e.movementX * 0.0028;
                pitch = Math.max(-1.5, Math.min(1.5, pitch - e.movementY * 0.0028));
                mouseVector.set(0, 0);
            } else {
                const rect = renderer.domElement.getBoundingClientRect();
                mouseVector.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
                mouseVector.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

                if (isOrbiting && (e.buttons & 6)) {
                    yaw -= e.movementX * 0.003;
                    pitch = Math.max(-1.5, Math.min(1.5, pitch - e.movementY * 0.003));
                    if (Math.hypot(e.clientX - rightDownX, e.clientY - rightDownY) > 5) {
                        rightDragged = true;
                    }
                }
            }

            let target = null;
            if (!state.currentOpenModalType) {
                target = getTargetUnderCrosshair(mouseVector);
                if (target?.node && !state.lockedInspectionNodeKey) {
                    const newKey = core.key(target.node.x, target.node.y, target.node.z);
                    if (state.focusedNodeKey !== newKey) {
                        state.focusedNodeKey = newKey;
                        solveCircuit();
                    }
                }
            } else {
                target = getTargetUnderCrosshair(mouseVector);
            }

            if (!isPointerLocked) {
                if (target && (target.isAirWire || target.pinData || target.sensorData || 
                               target.node?.type === core.COMP.SWITCH || 
                               core.BUTTON_TYPES.has(target.node?.type) || 
                               target.node?.type === core.COMP.VALVE_FLOW_CONTROL ||
                               target.node?.type === core.COMP.SUPPLY_PNEU ||
                               target.node?.type === core.COMP.SUPPLY_HYDRA ||
                               target.node?.type === core.COMP.POWER_SUPPLY_3P)) {
                    renderer.domElement.style.cursor = 'pointer';
                } else if (state.currentSelection.type === 'AIR_WIRE' && target?.pinData) {
                    renderer.domElement.style.cursor = 'crosshair';
                } else {
                    renderer.domElement.style.cursor = 'default';
                }
            }
        });

        window.addEventListener('resize', () => {
            if (camera && renderer) {
                camera.aspect = window.innerWidth / window.innerHeight;
                camera.updateProjectionMatrix();
                renderer.setSize(window.innerWidth, window.innerHeight);
            }
        });

        document.addEventListener('visibilitychange', () => {
            if (!document.hidden && !isLoopRunning) {
                isLoopRunning = true;
                clock.getDelta();
                requestAnimationFrame(animate);
            }
        });
    }

    let isLoopRunning = true;

    function animate() {
        if (document.hidden) {
            isLoopRunning = false;
            return;
        }
        isLoopRunning = true;
        requestAnimationFrame(animate);
        const dt = Math.min(clock.getDelta(), 0.1);

        _tempEuler.set(pitch, yaw, 0, 'YXZ');
        camera.quaternion.setFromEuler(_tempEuler);

        _fwdVector.set(0, 0, -1).applyAxisAngle(_upAxis, yaw);
        _rightVector.set(1, 0, 0).applyAxisAngle(_upAxis, yaw);
        const speed = 7.5 * dt;

        if (moveF) camera.position.addScaledVector(_fwdVector, speed);
        if (moveB) camera.position.addScaledVector(_fwdVector, -speed);
        if (moveR) camera.position.addScaledVector(_rightVector, speed);
        if (moveL) camera.position.addScaledVector(_rightVector, -speed);
        if (moveUp) camera.position.y += 4.0 * dt;
        if (moveDown) camera.position.y -= 4.0 * dt;

        camera.position.x = THREE.MathUtils.clamp(camera.position.x, -14, 14);
        camera.position.y = THREE.MathUtils.clamp(camera.position.y, 0.8, 10);
        camera.position.z = THREE.MathUtils.clamp(camera.position.z, -14, 14);

        let needResolveFeedback = false;

        if (window.LogicCraft && window.LogicCraft.machines) {
            window.LogicCraft.machines.solvePowerCircuit(state.circuit, state.airWires);
            window.LogicCraft.machines.updateMachines(dt, state.circuit);
        }

        if (window.LogicCraft && window.LogicCraft.conveyorCell) {
            window.LogicCraft.conveyorCell.update(dt, state.circuit, state.airWires);
        }

        state.circuit.forEach(node => {
            if (core.CYLINDER_TYPES.has(node.type)) {
                if (!node.linkedValve) {
                    node.motionState = 'SEM COMANDO';
                } else {
                    const isHydra = (node.type === core.COMP.CYL_HYDRA);
                    const isSingle = (node.type === core.COMP.CYL_SINGLE);
                    const baseRate = isHydra ? 1.8 : 5.0;
                    const flowScale = (node.flowScale !== undefined) ? node.flowScale : 1.0;
                    const motionRate = Math.max(0.2, baseRate * flowScale);

                    if (!node.linkedValve.hasPressure) {
                        node.motionState = 'SEM PRESSÃO (0 bar)';
                        node.targetExtension = isSingle ? 0.0 : (node.extension || 0.0);
                    } else {
                        // Atuador responde diretamente à posição do carretel da válvula
                        node.targetExtension = (node.linkedValve.spoolPosition > 0.5) ? 1.0 : 0.0;
                    }

                    const diff = (node.targetExtension !== undefined ? node.targetExtension : 0.0) - (node.extension || 0.0);
                    if (Math.abs(diff) > 0.001) {
                        node.extension = THREE.MathUtils.lerp(node.extension || 0.0, node.targetExtension, dt * motionRate);
                        if (node.linkedValve.hasPressure || isSingle) {
                            node.motionState = (diff > 0) ? 'AVANÇANDO' : 'RECUANDO';
                        }
                    } else {
                        node.extension = node.targetExtension;
                        if (node.linkedValve.hasPressure || isSingle) {
                            node.motionState = (node.extension >= 0.98) ? 'AVANÇADO' : 'RECUADO';
                        }
                    }
                }

                const prevS0 = node.sensorS0;
                const prevS1 = node.sensorS1;

                const pos0 = typeof node.posS0 === 'number' ? node.posS0 : 0.05;
                const pos1 = typeof node.posS1 === 'number' ? node.posS1 : 0.95;

                node.sensorS0 = Math.abs(node.extension - pos0) <= 0.05;
                node.sensorS1 = Math.abs(node.extension - pos1) <= 0.05;

                if (prevS0 !== node.sensorS0 || prevS1 !== node.sensorS1) {
                    needResolveFeedback = true;
                    if ((!prevS0 && node.sensorS0) || (!prevS1 && node.sensorS1)) {
                        window.LogicCraft?.audio?.playSensorBeep();
                    }
                }

                const rod = node.mesh.userData.pistonRod;
                const head = node.mesh.userData.pistonHead;
                const s0Mesh = node.mesh.userData.s0;
                const s1Mesh = node.mesh.userData.s1;
                const rearC = node.mesh.userData.rearChamber;
                const frontC = node.mesh.userData.frontChamber;

                if (rod && head) {
                    const travelDist = node.extension * 0.70;
                    rod.position.z = 0.45 + travelDist;
                    head.position.z = rod.position.z + 0.595;
                    const nut = node.mesh.userData.pistonNut;
                    if (nut) nut.position.z = rod.position.z + 0.540;
                }

                const isHydra = (node.type === core.COMP.CYL_HYDRA);
                const pressColor = isHydra ? COLOR_HYDRA_PRESSURIZED : COLOR_PNEU_PRESSURIZED;
                const exhaustColor = isHydra ? COLOR_HYDRA_EXHAUST : COLOR_PNEU_EXHAUST;

                const hasPress = Boolean(node.linkedValve && node.linkedValve.hasPressure);
                const isAdvancingOrAdvanced = Boolean(hasPress && node.linkedValve.state);
                if (node.lastAdvancingState !== isAdvancingOrAdvanced) {
                    if (!isAdvancingOrAdvanced && hasPress) {
                        if (window.AudioEngine) window.AudioEngine.playExhaust();
                    }
                    node.lastAdvancingState = isAdvancingOrAdvanced;
                }

                if (rearC && frontC) {
                    if (!hasPress) {
                        rearC.material.color.setHex(exhaustColor);
                        rearC.material.emissive.setHex(0x000000);
                        rearC.material.emissiveIntensity = 0.0;
                        frontC.material.color.setHex(exhaustColor);
                        frontC.material.emissive.setHex(0x000000);
                        frontC.material.emissiveIntensity = 0.0;
                    } else {
                        rearC.material.color.setHex(isAdvancingOrAdvanced ? pressColor : exhaustColor);
                        rearC.material.emissive.setHex(isAdvancingOrAdvanced ? pressColor : 0x000000);
                        rearC.material.emissiveIntensity = isAdvancingOrAdvanced ? 0.8 : 0.0;

                        frontC.material.color.setHex(!isAdvancingOrAdvanced ? pressColor : exhaustColor);
                        frontC.material.emissive.setHex(!isAdvancingOrAdvanced ? pressColor : 0x000000);
                        frontC.material.emissiveIntensity = !isAdvancingOrAdvanced ? 0.8 : 0.0;
                    }
                }

                if (s0Mesh && s1Mesh) {
                    s0Mesh.material.color.setHex(node.sensorS0 ? 0xef4444 : 0x334155);
                    s0Mesh.material.emissiveIntensity = node.sensorS0 ? 1.5 : 0;
                    s1Mesh.material.color.setHex(node.sensorS1 ? 0x22c55e : 0x334155);
                    s1Mesh.material.emissiveIntensity = node.sensorS1 ? 1.5 : 0;
                }
            } else if (node.type === core.COMP.SUPPLY_PNEU && node.mesh?.userData) {
                const ud = node.mesh.userData;
                const isPneuOn = (node.manualValue !== false);
                const targetNeedleAngle = isPneuOn ? -0.85 : 0.85;
                if (ud.needle) {
                    ud.needle.rotation.z = THREE.MathUtils.lerp(ud.needle.rotation.z, targetNeedleAngle, dt * 6.0);
                }
                if (ud.lever) {
                    const targetLeverAngle = isPneuOn ? 0 : Math.PI / 2;
                    ud.lever.rotation.y = THREE.MathUtils.lerp(ud.lever.rotation.y, targetLeverAngle, dt * 10.0);
                }
            } else if (node.type === core.COMP.SUPPLY_HYDRA && node.mesh?.userData) {
                const ud = node.mesh.userData;
                const isHydraOn = (node.manualValue !== false);
                const targetNeedleAngle = isHydraOn ? -1.05 : 0.85;
                if (ud.needle) {
                    ud.needle.rotation.z = THREE.MathUtils.lerp(ud.needle.rotation.z, targetNeedleAngle, dt * 5.0);
                }
                if (ud.runLed) {
                    ud.runLed.material.color.setHex(isHydraOn ? 0x22C55E : 0x1E293B);
                    ud.runLed.material.emissiveIntensity = isHydraOn ? 1.5 : 0.0;
                }
            } else if (node.type === core.COMP.CONTACTOR_3P && node.mesh?.userData) {
                const ud = node.mesh.userData;
                const isEngaged = Boolean(node.state);
                if (ud.plunger) {
                    const targetPlungerY = isEngaged ? -0.10 : -0.04;
                    ud.plunger.position.y = THREE.MathUtils.lerp(ud.plunger.position.y, targetPlungerY, dt * 18.0);
                }
                if (ud.coilLed) {
                    ud.coilLed.material.color.setHex(isEngaged ? 0x22C55E : 0x052E16);
                    ud.coilLed.material.emissive.setHex(isEngaged ? 0x22C55E : 0x000000);
                    ud.coilLed.material.emissiveIntensity = isEngaged ? 2.2 : 0.0;
                }
            } else if (node.type === core.COMP.POWER_SUPPLY_3P && node.mesh?.userData) {
                const ud = node.mesh.userData;
                const isPwrOn = (node.manualValue !== false);
                if (ud.mainSwitchKnob) {
                    const targetAngle = isPwrOn ? -Math.PI / 4 : 0;
                    ud.mainSwitchKnob.rotation.y = THREE.MathUtils.lerp(ud.mainSwitchKnob.rotation.y, targetAngle, dt * 10.0);
                }
                if (ud.pilotR) ud.pilotR.material.emissiveIntensity = isPwrOn ? 2.2 : 0.0;
                if (ud.pilotS) ud.pilotS.material.emissiveIntensity = isPwrOn ? 2.2 : 0.0;
                if (ud.pilotT) ud.pilotT.material.emissiveIntensity = isPwrOn ? 2.2 : 0.0;
            } else if (node.type === core.COMP.TIMER_TON) {
                const delay = node.delayTime || 2.0;
                if (node.inputActive) {
                    node.elapsedTime = (node.elapsedTime || 0) + dt;
                    if (node.elapsedTime >= delay) {
                        if (!node.timerDone) {
                            node.timerDone = true;
                            needResolveFeedback = true;
                        }
                    }
                } else {
                    if (node.elapsedTime > 0 || node.timerDone) {
                        node.elapsedTime = 0;
                        node.timerDone = false;
                        needResolveFeedback = true;
                    }
                }
                const tLed = node.mesh?.userData?.timerLed;
                if (tLed) {
                    if (node.timerDone) {
                        tLed.material.color.setHex(0x22C55E);
                        tLed.material.emissive.setHex(0x22C55E);
                        tLed.material.emissiveIntensity = 2.0;
                    } else if (node.inputActive) {
                        tLed.material.color.setHex(0xF59E0B);
                        tLed.material.emissive.setHex(0xF59E0B);
                        tLed.material.emissiveIntensity = 0.8 + 0.8 * Math.sin(Date.now() * 0.012);
                    } else {
                        tLed.material.color.setHex(0x1E293B);
                        tLed.material.emissive.setHex(0x000000);
                        tLed.material.emissiveIntensity = 0.0;
                    }
                }
                const ctx = node.mesh?.userData?.timerCtx;
                if (ctx) {
                    let displayNum = node.inputActive ? (node.elapsedTime || 0) : 0;
                    if (node.timerDone) displayNum = delay;
                    const txt = displayNum.toFixed(1) + 's';
                    if (txt !== node.mesh.userData.lastTimerText) {
                        ctx.fillStyle = '#020617';
                        ctx.fillRect(0,0,512,256);
                        ctx.fillStyle = '#F87171';
                        ctx.font = 'bold 120px monospace';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(txt, 256, 128);
                        node.mesh.userData.timerTex.needsUpdate = true;
                        node.mesh.userData.lastTimerText = txt;
                    }
                }
            }
        });

        if (fluidHosesGroup) {
            fluidHosesGroup.children.forEach(hose => {
                const hData = hose.userData;
                if (!hData) return;
                const cyl = hData.cylinder;
                const hasPress = hData.valve ? Boolean(hData.valve.hasPressure) : (hData.isSupply ? true : false);
                const isValveActive = hData.valve ? Boolean(hData.valve.state || hData.valve.spoolPosition > 0.5) : false;
                const isPressurized = hData.isSupply 
                    ? hasPress 
                    : (hData.valve ? (hData.isLineA ? (hasPress && isValveActive) : (hasPress && !isValveActive)) : false);

                const pressColor = hData.isHydra ? COLOR_HYDRA_PRESSURIZED : COLOR_PNEU_PRESSURIZED;
                const exhaustColor = hData.isHydra ? COLOR_HYDRA_EXHAUST : COLOR_PNEU_EXHAUST;

                if (hose.material) {
                    hose.material.color.setHex(isPressurized ? pressColor : exhaustColor);
                    hose.material.emissive.setHex(isPressurized ? pressColor : 0x000000);

                    const isMoving = hasPress && cyl && (cyl.motionState === 'AVANÇANDO' || cyl.motionState === 'RECUANDO');
                    if (isMoving && hose.material.map) {
                        const flowSpeed = hData.isHydra ? 2.5 : 7.5;
                        const flowDir = (cyl.motionState === 'AVANÇANDO') ? (hData.isLineA ? 1 : -1) : (hData.isLineA ? -1 : 1);
                        hose.material.map.offset.x = (hose.material.map.offset.x - flowDir * flowSpeed * dt) % 1.0;
                        hose.material.emissiveIntensity = 1.0;
                    } else {
                        hose.material.emissiveIntensity = isPressurized ? 0.8 : 0.0;
                    }
                }
            });
        }

        if (needResolveFeedback) {
            solveCircuit();
        }

        renderer.render(scene, camera);
    }

    function loadBcdExample() {
        window.isBatchLoading = true;
        try {
            clearCircuit(true);
            // 1. Quatro Chaves Seletoras (A=MSB/Peso 8, B=Peso 4, C=Peso 2, D=LSB/Peso 1)
            addBlock(-3.0, 0, -3.0, { type: core.COMP.SWITCH, switchId: 'A', manualValue: false, rot: 0 }, true);
            addBlock(-1.0, 0, -3.0, { type: core.COMP.SWITCH, switchId: 'B', manualValue: false, rot: 0 }, true);
            addBlock( 1.0, 0, -3.0, { type: core.COMP.SWITCH, switchId: 'C', manualValue: false, rot: 0 }, true);
            addBlock( 3.0, 0, -3.0, { type: core.COMP.SWITCH, switchId: 'D', manualValue: true,  rot: 0 }, true);

            // 2. Decodificador BCD para 7 Segmentos (DEC_BCD_7SEG)
            addBlock( 0.0, 0, -0.5, { type: core.COMP.DEC_BCD_7SEG, rot: 0 }, true);

            // 3. Display de 7 Segmentos (DISPLAY_7SEG)
            addBlock( 0.0, 0,  2.5, { type: core.COMP.DISPLAY_7SEG, rot: 0 }, true);

            // 4. Interligação das chaves às entradas A, B, C, D do decodificador
            addAirWire({ nodeX: -3.0, nodeY: 0, nodeZ: -3.0, pinId: 'A' }, { nodeX: 0.0, nodeY: 0, nodeZ: -0.5, pinId: 'A' }, true);
            addAirWire({ nodeX: -1.0, nodeY: 0, nodeZ: -3.0, pinId: 'B' }, { nodeX: 0.0, nodeY: 0, nodeZ: -0.5, pinId: 'B' }, true);
            addAirWire({ nodeX:  1.0, nodeY: 0, nodeZ: -3.0, pinId: 'C' }, { nodeX: 0.0, nodeY: 0, nodeZ: -0.5, pinId: 'C' }, true);
            addAirWire({ nodeX:  3.0, nodeY: 0, nodeZ: -3.0, pinId: 'D' }, { nodeX: 0.0, nodeY: 0, nodeZ: -0.5, pinId: 'D' }, true);

            // 5. Interligação das 7 saídas paralelas (a..g) diretamente aos 7 pinos do display
            const segNames = ['a', 'b', 'c', 'd', 'e', 'f', 'g'];
            segNames.forEach(s => {
                addAirWire(
                    { nodeX: 0.0, nodeY: 0, nodeZ: -0.5, pinId: s },
                    { nodeX: 0.0, nodeY: 0, nodeZ:  2.5, pinId: s },
                    true
                );
            });

            state.focusedNodeKey = core.key(0.0, 0.0, -0.5);
            updateFluidLinks();
            solveCircuit();
        } finally {
            window.isBatchLoading = false;
        }
        notify('success', 'Demonstração: Decodificador BCD para 7 Segmentos carregada com sucesso!');
    }

    function loadPneumaticExample() {
        window.isBatchLoading = true;
        try {
            clearCircuit(true);
            // 1. Unidade de Conservação FRL (6.0 bar)
            addBlock(-5.0, 0, -2.5, { type: core.COMP.SUPPLY_PNEU, manualValue: true, pressureBar: 6.0, rot: 0 }, true);

            // 2. Chaves Seletoras de Comando Bimanual e Porta Lógica AND
            addBlock(-4.5, 0, -1.5, { type: core.COMP.SWITCH, switchId: 'A', manualValue: false, rot: 0 }, true);
            addBlock(-4.5, 0,  1.5, { type: core.COMP.SWITCH, switchId: 'B', manualValue: false, rot: 0 }, true);
            addBlock(-2.5, 0,  0.0, { type: core.COMP.AND, inputCount: 2, rot: 0 }, true);

            // 3. Válvula Direcional 5/2 Monoestável (1V1) e Cilindro Dupla Ação (1A)
            addBlock( 1.0, 0,  0.0, { type: core.COMP.VALVE_5_2_MONO, tag: '1V1', rot: 0 }, true);
            addBlock( 5.5, 0,  0.0, { type: core.COMP.CYL_DOUBLE, tag: '1A', rot: 0 }, true);

            // 4. Fiação Elétrica de Comando (24Vdc)
            addAirWire({ nodeX: -4.5, nodeY: 0, nodeZ: -1.5, pinId: 'A' }, { nodeX: -2.5, nodeY: 0, nodeZ: 0.0, pinId: 'A' }, true);
            addAirWire({ nodeX: -4.5, nodeY: 0, nodeZ:  1.5, pinId: 'B' }, { nodeX: -2.5, nodeY: 0, nodeZ: 0.0, pinId: 'B' }, true);
            addAirWire({ nodeX: -2.5, nodeY: 0, nodeZ:  0.0, pinId: 'S' }, { nodeX:  1.0, nodeY: 0, nodeZ: 0.0, pinId: 'Y1' }, true);

            // 5. Mangueiras Pneumáticas (ISO 1219-1)
            // Alimentação: FRL 1(P) -> Válvula 1(P)
            addHose({ nodeX: -5.0, nodeY: 0, nodeZ: -2.5, portId: '1(P)' }, { nodeX: 1.0, nodeY: 0, nodeZ: 0.0, portId: '1(P)' }, true);
            // Linha de Trabalho A: Válvula 4(A) -> Cilindro câmara traseira de avanço A
            addHose({ nodeX:  1.0, nodeY: 0, nodeZ:  0.0, portId: '4(A)' }, { nodeX: 5.5, nodeY: 0, nodeZ: 0.0, portId: 'A' }, true);
            // Linha de Trabalho B: Válvula 2(B) -> Cilindro câmara dianteira de recuo B
            addHose({ nodeX:  1.0, nodeY: 0, nodeZ:  0.0, portId: '2(B)' }, { nodeX: 5.5, nodeY: 0, nodeZ: 0.0, portId: 'B' }, true);

            state.focusedNodeKey = core.key(5.5, 0.0, 0.0);
            updateFluidLinks();
            solveCircuit();
        } finally {
            window.isBatchLoading = false;
        }
        notify('success', 'Demonstração: Eletropneumática Industrial (Comando Bimanual) carregada com sucesso!');
    }

    function loadHydraulicExample() {
        window.isBatchLoading = true;
        try {
            clearCircuit(true);
            // 1. Central Hidráulica HPU (120 bar)
            addBlock(-5.0, 0, -2.5, { type: core.COMP.SUPPLY_HYDRA, manualValue: true, pressureBar: 120.0, rot: 0 }, true);

            // 2. Botoeira NF de Parada (11/12), Botoeira NA de Partida (13/14) e Relé de Selo
            addBlock(-4.5, 0, -1.2, { type: core.COMP.PUSH_BUTTON_NC, rot: 0 }, true);
            addBlock(-4.5, 0,  1.2, { type: core.COMP.PUSH_BUTTON_NO, rot: 0 }, true);
            addBlock(-2.0, 0,  0.0, { type: core.COMP.RELAY, rot: 0 }, true);

            // 3. Válvula Direcional 4/2 Monoestável (1V1) e Cilindro Hidráulico (1A)
            addBlock( 1.5, 0,  0.0, { type: core.COMP.VALVE_4_2_MONO, tag: '1V1', rot: 0 }, true);
            addBlock( 5.5, 0,  0.0, { type: core.COMP.CYL_HYDRA, tag: '1A', rot: 0 }, true);

            // 4. Fiação Elétrica: Circuito de Comando com Autorretenção (Selo)
            // Parada (NF) alimenta o Comum do Relé
            addAirWire({ nodeX: -4.5, nodeY: 0, nodeZ: -1.2, pinId: 'OUT' }, { nodeX: -2.0, nodeY: 0, nodeZ: 0.0, pinId: 'COM' }, true);
            // Partida (NA) energiza a Bobina A1
            addAirWire({ nodeX: -4.5, nodeY: 0, nodeZ:  1.2, pinId: 'OUT' }, { nodeX: -2.0, nodeY: 0, nodeZ: 0.0, pinId: 'A1' }, true);
            // Contato Auxiliar NO do Relé em paralelo com Partida (autorretenção / selo)
            addAirWire({ nodeX: -2.0, nodeY: 0, nodeZ:  0.0, pinId: 'NO' },  { nodeX: -2.0, nodeY: 0, nodeZ: 0.0, pinId: 'A1' }, true);
            // Saída NO alimenta o solenoide lateral Y1 da válvula hidráulica
            addAirWire({ nodeX: -2.0, nodeY: 0, nodeZ:  0.0, pinId: 'NO' },  { nodeX:  1.5, nodeY: 0, nodeZ: 0.0, pinId: 'Y1' }, true);

            // 5. Mangueiras Hidráulicas
            // Pressão: HPU P -> Válvula P
            addHose({ nodeX: -5.0, nodeY: 0, nodeZ: -2.5, portId: 'P' }, { nodeX: 1.5, nodeY: 0, nodeZ: 0.0, portId: 'P' }, true);
            // Retorno Obrigatório ao Tanque: Válvula T -> HPU T
            addHose({ nodeX:  1.5, nodeY: 0, nodeZ:  0.0, portId: 'T' }, { nodeX: -5.0, nodeY: 0, nodeZ: -2.5, portId: 'T' }, true);
            // Trabalho A: Válvula A -> Cilindro câmara A
            addHose({ nodeX:  1.5, nodeY: 0, nodeZ:  0.0, portId: 'A' }, { nodeX: 5.5, nodeY: 0, nodeZ: 0.0, portId: 'A' }, true);
            // Trabalho B: Válvula B -> Cilindro câmara B
            addHose({ nodeX:  1.5, nodeY: 0, nodeZ:  0.0, portId: 'B' }, { nodeX: 5.5, nodeY: 0, nodeZ: 0.0, portId: 'B' }, true);

            state.focusedNodeKey = core.key(5.5, 0.0, 0.0);
            updateFluidLinks();
            solveCircuit();
        } finally {
            window.isBatchLoading = false;
        }
        notify('success', 'Demonstração: Eletro-hidráulica de Potência (Circuito de Selo) carregada com sucesso!');
    }

    function loadTimeoutDemo() {
        window.isBatchLoading = true;
        try {
            clearCircuit(true);
            // 1. Unidade de Conservação FRL (6.0 bar)
            addBlock(-5.0, 0, -2.5, { type: core.COMP.SUPPLY_PNEU, manualValue: true, pressureBar: 6.0, rot: 0 }, true);

            // 2. Chave de Partida, Temporizador TON (3.0s) e Buzzer de 22mm
            addBlock(-5.0, 0,  1.0, { type: core.COMP.SWITCH, switchId: 'A', manualValue: false, rot: 0 }, true);
            addBlock(-2.5, 0,  0.0, { type: core.COMP.TIMER_TON, delayTime: 3.0, timeSet: 3.0, rot: 0 }, true);
            addBlock(-2.5, 0,  2.0, { type: core.COMP.BUZZER, rot: 0 }, true);

            // 3. Válvula Direcional 5/2 Monoestável (1V1) e Cilindro Dupla Ação (1A)
            addBlock( 1.0, 0,  0.0, { type: core.COMP.VALVE_5_2_MONO, tag: '1V1', rot: 0 }, true);
            addBlock( 5.5, 0,  0.0, { type: core.COMP.CYL_DOUBLE, tag: '1A', rot: 0 }, true);

            // 4. Fiação Elétrica
            // Chave A alimenta a entrada IN do Timer TON
            addAirWire({ nodeX: -5.0, nodeY: 0, nodeZ: 1.0, pinId: 'A' }, { nodeX: -2.5, nodeY: 0, nodeZ: 0.0, pinId: 'IN' }, true);
            // Saída temporizada Q aciona o sinaleiro sonoro Buzzer (borne X1)
            addAirWire({ nodeX: -2.5, nodeY: 0, nodeZ: 0.0, pinId: 'Q' }, { nodeX: -2.5, nodeY: 0, nodeZ: 2.0, pinId: 'X1' }, true);
            // Saída temporizada Q comuta o solenoide Y1 da Válvula 5/2
            addAirWire({ nodeX: -2.5, nodeY: 0, nodeZ: 0.0, pinId: 'Q' }, { nodeX:  1.0, nodeY: 0, nodeZ: 0.0, pinId: 'Y1' }, true);

            // 5. Mangueiras Pneumáticas
            addHose({ nodeX: -5.0, nodeY: 0, nodeZ: -2.5, portId: '1(P)' }, { nodeX: 1.0, nodeY: 0, nodeZ: 0.0, portId: '1(P)' }, true);
            addHose({ nodeX:  1.0, nodeY: 0, nodeZ:  0.0, portId: '4(A)' }, { nodeX: 5.5, nodeY: 0, nodeZ: 0.0, portId: 'A' }, true);
            addHose({ nodeX:  1.0, nodeY: 0, nodeZ:  0.0, portId: '2(B)' }, { nodeX: 5.5, nodeY: 0, nodeZ: 0.0, portId: 'B' }, true);

            state.focusedNodeKey = core.key(-2.5, 0.0, 0.0);
            updateFluidLinks();
            solveCircuit();
        } finally {
            window.isBatchLoading = false;
        }
        notify('success', 'Demonstração: Temporização On-Delay e Controle de Vazão carregada com sucesso!');
    }

    function load4BitBusDemo() {
        window.isBatchLoading = true;
        try {
            clearCircuit(true);

            // 4 Bits de Entrada Paralelos (A, B, C, D) com espaçamento generoso de 3 unidades entre cada linha
            const busBits = [
                { id: 'A', x: -4.5, val: true },
                { id: 'B', x: -1.5, val: false },
                { id: 'C', x:  1.5, val: true },
                { id: 'D', x:  4.5, val: false }
            ];

            busBits.forEach(bit => {
                // Chave Seletora em Z = -3.0 (com borne de saída metálico em Z = -2.5)
                addBlock(bit.x, 0, -3.0, { type: core.COMP.SWITCH, switchId: bit.id, manualValue: bit.val, rot: 0 }, true);
                
                // Trilha condutora contínua (PCB WIRE) conectada sem vãos de Z = -2.5 até Z = 3.5
                for (let z = -2.5; z <= 3.5; z += core.GRID_SIZE) {
                    addBlock(bit.x, 0, z, { type: core.COMP.WIRE, rot: 0 }, true);
                }
            });

            state.focusedNodeKey = core.key(-4.5, 0, -3.0);
            updateWireNetworks();
            solveCircuit();
        } finally {
            window.isBatchLoading = false;
        }
        notify('success', 'Demonstração: Barramento Didático 4 Bits Paralelos (A, B, C, D) carregado com sucesso!');
    }

    const loadAutoCycleDemo = load4BitBusDemo;

    function loadMotorLogicDemo() {
        window.isBatchLoading = true;
        try {
            clearCircuit(true);
            // 1. Alimentação Trifásica Industrial (POWER_SUPPLY_3P) com Chave Geral e Aterramento PE
            addBlock(-3.8, 0, -1.8, { type: core.COMP.POWER_SUPPLY_3P, manualValue: true, rot: 0 }, true);

            // 2. Chave de Comando 24Vdc (Partida / Pulso de Acionamento)
            addBlock(-3.8, 0,  1.4, { type: core.COMP.SWITCH, switchId: 'A', manualValue: true, rot: 0 }, true);

            // 3. Contator Tripolar de Potência K1 (CONTACTOR_3P) com Bobina A1/A2 e Contato Auxiliar
            addBlock(-0.6, 0,  0.0, { type: core.COMP.CONTACTOR_3P, rot: 0 }, true);

            // 4. Motor de Indução Trifásico 6 Pontas M1 (MOTOR_INDUCTION_6P)
            addBlock( 3.2, 0,  0.0, { type: core.COMP.MOTOR_INDUCTION_6P, rot: 0 }, true);

            // --- CIRCUITO DE COMANDO (24Vdc) ---
            addAirWire({ nodeX: -3.8, nodeY: 0, nodeZ:  1.4, pinId: 'A' }, { nodeX: -0.6, nodeY: 0, nodeZ: 0.0, pinId: 'A1' }, true);

            // --- CIRCUITO DE FORÇA / POTÊNCIA TRIFÁSICA (380V) ---
            // Fases L1, L2, L3 da fonte para os contatos principais do contator (1, 3, 5)
            addAirWire({ nodeX: -3.8, nodeY: 0, nodeZ: -1.8, pinId: 'L1' }, { nodeX: -0.6, nodeY: 0, nodeZ: 0.0, pinId: '1' }, true);
            addAirWire({ nodeX: -3.8, nodeY: 0, nodeZ: -1.8, pinId: 'L2' }, { nodeX: -0.6, nodeY: 0, nodeZ: 0.0, pinId: '3' }, true);
            addAirWire({ nodeX: -3.8, nodeY: 0, nodeZ: -1.8, pinId: 'L3' }, { nodeX: -0.6, nodeY: 0, nodeZ: 0.0, pinId: '5' }, true);

            // Contatos principais de força (2, 4, 6) para os enrolamentos do motor U1, V1, W1
            addAirWire({ nodeX: -0.6, nodeY: 0, nodeZ: 0.0, pinId: '2' }, { nodeX: 3.2, nodeY: 0, nodeZ: 0.0, pinId: 'U1' }, true);
            addAirWire({ nodeX: -0.6, nodeY: 0, nodeZ: 0.0, pinId: '4' }, { nodeX: 3.2, nodeY: 0, nodeZ: 0.0, pinId: 'V1' }, true);
            addAirWire({ nodeX: -0.6, nodeY: 0, nodeZ: 0.0, pinId: '6' }, { nodeX: 3.2, nodeY: 0, nodeZ: 0.0, pinId: 'W1' }, true);

            // --- FECHAMENTO DE PLACA EM ESTRELA (Y - 380V) ---
            // Interligação dos bornes W2, U2 e V2 formando o ponto neutro
            addAirWire({ nodeX: 3.2, nodeY: 0, nodeZ: 0.0, pinId: 'W2' }, { nodeX: 3.2, nodeY: 0, nodeZ: 0.0, pinId: 'U2' }, true);
            addAirWire({ nodeX: 3.2, nodeY: 0, nodeZ: 0.0, pinId: 'U2' }, { nodeX: 3.2, nodeY: 0, nodeZ: 0.0, pinId: 'V2' }, true);

            // --- ATERRAMENTO DE SEGURANÇA (NR-10) ---
            // Condutor de proteção PE ligado da barra de terra da fonte diretamente ao borne PE da carcaça do motor
            addAirWire({ nodeX: -3.8, nodeY: 0, nodeZ: -1.8, pinId: 'PE' }, { nodeX: 3.2, nodeY: 0, nodeZ: 0.0, pinId: 'PE' }, true);

            // Enquadramento límpido da câmera destacando a bancada sem oclusão por painéis laterais
            camera.position.set(-0.3, 5.6, 7.8);
            pitch = -0.52; yaw = 0;
            _tempEuler.set(pitch, yaw, 0, 'YXZ');
            camera.quaternion.setFromEuler(_tempEuler);

            state.focusedNodeKey = core.key(3.2, 0.0, 0.0);
            updateFluidLinks();
            solveCircuit();
        } finally {
            window.isBatchLoading = false;
        }
        notify('success', 'Demonstração: Partida Direta de Motor Trifásico com NR-10 carregada com sucesso!');
    }

    return {
        init,
        addBlock,
        removeBlock,
        clearCircuit,
        solveCircuit,
        resetCameraView,
        propagateSignals,
        loadBcdExample,
        loadHydraulicExample,
        loadPneumaticExample,
        loadTimeoutDemo,
        load4BitBusDemo,
        loadAutoCycleDemo,
        loadMotorLogicDemo,
        exportCircuitToFile,
        importCircuitFromFile,
        undoLastAction,
        redoLastAction,
        toggleSwitch,
        addAirWire,
        removeAirWireById,
        addHose: typeof addHose !== 'undefined' ? addHose : () => {},
        removeHoseById: typeof removeHoseById !== 'undefined' ? removeHoseById : () => {},
        updateCylinderSensors3D,
        adjustSensorPosition
    };
})();